export type SupportedLang = "pt-BR" | "pt-PT" | "es" | "en";

export interface LangOption {
  code: SupportedLang;
  nativeLabel: string;
}

export const LANGUAGES: LangOption[] = [
  { code: "pt-BR", nativeLabel: "Português (Brasil)" },
  { code: "pt-PT", nativeLabel: "Português (Portugal)" },
  { code: "es", nativeLabel: "Español" },
  { code: "en", nativeLabel: "English" },
];

export const DEFAULT_LANG: SupportedLang = "pt-BR";

export type I18nDict = Record<SupportedLang, Record<string, string>>;

export const DICT: I18nDict = {
  "pt-BR": {
    "common.logout": "Sair",
    "common.language": "Idioma",
    "common.backToCore": "Voltar para o Core",
    "common.toggleMenu": "Alternar menu",
    "common.toggleSidebar": "Alternar barra lateral",
    "common.breadcrumb": "Navegação estrutural",
    "common.themeSwitchDark": "Alternar para modo escuro",
    "common.themeSwitchLight": "Alternar para modo claro",
    "common.save": "Salvar",
    "common.saving": "Salvando...",
    "common.cancel": "Cancelar",
    "common.close": "Fechar",
    "common.delete": "Excluir",
    "createEngine.apiTokenPendingHint": "O API Token do node será exibido quando a geração do token de instalação concluir.",
    "common.edit": "Editar",
    "common.create": "Criar",
    "common.loading": "Carregando...",
    "common.error": "Erro",
    "common.search": "Buscar",
    "common.actions": "Ações",
    "common.status": "Status",
    "common.name": "Nome",
    "common.description": "Descrição",
    "common.active": "Ativo",
    "common.inactive": "Inativo",
    "common.tryAgain": "Tentar novamente",
    "common.back": "Voltar",
    "common.version": "Versão",
    "common.updatedAt": "Atualizado em",
    "common.noData": "Nenhum dado disponível",
    "common.noResults": "Nenhum resultado encontrado",
    "common.saveError": "Falha ao salvar.",
    "common.deleteConfirm": "Esta ação não pode ser desfeita.",
    "common.confirm": "Confirmar",
    "common.continue": "Continuar",
    "common.region": "Região",
    "common.script": "Script",
    "common.value": "Valor",
    "common.email": "Email",
    "common.type": "Tipo",
    "common.download": "Download",
    "common.previous": "Anterior",
    "common.next": "Próximo",
    "common.page": "Página",
    "common.showing": "Mostrando",
    "common.of": "de",
    "common.suspend": "Suspender",
    "common.activate": "Ativar",
    "common.enabled": "Habilitado",
    "common.disabled": "Desabilitado",
    "common.all": "Todos",
    "common.filter": "Filtrar",
    "common.copy": "Copiar",
    "common.copied": "Copiado",
    "common.nameRequired": "Nome é obrigatório.",
    "common.suspended": "Suspenso",
    "common.success": "Sucesso",
    "common.warning": "Aviso",
    "common.danger": "Perigo",
    "common.info": "Informação",
    "common.accessDenied": "Acesso negado",

    "syncstatus.title": "Status de Sincronização",
    "syncstatus.subtitle": "Observabilidade do ciclo de sincronização de engenharia entre Engine e Core",
    "syncstatus.colEngine": "Engine",
    "syncstatus.colLastCycle": "Último Ciclo",
    "syncstatus.colVersion": "Versão",
    "syncstatus.colStatus": "Status",
    "syncstatus.colPushed": "Enviados",
    "syncstatus.colDeleted": "Removidos",
    "syncstatus.colErrors": "Erros",
    "syncstatus.colDuration": "Duração",
    "syncstatus.neverSynced": "Nunca sincronizou",
    "syncstatus.historyTitle": "Histórico de Sincronização",
    "syncstatus.lastError": "Último Erro",
    "syncstatus.noHistory": "Nenhum histórico disponível",
    "syncstatus.statusOk": "OK",
    "syncstatus.statusPushError": "Erro de Push",
    "syncstatus.statusError": "Erro",
    "syncstatus.loadingHistory": "Carregando histórico...",
    "syncstatus.relativeNow": "Agora mesmo",
    "syncstatus.relativeSeconds": "{n}s atrás",
    "syncstatus.relativeMinutes": "{n}min atrás",
    "syncstatus.relativeHours": "{n}h atrás",
    "syncstatus.relativeDays": "{n}d atrás",
    "syncstatus.pushedDel": "Enviados: {pushed} / Removidos: {deleted}",
    "syncstatus.errorsCount": "Erros: {n}",
    "syncstatus.noErrorDetail": "Sem detalhe",

    "dynvarsoverride.title": "Variáveis Configuráveis",
    "dynvarsoverride.description": "Personalize os valores de variáveis globais para este Engine Node",
    "dynvarsoverride.tableKey": "Chave",
    "dynvarsoverride.tableName": "Nome",
    "dynvarsoverride.tableGlobal": "Valor Global",
    "dynvarsoverride.tableLocal": "Valor Local",
    "dynvarsoverride.tableStatus": "Status",
    "dynvarsoverride.tableUsage": "Uso",
    "dynvarsoverride.statusDefault": "Usando padrão do Core",
    "dynvarsoverride.statusOverridden": "Sobrescrito localmente",
    "dynvarsoverride.empty": "Nenhuma variável configurável pelo Engine.",
    "dynvarsoverride.emptyDesc": "O Core não definiu variáveis que possam ser alteradas localmente.",
    "dynvarsoverride.editTitle": "Editar Valor Local",
    "dynvarsoverride.editDesc": "Altere o valor desta variável para este Engine.",
    "dynvarsoverride.formValue": "Valor",
    "dynvarsoverride.removeOverride": "Remover sobrescrita",
    "dynvarsoverride.removeConfirm": "Restaurar valor global de",
    "dynvarsoverride.save": "Salvar Alteração",
    "dynvarsoverride.error": "Valor inválido para o tipo de dado.",
    "dynvarsoverride.statusBlockedByCore": "Bloqueada pelo Core",
    "dynvarsoverride.statusOrphaned": "Variável removida no Core",
    "dynvarsoverride.orphanedHelp": "Esta variável não existe mais no Core. O override local está inativo — remova-o.",
    "dynvarsoverride.deleteOrphanedOverride": "Remover override",
    "dynvarsoverride.globalRef": "Padrão do Core:",
    "dynvarsoverride.reapplyEnqueued": "Salvo. Reaplicação agendada para os dispositivos afetados.",
    "dynvarsoverride.reapplyDeduped": "Reaplicação já em andamento — valor salvo.",
    "dynvarsoverride.reapplyCooldown": "Reaplicação em espera (cooldown) — valor salvo.",
    "dynvarsoverride.reapplyFailed": "Valor salvo, mas falha ao reaplicar nos dispositivos afetados.",

    "route.dynVarOverrides": "Variáveis Configuráveis",
    "sidebar.opsDynVarOverrides": "Variáveis Configuráveis",

    "route.nocDashboard": "Painel NOC",
    "route.engineRegistry": "Registro de Engines",
    "route.engVirtualParams": "Parâmetros Virtuais",
    "route.engProvisions": "Provisões",
    "route.engPresets": "Presets",
    "route.engDynamicVars": "Variáveis Dinâmicas",
    "route.engCwmpConfigs": "Configurações CWMP",
    "route.engVendorMappings": "Mapeamentos Vendor",
    "route.engCpeModels": "Modelos CPE",
    "route.engFirmware": "Firmware",
    "route.engFiles": "Arquivos",
    "route.engTr369Controllers": "Controladores TR-369",
    "route.engOlt": "OLT",
    "route.adminUsers": "Usuários",
    "route.adminSettings": "Configurações",
    "route.adminBackups": "Backups",
    "route.adminBilling": "Faturamento",
    "route.adminBillingPlans": "Planos de Faturamento",
    "route.adminBillingClients": "Clientes",
    "route.adminBillingContracts": "Contratos",
    "route.defaultTitle": "RC ACS",
    "route.dashboard": "Painel",
    "route.devices": "Dispositivos",
    "route.oltManagement": "Gestão OLT",
    "route.reports": "Relatórios",
    "route.operations": "Operações",
    "route.faults": "Falhas",
    "route.syncDrift": "Desvio de Sincronização",
    "route.operators": "Operadores",
    "route.engineDefault": "Gestão da Engine",
    "route.profile": "Perfil",
    "route.olts": "OLTs",

    "sidebar.dashboard": "Painel",
    "sidebar.engineRegistry": "Registro de Engines",
    "sidebar.engineering": "Engenharia",
    "sidebar.engTr069": "TR-069 (GenieACS)",
    "sidebar.engTr369": "TR-369 (USP)",
    "sidebar.engDevices": "Dispositivos",
    "sidebar.engOlt": "OLT",
    "sidebar.engOltManagement": "Gestão OLT",
    "sidebar.engDynamicVars": "Variáveis Dinâmicas",
    "sidebar.engSyncStatus": "Status de Sincronização",
    "sidebar.engVirtualParams": "Parâmetros Virtuais",
    "sidebar.engProvisions": "Provisões",
    "sidebar.engPresets": "Presets",
    "sidebar.engCwmpConfigs": "Configurações CWMP",
    "sidebar.engFiles": "Arquivos",
    "sidebar.engTr369Controllers": "Controladores",
    "sidebar.engVendorMappings": "Mapeamentos Vendor",
    "sidebar.engCpeModels": "Modelos CPE",
    "sidebar.engFirmware": "Firmware",
    "sidebar.admin": "Administração",
    "sidebar.adminUsers": "Usuários",
    "sidebar.adminSettings": "Configurações",
    "sidebar.adminBackups": "Backups",
    "sidebar.adminBilling": "Faturamento",
    "sidebar.adminBillingPlans": "Planos",
    "sidebar.adminBillingClients": "Clientes",
    "sidebar.adminBillingContracts": "Contratos",
    "sidebar.devices": "Dispositivos",
    "sidebar.olts": "OLTs",
    "sidebar.opsEngRemote": "Eng. Remota",
    "sidebar.opsFiles": "Arquivos",
    "sidebar.opsHealth": "Saúde",
    "sidebar.opsHostServices": "Host Services",
    "sidebar.opsOnboarding": "Onboarding",
    "sidebar.opsPresets": "Presets",
    "sidebar.opsProvisions": "Provisões",
    "sidebar.opsSettings": "Configurações",
    "sidebar.opsStatus": "Status",
    "sidebar.opsOperators": "Operadores",
    "sidebar.opsVirtualParams": "Parâmetros Virtuais",
    "sidebar.reports": "Relatórios",
    "sidebar.operations": "Operações",
    "sidebar.faults": "Falhas",
    "sidebar.syncDrift": "Desvio de Sincronização",

    "login.coreTitle": "Faça login para acessar a Plataforma de Gerenciamento Core",
    "login.coreSubtitle": "Faça login com sua conta RC Control para acessar o painel",
    "login.loginButton": "Entrar com RC Control",
    "login.redirectNotice": "Você será redirecionado para o serviço de autenticação RC Control.",
    "login.acs": "RC ACS",
    "login.engineTitle": "Acesse o painel de gerenciamento local",
    "login.engineNode": "Engine Node",
    "login.localLogin": "Login local",
    "login.localLoginPasskey": "Login local (Passkey)",
    "login.backToCore": "Voltar para o Core",
    "login.authenticating": "Autenticando...",
    "login.verifying": "Verificando...",
    "login.ispTab": "Operador ISP",
    "login.passkeyTab": "Passkey",
    "login.username": "Usuário",
    "login.password": "Senha",
    "login.ispLoginButton": "Entrar",
    "login.ispLoginError": "Usuário ou senha inválidos.",
    "login.ispLoginFailed": "Falha ao autenticar. Tente novamente.",
    "login.passkeyFailed": "Não foi possível autenticar com a passkey. Tente novamente.",
    "login.passkeyCancelled": "Autenticação com passkey cancelada.",
    "login.passkeyUnsupported": "Este navegador não suporta passkeys. Use usuário e senha.",
    "login.sessionExpired": "Sessão expirada. Faça login novamente.",
    "login.ssoInvalidToken": "Token de acesso inválido ou expirado.",
    "login.localTab": "Usuário Local",
    "login.localUsername": "Usuário",
    "login.localPassword": "Senha",
    "login.localLoginButton": "Entrar",
    "login.localLoginError": "Usuário ou senha inválidos.",
    "login.invalidCredentials": "Usuário ou senha inválidos.",
    "login.rateLimited": "Muitas tentativas de login. Tente novamente em alguns minutos.",
    "login.coreOffline": "Core offline — funcionalidade limitada. Sessão SSO não pode ser renovada.",
    "login.serviceUnavailable": "Serviço indisponível no momento. Tente novamente mais tarde.",
    "login.connectionError": "Erro de conexão. Verifique sua rede e tente novamente.",
    "login.loginUnavailableNode": "Login não disponível neste nó.",
    "login.directLoginUnavailable": "Login direto indisponível neste nó. Acesse via SSO do Core.",
    "login.loginWithPasskey": "Entrar com Passkey",
    "login.or": "ou",
    "login.passkeyTooltip": "Autenticação com chave de segurança ou biometria",

    "unauthorized.title": "Acesso negado",
    "unauthorized.message": "Você não tem permissão para acessar esta página.",
    "unauthorized.backToDashboard": "Voltar ao dashboard",
    "unauthorized.signOut": "Sair",

    "isp.role.isp_admin": "Administrador ISP",
    "isp.role.isp_operator": "Operador ISP",
    "isp.role.isp_technician": "Técnico",

    "lang.ptBR": "Português (Brasil)",
    "lang.ptPT": "Português (Portugal)",
    "lang.es": "Español",
    "lang.en": "English",

    "noc.title": "Painel NOC",

    "engineRegistry.title": "Registro de Engines",
    "engineRegistry.description": "Gerencie todos os Engine Nodes entre regiões e clientes",
    "engineRegistry.newEngine": "Novo Engine",
    "engineRegistry.searchPlaceholder": "Buscar engines...",
    "engineRegistry.allStatuses": "Todos os Status",
    "engineRegistry.statusOnline": "Online",
    "engineRegistry.statusOffline": "Offline",
    "engineRegistry.statusDegraded": "Degradado",
    "engineRegistry.statusSuspended": "Suspenso",
    "engineRegistry.cloud": "Nuvem",
    "engineRegistry.loading": "Carregando engine nodes...",
    "engineRegistry.empty": "Nenhum engine node encontrado",
    "engineRegistry.createFirst": "Criar seu primeiro Engine Node",
    "engineRegistry.tableName": "Nome",
    "engineRegistry.tableClient": "Cliente",
    "engineRegistry.tableRegion": "Região",
    "engineRegistry.tableHealth": "Saúde",
    "engineRegistry.tableCpes": "CPEs",
    "engineRegistry.tableTier": "Tier",
    "engineRegistry.tableHeartbeat": "Último Heartbeat",
    "engineRegistry.never": "Nunca",
    "engineRegistry.justNow": "Agora mesmo",
    "engineRegistry.secondsAgo": "s atrás",
    "engineRegistry.minutesAgo": "min atrás",
    "engineRegistry.hoursAgo": "h atrás",
    "engineRegistry.daysAgo": "d atrás",
    "engineRegistry.edit": "Editar",
    "engineRegistry.activate": "Ativar",
    "engineRegistry.suspend": "Suspender",
    "engineRegistry.delete": "Excluir",
    "engineRegistry.deleteTitle": "Excluir Engine Node",
    "engineRegistry.deleteMessage": "Tem certeza que deseja excluir",
    "registry.errorDelete": "Falha ao excluir o nó. Verifique as dependências e tente novamente.",
    "registry.errorConflict": "Não foi possível excluir o nó: existem dependências que bloqueiam a exclusão.",
    "registry.errorForbidden": "Você não tem permissão de administrador para excluir este nó.",
    "registry.nameMismatch": "O nome digitado não corresponde ao nome do nó.",
    "hostServices.editInterface": "Editar interface",
    "hostServices.deleteInterface": "Excluir interface",
    "hostServices.deleteInterfaceConfirm": "Confirma exclusão da interface",
    "hostServices.deleteInterfaceWarning": "A interface será removida do kernel e da configuração. Para VLANs, a interface é excluída do sistema. Para interfaces físicas, apenas removida da configuração.",
    "hostServices.interfaceName": "Nome",
    "hostServices.ipv4Address": "Endereço IPv4",
    "hostServices.ipv6Address": "Endereço IPv6",
    "hostServices.subnet4": "Subnet IPv4",
    "hostServices.prefix6": "Prefixo IPv6",
    "hostServices.poolStart": "Início do pool",
    "hostServices.poolEnd": "Fim do pool",
    "hostServices.gateway": "Gateway",
    "hostServices.dnsServers": "Servidores DNS",
    "hostServices.leaseTime": "Tempo de lease (s)",
    "hostServices.cidr.ipv4Cidr": "IPv4 CIDR",
    "hostServices.cidr.ipv6Cidr": "IPv6 CIDR",
    "hostServices.cidr.ipv4Placeholder": "ex: 10.50.0.1/24",
    "hostServices.cidr.ipv6Placeholder": "ex: 2001:db8::1/64",
    "hostServices.cidr.auto": "auto",
    "hostServices.cidr.manual": "manual",
    "hostServices.cidr.reset": "Redefinir para derivado",
    "hostServices.cidr.reserveFirst": "Reservar primeiros",
    "hostServices.cidr.networkId": "Rede",
    "hostServices.cidr.netmask": "Máscara",
    "hostServices.cidr.prefixLength": "Prefixo",
    "hostServices.cidr.broadcast": "Broadcast",
    "hostServices.cidr.usableRange": "Range útil",
    "hostServices.cidr.totalHosts": "Hosts totais",
    "hostServices.cidr.locked": "travado",
    "hostServices.cidr.unlocked": "editável",
    "hostServices.cidr.lock": "Travar gateway",
    "hostServices.cidr.unlock": "Destravar gateway",
    "hostServices.wizard.interfaces": "Interfaces CPE",
    "hostServices.wizard.subnets": "Subnet/Prefixo",
    "hostServices.wizard.step1": "Rede & Pools CPE",
    "hostServices.wizard.step2": "TR-069 & Serviços",
    "hostServices.wizard.step3": "Preview & Commit",
    "hostServices.wizard.services": "TR-069 & Serviços",
    "hostServices.wizard.review": "Revisão",
    "hostServices.wizard.preview.kea4": "Kea DHCPv4",
    "hostServices.wizard.preview.kea6": "Kea DHCPv6",
    "hostServices.wizard.preview.unbound": "Unbound",
    "hostServices.wizard.preview.radvd": "radvd",
    "hostServices.wizard.noInterfacesSelected": "Selecione pelo menos uma interface para continuar.",
    "hostServices.wizard.overlapConflictV4": "Conflito IPv4 entre {a} e {b}",
    "hostServices.wizard.overlapConflictV6": "Conflito IPv6 entre {a} e {b}",
    "hostServices.wizard.autoDnsServers": "DNS para CPEs (auto)",
    "hostServices.wizard.noDnsServers": "nenhum",
    "hostServices.wizard.autoDnsViews": "Views DNS geradas automaticamente",
    "hostServices.wizard.acsUrlPrimary": "ACS URL primária",
    "hostServices.wizard.acsUrlFallback": "ACS URL fallback",
    "hostServices.wizard.dnsForwarders": "DNS forwarders upstream",
    "hostServices.wizard.dnsForwardersPlaceholder": "1.1.1.1, 8.8.8.8",
    "hostServices.wizard.dnsForwardersHelp": "Servidores de encaminhamento separados por vírgula. Deixe em branco para desabilitar o encaminhamento upstream.",
    "hostServices.wizard.reviewInterfaces": "Interfaces",
    "hostServices.wizard.reviewSubnet4": "Subnet IPv4",
    "hostServices.wizard.reviewPrefix6": "Prefixo IPv6",
    "hostServices.wizard.reviewForwarders": "Forwarders",
    "hostServices.wizard.noForwarders": "nenhum",
    "hostServices.wizard.reviewAcsUrl": "ACS primária",
    "hostServices.wizard.activating": "Ativando...",
    "hostServices.wizard.confirmActivate": "Confirmar e ativar",
    "hostServices.vlanIdRange": "VLAN ID deve estar entre 1 e 4094.",
    "hostServices.parentRequired": "Interface pai é obrigatória.",
    "hostServices.wizard.result.title": "Resultado da aplicação",
    "hostServices.wizard.result.applied": "aplicado",
    "hostServices.wizard.result.partial": "Aplicação parcial — alguns serviços falharam.",
    "hostServices.wizard.result.success": "Todos os serviços foram aplicados com sucesso.",
    "hostServices.wizard.result.allFailed": "Nenhum serviço pôde ser aplicado.",
    "hostServices.wizard.result.retry": "Tentar novamente",
    "hostServices.wizard.result.retrying": "Tentando...",
    "hostServices.wizard.result.close": "Fechar",
    "hostServices.wizard.result.backToEdit": "Voltar para edição",
    "hostServices.wizard.result.skipped": "pulado (sem interfaces qualificadas)",
    "hostServices.wizard.result.noServices": "Nenhum serviço retornado pelo backend.",
    "hostServices.wizard.result.appliedCount": "serviço(s) aplicado(s).",
    "hostServices.wizard.open": "Abrir assistente de configuração",
    "hostServices.wizard.slaacOverlapNote": "Com SLAAC+DHCPv6 simultâneos (modo dual), endereços EUI-64 podem coincidir com o pool DHCPv6; o DAD do cliente resolve duplicidades. Reduza o pool ou desative SLAAC se quiser isolamento total.",

    "engineDetail.notFound": "Engine Node Não Encontrado",
    "engineDetail.notFoundMsg": "O node com ID especificado não existe.",
    "engineDetail.backToRegistry": "Voltar ao Registro",
    "engineDetail.registryBreadcrumb": "Registro de Engines",
    "engineDetail.healthIndex": "Índice de Saúde",
    "engineDetail.activeCpes": "CPEs Ativos",
    "engineDetail.licenseTier": "Tier de Licença",
    "engineDetail.maxCpes": "Máx. CPEs",
    "engineDetail.heartbeat": "Heartbeat",
    "engineDetail.loading": "Carregando engine node...",
    "engineDetail.backups": "Backups",
    "engineDetail.remoteLogs": "Logs Remotos",
    "engineDetail.healthHistory": "Histórico de Saúde",

    "vp.title": "Parâmetros Virtuais",
    "vp.description": "Gerencie scripts de parâmetros virtuais para engenharia de CPEs",
    "vp.newParam": "Novo Parâmetro",
    "vp.loading": "Carregando parâmetros virtuais...",
    "vp.error": "Erro ao carregar parâmetros virtuais.",
    "vp.empty": "Nenhum parâmetro virtual encontrado.",
    "vp.createFirst": "Criar seu primeiro Parâmetro Virtual",
    "vp.tableName": "Nome",
    "vp.tableDescription": "Descrição",
    "vp.tableScript": "Script",
    "vp.tableVersion": "Versão",
    "vp.tableUpdatedAt": "Atualizado em",
    "vp.tableActions": "Ações",
    "vp.editTitle": "Editar Parâmetro Virtual",
    "vp.newTitle": "Novo Parâmetro Virtual",
    "vp.editDesc": "Edite os campos do parâmetro virtual.",
    "vp.newDesc": "Preencha os campos para criar um novo parâmetro virtual.",
    "vp.formName": "Nome *",
    "vp.formNamePlaceholder": "Ex: SNR Médio",
    "vp.nameRequired": "Nome é obrigatório.",
    "vp.formDesc": "Descrição",
    "vp.formDescPlaceholder": "Ex: Calcula a média do SNR nas últimas 24h",
    "vp.formScript": "Script (JavaScript)",
    "vp.formScriptPlaceholder": "// Escreva o script JavaScript do parâmetro virtual",
    "vp.validateScript": "Validar Script",
    "vp.scriptValid": "Script válido",
    "vp.scriptInvalid": "Script inválido",
    "vp.deleteTitle": "Excluir Parâmetro Virtual",
    "vp.deleteMessage": "Tem certeza que deseja excluir",
    "vp.versionHistory": "Histórico de Versões",
    "vp.versionHistoryDesc": "Versões anteriores do parâmetro virtual.",
    "vp.noVersions": "Nenhuma versão anterior encontrada.",
    "vp.versionLabel": "Versão",
    "vp.reason": "Motivo",

    "provisions.title": "Provisões",
    "provisions.description": "Gerencie scripts de provisão para configuração automática de CPEs",
    "provisions.newProvision": "Nova Provisão",
    "provisions.loading": "Carregando provisões...",
    "provisions.error": "Erro ao carregar provisões.",
    "provisions.empty": "Nenhuma provisão encontrada.",
    "provisions.createFirst": "Criar sua primeira Provisão",
    "provisions.tableName": "Nome",
    "provisions.tableScript": "Script",
    "provisions.editTitle": "Editar Provisão",
    "provisions.newTitle": "Nova Provisão",
    "provisions.editDesc": "Edite os campos da provisão.",
    "provisions.newDesc": "Preencha os campos para criar uma nova provisão.",
    "provisions.formName": "Nome *",
    "provisions.formNamePlaceholder": "Ex: Configuração Inicial PPPoE",
    "provisions.formScript": "Script (JavaScript)",
    "provisions.formScriptPlaceholder": "// Escreva o script JavaScript de provisão",
    "provisions.validate": "Validar",
    "provisions.validScript": "Script válido",
    "provisions.invalidScript": "Script inválido",
    "provisions.deleteTitle": "Excluir Provisão",
    "provisions.deleteMessage": "Tem certeza que deseja excluir",
    "provisions.versionHistoryDesc": "Versões anteriores da provisão.",

    "presets.title": "Presets",
    "presets.description": "Gerencie presets de configuração para provisionamento de CPEs",
    "presets.newPreset": "Novo Preset",
    "presets.loading": "Carregando presets...",
    "presets.error": "Erro ao carregar presets.",
    "presets.empty": "Nenhum preset encontrado.",
    "presets.createFirst": "Criar seu primeiro Preset",
    "presets.tableName": "Nome",
    "presets.tableConfig": "Config",
    "presets.editTitle": "Editar Preset",
    "presets.newTitle": "Novo Preset",
    "presets.editDesc": "Edite os campos do preset.",
    "presets.newDesc": "Preencha os campos para criar um novo preset.",
    "presets.formName": "Nome *",
    "presets.formNamePlaceholder": "Ex: Configuração Padrão WiFi",
    "presets.formConfig": "Config (JSON)",
    "presets.channel": "Canal",
    "presets.weight": "Peso",
    "presets.schedule": "Agendamento",
    "presets.scheduleHelp": "Expressão cron (ex: 0 2 * * *)",
    "presets.events": "Eventos",
    "presets.precondition": "Pré-condição",
    "presets.preconditionHelp": "Expressão booleana avaliada antes de aplicar o preset. Ex.: Tags.Provisioned. Suporta operadores GenieACS (AND, OR, NOT, IS NULL). Placeholders {{var}} não são suportados aqui.",
    "presets.provisions": "Provisions",
    "presets.addProvision": "Adicionar Provision",
    "presets.provisionArgs": "Argumentos (JSON)",
    "presets.jsonInvalid": "JSON inválido. Corrija a sintaxe.",
    "presets.deleteTitle": "Excluir Preset",
    "presets.deleteMessage": "Tem certeza que deseja excluir",
    "presets.versionHistoryDesc": "Versões anteriores do preset.",

    "dynvars.title": "Variáveis Dinâmicas",
    "dynvars.description": "Gerencie variáveis dinâmicas utilizadas nos scripts de engenharia",
    "dynvars.newVar": "Nova Variável",
    "dynvars.loading": "Carregando variáveis dinâmicas...",
    "dynvars.error": "Erro ao carregar variáveis dinâmicas.",
    "dynvars.empty": "Nenhuma variável dinâmica encontrada.",
    "dynvars.createFirst": "Criar sua primeira Variável Dinâmica",
    "dynvars.tableKey": "Chave",
    "dynvars.tableName": "Nome",
    "dynvars.tableDataType": "Tipo de Dado",
    "dynvars.tableDefault": "Valor Padrão",
    "dynvars.editTitle": "Editar Variável Dinâmica",
    "dynvars.newTitle": "Nova Variável Dinâmica",
    "dynvars.editDesc": "Edite os campos da variável dinâmica.",
    "dynvars.newDesc": "Preencha os campos para criar uma nova variável dinâmica.",
    "dynvars.formKey": "Chave *",
    "dynvars.formKeyPlaceholder": "ex: cpe_user_web",
    "dynvars.formKeyHelper": "Identificador usado nos scripts como {{chave}}. Sincronizado para todos os Engines. Use apenas letras minúsculas, números e sublinhados (_).",
    "dynvars.keyRequired": "Chave é obrigatória.",
    "dynvars.keyPatternError": "A chave deve começar com letra minúscula e conter apenas letras minúsculas, números e sublinhados (_).",
    "dynvars.previewScript": "Usado nos scripts como:",
    "dynvars.formName": "Nome *",
    "dynvars.formNamePlaceholder": "Ex: Usuário Web do CPE",
    "dynvars.formNameHelper": "Nome descritivo amigável exibido apenas na listagem do painel. Nunca aparece no código dos scripts.",
    "dynvars.formDescHelper": "Anotação interna para a equipe sobre o propósito desta variável. Não é enviada aos scripts.",
    "dynvars.formDescPlaceholder": "Ex: Credencial de acesso para painel web das ONTs Huawei e ZTE",
    "dynvars.formSourceType": "Origem",
    "dynvars.formSourceTypeHelper": "De onde o Engine obtém o valor da variável no momento do envio (push) para o GenieACS.",
    "dynvars.sourceHintStaticTitle": "Valor Fixo (Estático)",
    "dynvars.sourceHintStatic": "O valor é fixo e definido no campo 'Valor Padrão'. Recomendado para a maioria dos casos (credenciais, servidor NTP, fuso horário).",
    "dynvars.sourceHintCwmpTitle": "Configuração CWMP",
    "dynvars.sourceHintCwmp": "Sincroniza valor de uma chave de config do GenieACS. Nota: a resolução automática via CWMP ainda não está implementada no Engine e utilizará o Valor Padrão como fallback.",
    "dynvars.sourceHintSystemTitle": "Configuração do Sistema",
    "dynvars.sourceHintSystem": "Lê uma configuração global do Engine. Formato: setting:CHAVE (ex.: setting:admin.smtp).",
    "dynvars.sourceHintEnvTitle": "Variável de Ambiente",
    "dynvars.sourceHintEnv": "Lê variável de ambiente do host do Engine. Deve começar com DYNVAR_ (ou ser INFORM_INTERVAL). Nomes com PASSWORD, SECRET, TOKEN, KEY, URI ou URL são bloqueados por segurança.",
    "dynvars.formSourceRef": "Referência da Origem",
    "dynvars.formSourceRefHelper": "Identificador da origem externa a ser consultada pelo Engine.",
    "dynvars.sourceRefPlaceholderCwmp": "ex: cwmp:Device.ManagementServer.URL",
    "dynvars.sourceRefPlaceholderSetting": "ex: setting:admin.smtp",
    "dynvars.sourceRefPlaceholderEnv": "ex: env:DYNVAR_NTP_SERVER",
    "dynvars.formDataType": "Tipo de Dado",
    "dynvars.formDataTypeHelper": "Define como o valor é validado antes do envio (fail-closed: valor inválido para o tipo impede o envio da provisão).",
    "dynvars.dataTypeHintString": "Texto: injetado como string literal JS segura entre aspas.",
    "dynvars.dataTypeHintBoolean": "Booleano: aceita apenas true ou false.",
    "dynvars.dataTypeHintInt": "Inteiro: número inteiro sem casas decimais (ex.: 600).",
    "dynvars.dataTypeHintJson": "JSON: objeto ou array JSON válido (ex.: {\"opcao\": 1}).",
    "dynvars.dataTypeHintEnum": "Enum: restrito aos valores definidos na lista de opções.",
    "dynvars.formDefault": "Valor Padrão",
    "dynvars.formDefaultHelper": "Valor utilizado nos scripts (se Estático) ou fallback caso a origem falhe. Se vazio e sem valor de origem, o provisionamento é cancelado.",
    "dynvars.placeholderDefaultInt": "ex: 600",
    "dynvars.formEnumOptions": "Opções do Enum",
    "dynvars.formEnumOptionsPlaceholder": "ex: bridge, router, pppoe",
    "dynvars.formEnumOptionsHint": "Valores separados por vírgula.",
    "dynvars.formEnumOptionsHelper": "Lista de valores aceitos separados por vírgula. Qualquer valor fora desta lista é rejeitado.",
    "dynvars.formActive": "Ativo",
    "dynvars.formActiveHelper": "Variáveis inativas não são resolvidas. Provisões que dependem desta variável são abortadas durante o envio.",
    "dynvars.formDefaultPlaceholder": "Valor padrão (opcional)",
    "dynvars.selectDefault": "Selecione o padrão",
    "dynvars.emptyDesc": "Crie variáveis parametrizadas para injetar valores dinâmicos nos scripts de provisão e parâmetros virtuais do GenieACS sem expor credenciais fixas.",
    "dynvars.sourceType.static": "Estático",
    "dynvars.sourceType.cwmp_config": "Config CWMP",
    "dynvars.sourceType.system_setting": "Configuração do Sistema",
    "dynvars.sourceType.env_var": "Variável de Ambiente",
    "dynvars.dataType.string": "Texto",
    "dynvars.dataType.boolean": "Booleano",
    "dynvars.dataType.int": "Inteiro",
    "dynvars.dataType.json": "JSON",
    "dynvars.dataType.enum": "Enum",
    "dynvars.deleteTitle": "Excluir Variável Dinâmica",
    "dynvars.deleteMessage": "Tem certeza que deseja excluir",
    "dynvars.usageColumn": "Uso",
    "dynvars.usageFormat": "Usada em: {provisions} provisions, {vps} VPs, {presets} presets",
    "dynvars.scope": "Escopo",
    "dynvars.scopeGlobal": "Global",
    "dynvars.scopeConfigurable": "Configurável",
    "dynvars.scopeConfigurableEngine": "Configurável por Engine",
    "dynvars.scopeGlobalHelper": "Global: valor fixo definido pelo Core; Engines apenas aplicam",
    "dynvars.scopeConfigurableHelper": "Configurável por Engine: Core envia o valor padrão; cada Engine pode definir seu próprio valor na sua UI (aplicado aos scripts locais).",
    "dynvars.scopeToggleWarning": "Atenção: overrides existentes nas Engines ficarão inativos (não são removidos)",

    "tr369.comingSoon": "TR-369 em breve",
    "tr369.description": "A gestão de controladores TR-369 (USP/OpenUSP) será habilitada assim que a implementação do controlador Go estiver concluída.",

    "vendorMappings.title": "Mapeamentos Vendor",
    "vendorMappings.description": "Gerencie mapeamentos de fabricantes e modelos para TR-069",
    "vendorMappings.newMapping": "Novo Mapeamento",
    "vendorMappings.loading": "Carregando mapeamentos...",
    "vendorMappings.error": "Erro ao carregar mapeamentos.",
    "vendorMappings.empty": "Nenhum mapeamento encontrado.",
    "vendorMappings.createFirst": "Criar seu primeiro Mapeamento",
    "vendorMappings.tableVendor": "Fabricante",
    "vendorMappings.tableModel": "Modelo",
    "vendorMappings.tablePath": "Path",
    "vendorMappings.editTitle": "Editar Mapeamento",
    "vendorMappings.newTitle": "Novo Mapeamento",
    "vendorMappings.editDesc": "Edite os campos do mapeamento.",
    "vendorMappings.newDesc": "Preencha os campos para criar um novo mapeamento vendor.",
    "vendorMappings.formVendor": "Fabricante *",
    "vendorMappings.formVendorPlaceholder": "Ex: Huawei",
    "vendorMappings.vendorRequired": "Fabricante é obrigatório.",
    "vendorMappings.formModel": "Modelo *",
    "vendorMappings.formModelPlaceholder": "Ex: HG8245H",
    "vendorMappings.modelRequired": "Modelo é obrigatório.",
    "vendorMappings.formPath": "TR-069 Path",
    "vendorMappings.formPathPlaceholder": "Ex: InternetGatewayDevice.LANDevice.1.WLANConfiguration.1",
    "vendorMappings.formDescPlaceholder": "Ex: Mapeamento para ONTs Huawei GPON",
    "vendorMappings.deleteTitle": "Excluir Mapeamento",
    "vendorMappings.deleteMessage": "Tem certeza que deseja excluir",

    "cpeModels.title": "Modelos CPE",
    "cpeModels.description": "Gerencie modelos de equipamentos de clientes (CPE)",
    "cpeModels.newModel": "Novo Modelo",
    "cpeModels.loading": "Carregando modelos CPE...",
    "cpeModels.error": "Erro ao carregar modelos CPE.",
    "cpeModels.empty": "Nenhum modelo CPE encontrado.",
    "cpeModels.createFirst": "Criar seu primeiro Modelo CPE",
    "cpeModels.tableVendor": "Fabricante",
    "cpeModels.tableModel": "Modelo",
    "cpeModels.tableFwVersion": "Versão FW",
    "cpeModels.tableWifi": "WiFi",
    "cpeModels.tablePorts": "Portas",
    "cpeModels.editTitle": "Editar Modelo CPE",
    "cpeModels.newTitle": "Novo Modelo CPE",
    "cpeModels.editDesc": "Edite os campos do modelo CPE.",
    "cpeModels.newDesc": "Preencha os campos para criar um novo modelo CPE.",
    "cpeModels.formFwVersion": "Versão Firmware",
    "cpeModels.formFwPlaceholder": "Ex: V3R017C00S100",
    "cpeModels.formWifi": "Bandas WiFi",
    "cpeModels.formWifiPlaceholder": "Ex: 2.4GHz, 5GHz",
    "cpeModels.formPorts": "Portas",
    "cpeModels.formPortsPlaceholder": "Ex: 4x GE, 2x POTS, 1x USB",
    "cpeModels.formDescPlaceholder": "Ex: ONT GPON para clientes residenciais",
    "cpeModels.deleteTitle": "Excluir Modelo CPE",
    "cpeModels.deleteMessage": "Tem certeza que deseja excluir",

    "firmware.title": "Firmware",
    "firmware.description": "Gerencie versões de firmware e realize deploys em engines",
    "firmware.newFirmware": "Novo Firmware",
    "firmware.loading": "Carregando firmware...",
    "firmware.error": "Erro ao carregar firmware.",
    "firmware.empty": "Nenhum firmware encontrado.",
    "firmware.createFirst": "Criar seu primeiro Firmware",
    "firmware.tableName": "Nome",
    "firmware.tableVersion": "Versão",
    "firmware.tableModel": "Modelo",
    "firmware.tableSize": "Tamanho",
    "firmware.editTitle": "Editar Firmware",
    "firmware.newTitle": "Novo Firmware",
    "firmware.editDesc": "Edite os campos do firmware.",
    "firmware.newDesc": "Preencha os campos para cadastrar um novo firmware.",
    "firmware.formVersion": "Versão *",
    "firmware.formVersionPlaceholder": "Ex: 3.2.1",
    "firmware.versionRequired": "Versão é obrigatória.",
    "firmware.formModel": "Modelo (ID)",
    "firmware.formModelPlaceholder": "Ex: 5",
    "firmware.formFile": "Arquivo",
    "firmware.uploadFuture": "Upload de arquivo via API disponível em versão futura.",
    "firmware.formDesc": "Descrição",
    "firmware.formDescPlaceholder": "Ex: Release notes da versão",
    "firmware.deleteTitle": "Excluir Firmware",
    "firmware.deleteMessage": "Tem certeza que deseja excluir",
    "firmware.deployTitle": "Deploy de Firmware",
    "firmware.deployDesc": "Implantar nos engines selecionados.",
    "firmware.deployEngines": "Engine UUIDs",
    "firmware.deployPlaceholder": "UUID1, UUID2, UUID3 (separados por vírgula)",
    "firmware.deploySeparator": "Separe múltiplos UUIDs por vírgula.",
    "firmware.deploying": "Implantando...",
    "firmware.deploy": "Implantar",
    "firmware.deployError": "Falha ao implantar.",
    "firmware.deploySuccess": "Deploy iniciado com sucesso!",

    "files.title": "Arquivos",
    "files.description": "Gerencie arquivos de firmware, configuração e outros recursos",
    "files.newFile": "Novo Arquivo",
    "files.loading": "Carregando arquivos...",
    "files.error": "Erro ao carregar arquivos.",
    "files.empty": "Nenhum arquivo encontrado.",
    "files.createFirst": "Criar seu primeiro Arquivo",
    "files.tableType": "Tipo",
    "files.tableSize": "Tamanho",
    "files.fileTypeImage": "Imagem",
    "files.fileTypeText": "Texto",
    "files.fileTypeBinary": "Binário",
    "files.editTitle": "Editar Arquivo",
    "files.newTitle": "Novo Arquivo",
    "files.editDesc": "Edite os metadados do arquivo.",
    "files.newDesc": "Preencha os campos para adicionar um novo arquivo.",
    "files.formNamePlaceholder": "Ex: fw_hg8245h_v3.2.1.bin",
    "files.formMime": "Descrição / Tipo MIME",
    "files.formMimePlaceholder": "Ex: application/octet-stream",
    "files.deleteTitle": "Excluir Arquivo",
    "files.deleteMessage": "Tem certeza que deseja excluir",

    "users.title": "Usuários",
    "users.newUser": "Novo Usuário",
    "users.searchPlaceholder": "Buscar por nome ou email...",
    "users.allRoles": "Todas as funções",
    "users.roleDeveloper": "Desenvolvedor",
    "users.roleAdmin": "Admin",
    "users.roleOperator": "Operador",
    "users.tableName": "Nome",
    "users.tableEmail": "Email",
    "users.tableRole": "Função",
    "users.tableStatus": "Status",
    "users.tableActions": "Ações",
    "users.empty": "Nenhum usuário encontrado",
    "users.deleteTitle": "Excluir usuário",
    "users.deleteMessage": "Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.",

    "operators.pageTitle": "Operadores",
    "operators.newOperator": "Novo Operador",
    "operators.never": "Nunca",
    "operators.searchPlaceholder": "Buscar por nome ou email...",
    "operators.empty": "Nenhum operador encontrado",
    "operators.tableUsername": "Usuário",
    "operators.tableFullName": "Nome",
    "operators.tableEmail": "Email",
    "operators.tableRole": "Papel",
    "operators.tableLastLogin": "Último login",
    "operators.tableStatus": "Status",
    "operators.tableActions": "Ações",
    "operators.statusActive": "Ativo",
    "operators.statusInactive": "Inativo",
    "operators.reactivate": "Reativar",
    "operators.createTitle": "Novo Operador",
    "operators.password": "Senha",
    "operators.showPassword": "Mostrar senha",
    "operators.hidePassword": "Ocultar senha",
    "operators.deactivateButton": "Desativar",
    "operators.deactivateTitle": "Desativar operador?",
    "operators.deactivateMessage": "O operador \"{{name}}\" será desativado (soft delete).",
    "operators.deleteButton": "Excluir",
    "operators.deleteTitle": "Excluir operador?",
    "operators.deleteMessage": "Excluir definitivamente {{name}}? Esta ação é permanente e remove também as passkeys do operador.",
    "operators.setPasswordButton": "Definir senha",
    "operators.setPasswordTitle": "Definir senha",
    "operators.setPasswordFor": "Definir senha para {{name}}",
    "operators.currentPassword": "Senha atual",
    "operators.newPassword": "Nova senha",
    "operators.confirmPassword": "Confirmar nova senha",
    "operators.passwordMismatch": "As senhas não coincidem",

    "profile.changePassword.title": "Altere sua senha",
    "profile.changePassword.description": "Por segurança, você deve definir uma nova senha antes de continuar.",
    "profile.changePassword.currentPassword": "Senha atual",
    "profile.changePassword.newPassword": "Nova senha",
    "profile.changePassword.confirmPassword": "Confirmar nova senha",
    "profile.changePassword.submit": "Salvar nova senha",
    "profile.changePassword.required": "Preencha todos os campos.",
    "profile.changePassword.mismatch": "As senhas não coincidem.",
    "profile.changePassword.weakPassword": "A nova senha deve ter ao menos 12 caracteres, com maiúscula, minúscula, número e símbolo.",
    "profile.changePassword.invalidCurrent": "Senha atual incorreta.",
    "profile.changePassword.error": "Não foi possível alterar a senha. Tente novamente.",

    "settings.title": "Configurações",
    "backups.title": "Backups",
    "billing.title": "Faturamento",
    "profile.title": "Perfil",
    "profile.name": "Nome",
    "profile.email": "Email",
    "profile.role": "Função",
    "profile.roleDeveloper": "Desenvolvedor",
    "profile.roleAdmin": "Administrador",
    "profile.roleOperator": "Operador",
    "profile.sessionExpires": "Sessão expira em",
    "profile.authorizedEngines": "Engines autorizados",
    "profile.none": "Nenhum",
    "profile.notAuthenticated": "Não autenticado",
    "profile.authMethod": "Método de autenticação",
    "profile.authWebauthn": "WebAuthn Local",
    "profile.authSso": "SSO (Core)",
    "profile.authPassword": "Senha local",
    "profile.session": "Sessão",
    "profile.renewSession": "Renovar sessão",
    "profile.localSessionNote": "Sessões locais não podem ser renovadas (sem conexão com o Core).",
    "profile.coreConnection": "Conexão com o Core",
    "profile.connected": "Conectado",
    "profile.disconnected": "Offline",
    "profile.passkeys.title": "Passkeys",
    "profile.passkeys.empty": "Nenhuma passkey registrada.",
    "profile.passkeys.created": "Criada em",
    "profile.passkeys.revoke": "Revogar passkey",
    "profile.passkeys.revokeTitle": "Revogar passkey?",
    "profile.passkeys.revokeMessage": "A passkey \"{{name}}\" será revogada e não poderá mais ser usada para login. Esta ação não pode ser desfeita.",
    "profile.passkeys.revokeConfirm": "Revogar",
    "profile.passkeys.revoked": "Passkey revogada com sucesso.",
    "profile.passkeys.error": "Falha ao carregar as passkeys. Tente novamente.",
    "profile.passkeys.notFound": "Passkey não encontrada ou não disponível.",
    "notFound.message": "Página não encontrada",
    "createEngine.title": "Criar Engine Node",
    "createEngine.description": "Registrar um novo Engine Node no registro.",
    "createEngine.searchMinChars": "Digite ao menos 3 caracteres para buscar",
    "createEngine.formUrl": "URL Base",
    "createEngine.formUrlPlaceholder": "https://engine.cliente.com",
    "createEngine.formChannel": "Canal",
    "createEngine.channelStable": "Stable",
    "createEngine.channelBeta": "Beta",
    "createEngine.channelAlfa": "Alfa",
    "createEngine.formCloudHosted": "Hospedado na Nuvem",
    "createEngine.formIpv4": "IPv4 do Servidor",
    "createEngine.formIpv6": "IPv6 do Servidor",
    "createEngine.formCaaEmail": "E-mail CAA (TLS)",
    "createEngine.creating": "Criando...",
    "createEngine.createButton": "Criar Engine",
    "createEngine.createError": "Falha ao criar engine node.",
    "createEngine.tokenWarning": "Guarde este token com segurança — exibido uma única vez. O API Token do node pode ser regenerado depois no painel de ações do node.",
    "createEngine.installTokenTitle": "Token de Instalação",
    "createEngine.installTokenHint24h": "Expira em 24 horas — uso único",
    "createEngine.installTokenPlaybookHint": "Use com o playbook installer/install-engine.yml",
    "createEngine.installTokenGenerating": "Gerando token de instalação...",
    "createEngine.installTokenGenerateError": "Não foi possível gerar o token de instalação. O node foi criado — gere o token no painel de ações do node.",
    "createEngine.installTokenNotStarted": "Não foi possível iniciar a geração do token de instalação. Use o painel de ações do node para gerá-lo.",
    "createEngine.installTokenPendingHint": "Token de instalação ainda não solicitado.",
    "createEngine.goToNodeActions": "Abrir Ações do node",
    "createEngine.apiTokenLabel": "API Token do node",
    "createEngine.apiTokenDesc": "Token M2M usado pelo node para autenticar chamadas à API do Core.",
    "engine.create.clientSearchMode": "Modo de busca",
    "engine.create.clientSearch": "Buscar cliente",
    "engine.create.clientSearchPlaceholder": "Buscar por nome ou CPF/CNPJ...",
    "engine.create.clientSearchMinChars": "Digite ao menos 3 caracteres ou 11 dígitos",
    "engine.create.clientSearchError": "Erro ao buscar clientes",
    "engine.create.clientsFound": "Clientes encontrados",
    "engine.create.controlClientsFound": "Clientes no RC Control (clique para importar)",
    "engine.create.noClientsFound": "Nenhum cliente encontrado",
    "engine.create.newClient": "Cadastrar novo cliente",
    "engine.create.newClientTitle": "Novo Cliente Local",
    "engine.create.clientName": "Nome do Cliente",
    "engine.create.clientDocument": "CPF/CNPJ",
    "engine.create.clientEmail": "E-mail (opcional)",
    "engine.create.createClientBtn": "Cadastrar e Selecionar",
    "engine.create.creatingClient": "Cadastrando...",
    "engine.create.clientCreateError": "Erro ao cadastrar cliente",
    "engine.create.selectedClient": "Cliente Selecionado",
    "engine.create.changeClient": "Trocar",
    "engine.create.selectContract": "Contrato Ativo (obrigatório)",
    "engine.create.selectContractPlaceholder": "Selecione um contrato ativo...",
    "engine.create.contractRequired": "É obrigatório selecionar um contrato ativo",
    "engine.create.noActiveContracts": "Nenhum contrato ativo encontrado para este cliente.",
    "engine.create.goToContracts": "Ir para Contratos",
    "engine.create.contractNotActive": "O contrato selecionado não está mais ativo. Por favor, selecione outro contrato.",
    "engine.create.loadingContracts": "Carregando contratos ativos...",
    "engine.create.contractsLoadError": "Erro ao carregar contratos ativos",
    "engine.create.clientMatchAmbiguous": "Cliente já cadastrado, mas não foi possível vinculá-lo automaticamente.",
    "engine.create.clientNameAndDocumentRequired": "Nome e CPF/CNPJ são obrigatórios",
    "engine.create.clientDocumentInvalid": "CPF/CNPJ inválido (deve conter 11 ou 14 dígitos)",
    "engine.create.clientAlreadyExists": "Já existe um cliente cadastrado com este documento",
    "engine.create.clientCreateBadRequest": "Dados inválidos para cadastro de cliente",
    "engine.create.noPlan": "Sem plano",
    "engine.create.noEndDate": "Sem término",
    "engine.create.contractNotFound": "Contrato não encontrado",
    "engine.create.createBadRequest": "Requisição inválida para criação do engine",
    "engine.contracts.linkedContract": "Contrato Vinculado",
    "engine.contracts.contractLabel": "Contrato",
    "engine.contracts.planLabel": "Plano",
    "engine.contracts.clientLabel": "Cliente",
    "engine.contracts.validity": "Vigência",
    "engine.contracts.status.active": "Ativo",
    "engine.contracts.status.warning": "Aviso",
    "engine.contracts.status.blocked": "Bloqueado",
    "engine.contracts.status.revoked": "Revogado",
    "engine.contracts.unavailable": "Informações do contrato indisponíveis",
    "engine.contracts.noContract": "Nenhum contrato vinculado",
    "engine.contracts.changeContract": "Alterar Contrato",
    "engine.contracts.unlinkContract": "Desvincular Contrato",
    "engine.contracts.selectActiveContract": "Selecionar Contrato Ativo",
    "engine.contracts.pickerTitle": "Vincular Contrato ao Engine",
    "engine.contracts.pickerDescription": "Selecione um contrato ativo do cliente para vincular a este Engine.",
    "engine.contracts.pickerDescriptionAny": "Selecione um contrato ativo para vincular a este Engine.",
    "engine.contracts.saving": "Salvando...",
    "engine.contracts.saveSuccess": "Contrato atualizado com sucesso",
    "engine.contracts.saveError": "Erro ao atualizar contrato",
    "engine.contracts.pendingChange": "Alteração pendente",
    "engineActions.activateDesc": "Habilitar este Engine Node.",
    "engineActions.cloudAutoActivate": "Nodes em nuvem auto-ativam.",
    "engineActions.openEngine": "Abrir Engine",
    "engineActions.openEngineDesc": "Acessar interface do Engine Node.",
    "engineActions.restorePayment": "Restaurar Pagamento",
    "engineActions.restorePaymentDesc": "Reativar faturamento do Engine Node.",
    "engineActions.confirmRevoke": "Confirmar Revogação",
    "engineActions.confirmRevokeDesc": "Confirma revogação definitiva do Engine Node.",
    "engineActions.suspendDesc": "Suspender temporariamente o gerenciamento de CPEs.",
    "engineActions.rotateToken": "Rotacionar Token API",
    "engineActions.rotateTokenDesc": "Gerar novo segredo HMAC. Token antigo invalidado imediatamente.",
    "engineActions.tokenWarning": "Guarde com segurança — você não verá novamente.",
    "engineActions.deleteDesc": "Remover permanentemente. Exclui em cascata backups e logs.",
    "engineActions.suspendTitle": "Suspender Engine Node",
    "engineActions.suspendMessage": "Tem certeza que deseja suspender",
    "engineActions.deleteTitle": "Excluir Engine Node",
    "engineActions.deleteMessage": "Você está prestes a excluir permanentemente",
    "engineActions.confirmDelete": "Confirmar Exclusão",
    "engineActions.confirmDeletePrompt": "Digite o nome do engine para confirmar:",
    "engineActions.deletePermanently": "Excluir Permanentemente",
    "engineActions.copied": "Copiado",
    "engineActions.copy": "Copiar",
    "engineActions.rotateTokenButton": "Rotacionar Token",
    "engineActions.installToken": "Token de Instalação",
    "engineActions.installTokenDesc": "Gerar token temporário (24h) para o instalador Ansible baixar a configuração.",
    "engineActions.installTokenButton": "Gerar Install Token",
    "engineActions.installTokenWarning": "Guarde este token — válido por 24h, uso único.",
    "engine.actions.openPanelFailed": "Falha ao gerar token de acesso. Tente novamente.",
    "engineInfo.title": "Informações do Engine",
    "engineInfo.description": "Campos editáveis podem ser alterados inline — clique em Salvar Alterações para persistir.",
    "engineInfo.configSection": "Configuração",
    "engineInfo.reportedBy": "Relatado pelo Engine",
    "engineInfo.nodeId": "Node ID",
    "engineInfo.slug": "Slug",
    "engineInfo.billingStatus": "Status de Faturamento",
    "engineInfo.contractVersion": "Versão do Contrato",
    "engineInfo.containerVersions": "Versões dos Containers",
    "engineInfo.lastHeartbeat": "Último Heartbeat",
    "engineInfo.lastHealthScore": "Último Índice de Saúde",
    "engineInfo.cpeCount": "CPEs Ativos",
    "engineInfo.cpeOverage": "Excedente CPE",
    "engineInfo.tokenRotated": "Token API Rotacionado",
    "engineInfo.createdAt": "Criado em",
    "engineInfo.updatedAt": "Atualizado em",
    "engineInfo.suspendedAt": "Suspenso em",
    "engineInfo.revokedAt": "Revogado em",
    "engineInfo.haproxyIp": "HAProxy IP",
    "engineInfo.redisEndpoint": "Redis Endpoint",
    "engineInfo.clickhouseEndpoint": "ClickHouse Endpoint",
    "engineInfo.billingPeriod": "Período de Faturamento",
    "engineInfo.periodStart": "Início do Período",
    "engineInfo.periodEnd": "Fim do Período",
    "engineInfo.uspVersion": "Versão USP",
    "engineInfo.uspCpes": "CPEs USP",
    "engineInfo.formClientId": "ID do Cliente",
    "engineInfo.formWsUrl": "WS URL",
    "engineInfo.formWsUrlPlaceholder": "wss://engine.example.com",
    "engineInfo.cloudHosted": "Hospedado na Nuvem",
    "engineInfo.formMaxCpes": "Máx. CPEs",
    "engineInfo.formSoftLimit": "Limite Flexível",
    "engineInfo.formHardLimit": "Limite Rígido",
    "engineInfo.saveChanges": "Salvar Alterações",
    "engineInfo.saveError": "Falha ao salvar",
    "engineBackups.loading": "Carregando backups...",
    "engineBackups.empty": "Nenhum backup disponível",
    "engineBackups.tableFilename": "Nome do Arquivo",
    "engineBackups.tableChecksum": "Checksum",
    "engineBackups.tableCreatedAt": "Criado em",
    "engineBackups.statusUnknown": "Desconhecido",
    "engineBackups.restore": "Restaurar",
    "engineBackups.restoreConfirmTitle": "Restaurar backup no node",
    "engineBackups.restoreConfirmBody": "Esta operação substitui os dados do node pelo conteúdo deste backup. O node será reiniciado durante a restauração.",
    "engineBackups.restoreConfirmNodePrompt": "Digite o nome do node ({{node}}) para confirmar:",
    "engineBackups.restoreConfirmInputLabel": "Nome do node para confirmação",
    "engineBackups.restoreConfirmPlaceholder": "Nome do node",
    "engineBackups.restoreConfirmAction": "Restaurar backup",
    "engineBackups.restorePending": "Restauração em andamento",
    "engineBackups.restoreApplied": "Restauração aplicada",
    "engineBackups.restoreFailed": "Restauração falhou",
    "engineBackups.restoreSessionTitle": "Restaurações desta sessão",
    "engineBackups.restoreErrorLegacy": "Backup por node não é suportado — recrie o backup com o instalador atual.",
    "engineBackups.restoreErrorKeyMismatch": "O backup foi cifrado para outra chave e não pode ser restaurado.",
    "engineBackups.restoreErrorNodeUnavailable": "Node offline ou sem suporte a restauração de backup.",
    "engineBackups.restoreErrorAlreadyActive": "Outra restauração já está ativa neste node.",
    "engineBackups.restoreErrorDecryptFailed": "Falha ao decifrar o backup — consulte os registros do Core.",
    "engineBackups.restoreErrorGeneric": "Falha ao iniciar a restauração.",
    "engineBackups.restoreErrorBusy": "O node está ocupado com outra operação de restauração.",
    "engineBackups.restoreErrorKeyUnavailable": "Chave global de backup indisponível no Core — tente novamente.",
    "engineBackups.restoreErrorNotFound": "Registro da restauração não encontrado.",
    "engineBackups.restoreErrorNodeDisconnected": "O node desconectou durante a restauração.",
    "engineBackups.restoreErrorTimeout": "A restauração excedeu o tempo limite.",
    "engineBackups.restoreErrorFileUnavailable": "Arquivo de backup indisponível no armazenamento do Core.",
    "engineBackups.restoreErrorSendFailed": "Falha ao entregar o backup ao node.",
    "engineBackups.restoreErrorOverLimit": "O backup excede o limite de tamanho de restauração.",
    "engineBackups.restoreErrorEmptyPlaintext": "O conteúdo decifrado do backup está vazio.",
    "engineBackups.restoreErrorReconciled": "Restauração antiga reconciliada pelo Core — inicie uma nova operação.",
    "engineBackups.restoreErrorFailed": "A restauração falhou no node.",
    "engineBackups.restoreErrorOther": "Restauração falhou com código não reconhecido — consulte os registros do Core.",
    "engine.backupTitle": "Backups",
    "engine.download": "Baixar",
    "engine.noBackups": "Nenhum backup disponível",
    "engine.downloadFailed": "Falha ao baixar backup",
    "engine.backupSize": "Tamanho",
    "engine.backupDate": "Data",
    "engineHealth.loading": "Carregando dados de saúde...",
    "engineHealth.empty": "Sem dados de saúde ainda",
    "engineHealth.last24h": "Últimas 24 horas",
    "engineHealth.chartLabel": "Índice de Saúde",
    "engineHealth.details": "Detalhamento do índice de saúde",
    "engineHealth.currentScore": "Índice atual",
    "engineHealth.lastUpdate": "Última atualização",
    "engineHealth.component.db": "Banco de dados",
    "engineHealth.component.redis": "Redis",
    "engineHealth.component.genieacs": "GenieACS",
    "engineHealth.component.core_ws": "WebSocket Core",
    "engineHealth.statusOk": "OK",
    "engineHealth.statusDown": "Indisponível",
    "engineHealth.statusUnconfigured": "Não configurado",
    "engineHealth.pointsLost": "Pontos perdidos",
    "engineHealth.noPenalty": "Sem penalidade",
    "engineHealth.cpu": "CPU",
    "engineHealth.producerHeartbeat": "Fonte: heartbeat do Engine",
    "engineHealth.producerSentinel": "Fonte: telemetria do Sentinel",
    "engineHealth.formulaHeartbeat":
      "Base 100; -20 por serviço essencial indisponível (DB, Redis, GenieACS); -10 se o WebSocket com o Core estiver desconectado.",
    "engineHealth.formulaSentinel":
      "Base 100; -15 por serviço não saudável; -20 se CPU > 90%; -10 se CPU > 75%.",
    "engineHealth.breakdownPending":
      "Detalhamento disponível a partir do próximo heartbeat.",
    "engineHealth.noHistory24h": "Sem histórico nas últimas 24 horas.",
    "engineLogs.loading": "Carregando logs...",
    "engineLogs.filterLevel": "Filtrar por nível",
    "engineLogs.allLevels": "Todos os Níveis",
    "engineLogs.levelInfo": "Info",
    "engineLogs.levelWarn": "Aviso",
    "engineLogs.levelError": "Erro",
    "engineLogs.autoRefresh": "Atualização automática a cada 10s",
    "engineLogs.empty": "Nenhum registro de log ainda",
    "versionHistory.title": "Histórico de Versões",
    "versionHistory.noVersions": "Nenhuma versão anterior encontrada.",
    "versionHistory.reason": "Motivo",
    "noc.totalCpes": "Total de CPEs",
    "noc.onlineEngines": "Engines Online",
    "noc.activeOutages": "Rompimentos Ativos",
    "noc.inactiveDevices": "Dispositivos Inativos",
    "noc.cpeCountChart": "Contagem de CPEs (24h)",
    "noc.noHistoryData": "Sem dados históricos",
    "noc.engineHealth": "Saúde dos Engines",
    "noc.healthIndex": "Índice de Saúde",
    "noc.noEngines": "Nenhum engine registrado",
    "noc.statusOnline": "Online",
    "noc.statusOffline": "Offline",
    "noc.statusDegraded": "Degradado",
    "noc.statusSuspended": "Suspenso",
    "noc.recentOutages": "Rompimentos Recentes",
    "noc.noOutages": "Nenhum rompimento nas últimas 24h",
    "noc.avgCpu": "CPU Médio",
    "noc.totalRam": "RAM Total",
    "noc.totalDisk": "Disco Total",
    "engine.cpu": "CPU",
    "engine.ram": "RAM",
    "engine.disk": "Disco",
    "engine.services": "Serviços",
    "engine.containers": "Containers",
    "engine.infrastructure": "Infraestrutura",
    "engine.serviceName": "Serviço",
    "engine.serviceStatus": "Status",
    "engine.containerImage": "Imagem",
    "noc.active": "Ativo",
    "noc.resolved": "Resolvido",
    "noc.engineRegistry": "Registro de Engines",
    "noc.liveEvents": "Eventos ao Vivo",
    "noc.filterAll": "Todos",
    "noc.filterFaults": "Falhas",
    "noc.filterOutages": "Rompimentos",
    "noc.filterSignals": "Sinais",
    "noc.noEvents": "Nenhum evento ainda",
    "noc.device": "Dispositivo",
    "noc.olt": "OLT",
    "settings.tabGeneral": "Geral",
    "settings.tabSmtp": "SMTP",
    "settings.tabIntegrations": "Integrações",
    "settings.tabSecurity": "Segurança",
    "settings.timezone": "Timezone",
    "settings.timezoneHelp": "Tempos na interface serão exibidos neste fuso horário. Servidores sempre UTC.",
    "settings.smtpHost": "Host",
    "settings.smtpPort": "Port",
    "settings.smtpUser": "Usuário",
    "settings.smtpPassword": "Senha",
    "settings.smtpFrom": "Remetente (Email)",
    "settings.smtpEncryption": "Criptografia",
    "settings.smtpEncryptionNone": "Nenhuma",
    "settings.smtpEncryptionTls": "TLS (implícito)",
    "settings.smtpEncryptionStarttls": "STARTTLS",
    "settings.testEmail": "Testar Email",
    "settings.sending": "Enviando...",
    "settings.testEmailSent": "Email enviado com sucesso!",
    "settings.loadingSettings": "Carregando configurações...",
    "settings.saveSettings": "Salvar Configurações",
    "settings.savedSuccess": "Configurações salvas com sucesso!",
    "settings.saveError": "Erro ao salvar",
    "settings.rcControlToken": "Token API RC Control",
    "settings.rcControlTokenPlaceholder": "Bearer token gerado no painel admin do RC Control",
    "settings.rcControlTokenHelp": "Gerado em: RC Control → Configurações → Tokens de API. Escopos: read:clients ou *.",
    "settings.saveIntegration": "Salvar Integração",
    "settings.tokenSavedSuccess": "Token salvo com sucesso!",
    "settings.testToken": "Testar token",
    "settings.testing": "Testando...",
    "settings.tokenValid": "Token válido",
    "settings.tokenInvalid": "Token inválido",
    "settings.sessionTimeout": "Session Timeout (minutos)",
    "settings.sessionTimeoutHelp": "Alterações exigem reinicialização do Core para ter efeito.",
    "settings.rateLimit": "Rate Limit (por minuto)",
    "settings.tabUpdateChannels": "Canais de Update",
    "settings.tabRegistry": "Registry",
    "settings.tabBackup": "Backups",
    "settings.backupRetentionTitle": "Retenção de Backups",
    "settings.backupRetentionDescription": "Política global de retenção aplicada a todos os nodes no auto-prune (upload e worker).",
    "settings.backupRetentionMaxCount": "Máximo de backups por node",
    "settings.backupRetentionMaxCountHelp": "0 desabilita o critério de quantidade. O sistema mantém um teto absoluto de 50 backups por node (safety net).",
    "settings.backupRetentionMaxCountError": "Informe um número inteiro entre 0 e 1000.",
    "settings.backupRetentionMaxAgeDays": "Idade máxima dos backups (dias)",
    "settings.backupRetentionMaxAgeDaysHelp": "0 desabilita o critério de idade. Backups mais antigos que este limite são removidos pelo prune.",
    "settings.backupRetentionMaxAgeDaysError": "Informe um número inteiro entre 0 e 3650.",
    "settings.backupRetentionSave": "Salvar retenção",
    "settings.backupRetentionSaved": "Retenção salva com sucesso.",
    "settings.updateChannels.title": "Canais de Atualização",
    "settings.updateChannels.channel": "Canal",
    "settings.updateChannels.latestTag": "Tag Atual",
    "settings.updateChannels.releasedAt": "Lançado em",
    "settings.updateChannels.sentinelVersion": "Versão Sentinel",
    "settings.updateChannels.scheduleMode": "Modo de Agendamento",
    "settings.updateChannels.immediate": "Imediato",
    "settings.updateChannels.maintenanceWindow": "Janela de Manutenção",
    "settings.updateChannels.manual": "Manual",
    "settings.updateChannels.paused": "Pausado",
    "settings.updateChannels.windowStart": "Início",
    "settings.updateChannels.windowEnd": "Fim",
    "settings.updateChannels.timezone": "Fuso Horário",
    "settings.updateChannels.pushUpdateNow": "Forçar Update Agora",
    "settings.updateChannels.save": "Salvar",
    "settings.updateChannels.cancel": "Cancelar",
    "settings.registry.title": "Configurações do Registry",
    "settings.registry.url": "URL do Registry",
    "settings.registry.repository": "Repositório",
    "settings.registry.username": "Usuário",
    "settings.registry.password": "Senha",
    "settings.registry.passwordSaved": "Senha salva",
    "settings.registry.showPassword": "Mostrar senha",
    "settings.registry.hidePassword": "Ocultar senha",
    "settings.registry.passwordKeepHint": "Deixe em branco para manter a senha atual.",
    "settings.smtp.passwordSaved": "Senha salva",
    "settings.smtp.passwordKeepHint": "Deixe em branco para manter a senha atual.",
    "settings.registry.testConnection": "Testar Conexão",
    "settings.registry.save": "Salvar",
    "settings.registry.connectionSuccess": "Conexão bem-sucedida",
    "settings.registry.connectionFailed": "Falha na conexão",
    "backups.trigger": "Disparar Backup",
    "backups.triggering": "Disparando...",
    "backups.tableId": "ID",
    "backups.tableDate": "Data",
    "backups.tableSize": "Tamanho",
    "backups.tableType": "Tipo",
    "backups.tableNode": "Engine",
    "backups.typeDatabase": "Banco de Dados",
    "backups.typeConfig": "Configuração",
    "backups.manual": "Manual",
    "backups.automatic": "Automático",
    "backups.noBackups": "Nenhum backup encontrado",
    "backups.deleteTitle": "Excluir backup",
    "backups.deleteMessage": "Tem certeza que deseja excluir este backup?",
    "backups.effectiveRetention": "Retenção efetiva (por node)",
    "backups.effectiveRetentionValue": "{{count}} / {{days}}d",
    "billing.monthlyClose": "Fechamento Mensal",
    "billing.loadingSummary": "Carregando resumo...",
    "billing.totalEngines": "Total de Engines",
    "billing.activeCpes": "CPEs Ativos",
    "billing.activeEngines": "Engines Ativos",
    "billing.warningEngines": "Em Aviso",
    "billing.enginesBilling": "Engines - Billing",
    "billing.overageAlerts": "Alertas de Overage",
    "billing.noOverage": "Nenhum engine excedendo soft limit.",
    "billing.antiFraudAlerts": "Alertas Anti-Fraude",
    "billing.antiFraudDetected": "alertas detectados.",
    "billing.noAnomalies": "Nenhuma anomalia detectada.",
    "billing.revenueTrend": "Tendência de Receita",
    "billing.revenueChartPlaceholder": "Gráfico de receita - disponível após F08-BILLING",
    "billing.closeConfirmTitle": "Fechamento Mensal",
    "billing.closeConfirmMessage": "Confirma o fechamento de faturamento para o período atual? Esta ação aciona o BillingCalculatorWorker e gera as faturas do mês.",
    "billing.closeConfirmLabel": "Confirmar Fechamento",
    "billing.overviewSubtitle": "Visão geral e fechamento de faturamento de contratos e engines.",
    "billing.selectPeriod": "Mês de Referência",
    "billing.periodsHistory": "Histórico de Fechamentos",
    "billing.periodsCount": "período(s) registrado(s)",
    "billing.scope": "Escopo",
    "billing.scopeContract": "Contrato",
    "billing.scopeEngine": "Engine",
    "billing.reference": "Referência",
    "billing.period": "Período",
    "billing.cpeDays": "CPE-Dias / Média",
    "billing.amount": "Valor",
    "billing.status": "Status",
    "billing.dueDate": "Vencimento",
    "billing.breakdown": "Detalhamento",
    "billing.viewBreakdown": "Ver Detalhamento",
    "billing.noPeriods": "Nenhum período de faturamento registrado.",
    "billing.periodsClosed": "período(s) de contrato fechado(s).",
    "billing.engineCloseLegacy": "Fechamento Engine (Legado)",
    "billing.engineCloseConfirmTitle": "Fechamento de Engine (Legado)",
    "billing.engineCloseConfirmMessage": "Confirma o fechamento legado por engine? Esta ação aciona o BillingCalculatorWorker via ClickHouse.",
    "billing.engineCloseResult": "Resultado do Fechamento Engine",
    "billing.contractsOverview": "Faturamento por Contrato",
    "billing.totalContracts": "Total de Contratos",
    "billing.totalLabel": "Total",
    "billing.engineRef": "Engine",
    "billing.loadingPeriods": "Carregando histórico de períodos...",
    "billing.closeError": "Erro ao realizar fechamento mensal.",
    "billing.engineCloseError": "Erro ao realizar fechamento legacy de engine.",
    "billing.revenueEstimate": "Estimativa de Receita (Todos os Escopos)",
    "billing.contracts.title": "Contratos",
    "billing.contracts.id": "Contrato",
    "billing.contracts.client": "Cliente",
    "billing.contracts.plan": "Plano",
    "billing.contracts.periodsClosed": "Períodos Fechados",
    "billing.contracts.avgCpes": "Média CPEs",
    "billing.contracts.faixa": "Faixa",
    "billing.contracts.totalAmount": "Total Faturado",
    "billing.contracts.noContracts": "Nenhum contrato com períodos fechados.",
    "billing.plans.title": "Planos de Faturamento",
    "billing.plans.default": "Padrão",
    "billing.clients.title": "Clientes",

    "versioning.versionControl": "Controle de Versão",
    "versioning.commit": "Confirmar",
    "versioning.commitMessage": "Mensagem de commit",
    "versioning.history": "Histórico",
    "versioning.branch": "Branch",
    "versioning.branches": "Branches",
    "versioning.createBranch": "Criar Branch",
    "versioning.branchName": "Nome da Branch",
    "versioning.baseRef": "Referência Base",
    "versioning.merge": "Mesclar",
    "versioning.mergeFrom": "Mesclar de",
    "versioning.mergeTo": "Mesclar para",
    "versioning.cherryPick": "Cherry Pick",
    "versioning.rollback": "Reverter",
    "versioning.rollbackTo": "Reverter para",
    "versioning.publish": "Publicar",
    "versioning.publishTo": "Publicar para",
    "versioning.publishAll": "Publicar Tudo",
    "versioning.staged": "Preparado",
    "versioning.staging": "Preparação",
    "versioning.stage": "Preparar",
    "versioning.unstage": "Remover da preparação",
    "versioning.clearStaging": "Limpar preparação",
    "versioning.stagedChanges": "Mudanças Preparadas",
    "versioning.noCommits": "Nenhum commit ainda",
    "versioning.noBranches": "Nenhuma branch disponível",
    "versioning.currentBranch": "Branch atual",
    "versioning.version": "Versão",
    "versioning.author": "Autor",
    "versioning.date": "Data",
    "versioning.message": "Mensagem",
    "versioning.compare": "Comparar",
    "versioning.diff": "Diff",
    "versioning.from": "De",
    "versioning.to": "Para",
    "versioning.field": "Campo",
    "versioning.noChanges": "Nenhuma alteração",
    "versioning.showAll": "Mostrar Todos",
    "versioning.showChangesOnly": "Mostrar Apenas Alterações",
    "staging.title": "Alterações em Etapa",
    "staging.empty": "Nenhuma alteração em etapa",
    "staging.remove": "Remover",
    "staging.channel": "Canal",
    "staging.clearAll": "Limpar Tudo",
    "staging.publishAll": "Publicar Tudo",
    "staging.published": "{count} itens publicados com sucesso",
    "staging.publishError": "Erro ao publicar",
    "staging.badgeLabel": "Alterações em Etapa",
    "staging.addToStaging": "Adicionar à Etapa",
    "cwmp.cwmpConfigs": "Configurações CWMP",
    "cwmp.configKey": "Chave de Configuração",
    "cwmp.configValue": "Valor de Configuração",
    "cwmp.dataType": "Tipo de Dado",
    "cwmp.configType": "Tipo de Config",
    "cwmp.tagValue": "Valor da Tag",
    "cwmp.objectName": "Nome do Objeto",
    "cwmp.ageValue": "Idade (segundos)",
    "cwmp.provisionArgs": "Argumentos da Provision",
    "cwmp.createConfig": "Criar Configuração",
    "cwmp.editConfig": "Editar Configuração",
    "cwmp.deleteConfig": "Excluir Configuração",
    "cwmp.configName": "Nome da Configuração",
    "cwmp.configDescription": "Descrição da Configuração",
    "cwmp.active": "Ativo",
    "branch.channel": "Canal",
    "branch.feature": "Funcionalidade",
    "branch.deleteBranch": "Excluir Branch",
    "branch.cannotDeleteChannel": "Não é possível excluir uma branch de canal",
    "branch.cannotDelete": "Não foi possível excluir a branch",
    "diff.fieldByField": "Campo por Campo",
    "diff.noDifferences": "Nenhuma diferença encontrada",
    "publish.publishWarning": "Os Engines receberão esta atualização na próxima sincronização. Esta ação não pode ser desfeita.",
    "publish.publishConfirm": "Confirmar Publicação",
    "publish.publishSuccess": "Publicação concluída com sucesso",
    "publish.publishFailed": "Falha ao publicar",
    "common.searchPlaceholder": "Buscar...",
    "common.searchDevice": "Buscar dispositivo...",
    "common.loadError": "Falha ao carregar. Tente novamente.",
    "common.notFound": "Não encontrado",
    "common.notFoundTitle": "404",
    "common.notFoundMessage": "Página não encontrada",
    "common.comingSoon": "Em breve",
    "common.critical": "Crítico",
    "common.ignoreReasonPrompt": "Motivo para ignorar?",
    "common.assignOltPrompt": "Atribuir ao ID da OLT:",
    "common.examplePlaceholder": "ex. 8.8.8.8",
    "common.cidrPlaceholder": "192.168.1.0/24",
    "common.oltId": "ID da OLT",
    "faults.title": "Gerenciamento de Falhas",
    "faults.searchCommands": "Buscar comandos...",
    "faults.searchBySerial": "Buscar por serial...",
    "engine.vp.title": "Parâmetros Virtuais (Cache Local)",
    "engine.provisions.title": "Provisions (Cache Local)",
    "engine.files.title": "Arquivos (Cache Local)",
    "onboarding.title": "Onboarding",
    "health.scoreTitle": "Pontuação de Saúde",
    "health.factorsTitle": "Fatores",
    "health.trendTitle": "Tendência (30 dias)",
    "status.title": "Status do Sistema",
    "status.workers": "Workers",
    "status.mariadb": "MariaDB",
    "settings.general": "Geral",
    "settings.erpTest": "Teste de ERP",
    "settings.systemLogs": "Logs do Sistema",
    "hostServices.title": "Serviços do Host (On-Premise)",
    "hostServices.config": "Configuração",
    "hostServices.configApplied": "Configuração aplicada",
    "hostServices.apply": "Aplicar",
    "hostServices.advanced": "Avançado",
    "hostServices.vendorClasses": "Classes de Vendor",
    "hostServices.testClassification": "Testar classificação",
    "hostServices.matchType": "Tipo de match",
    "hostServices.matchValue": "Valor de match",
    "hostServices.urlMode": "Modo URL",
    "hostServices.protocol": "Protocolo",
    "hostServices.acsUrl": "ACS URL",
    "hostServices.simulate": "Simular",
    "hostServices.matchedClass": "Classe encontrada",
    "hostServices.wizardCompleted": "Wizard concluído",
    "hostServices.configLoading": "Carregando config...",
    "hostServices.wizardApplied": "Serviços aplicados",
    "hostServices.wizardApplyPartial": "Aplicado com falhas parciais",
    "hostServices.wizardApplyError": "Erro ao aplicar configuração",
    "hostServices.parentInterface": "Interface pai",
    "hostServices.unbound.forwarders": "Forwarders (separados por vírgula)",
    "hostServices.unbound.forwardersPlaceholder": "8.8.8.8, 8.8.4.4",
    "hostServices.unbound.port": "Porta",
    "hostServices.unbound.generate": "Gerar config",
    "hostServices.unbound.noInterfacesSelected": "Nenhuma interface selecionada",
    "hostServices.unbound.interfaceListHelp": "DNS views são geradas automaticamente para as interfaces selecionadas:",
    "hostServices.unbound.derivedAcls": "ACLs derivadas das interfaces:",
    "hostServices.unbound.parsedAcls": "ACLs detectadas na configuração atual (somente leitura):",
    "hostServices.unbound.openResolverWarning": "Resolver aberto detectado: access-control 0.0.0.0/0 allow ou ::/0 allow encontrado. Remova para evitar recursão DNS aberta.",
    "hostServices.restart": "Reiniciar",
    "hostServices.restarting": "Reiniciando...",
    "hostServices.restartSuccess": "Serviço reiniciado",
    "hostServices.restartError": "Erro ao reiniciar serviço",
    "auth.passkeyTitle": "Passkeys (WebAuthn)",
    "auth.registerChallengeError": "Falha ao obter desafio de registro",
    "auth.registerVerificationError": "Falha na verificação do registro",
    "auth.registerPasskeyError": "Falha ao registrar passkey",
    "auth.registerPasskeySuccess": "Passkey registrada com sucesso",
    "auth.registeringPasskey": "Registrando...",
    "auth.registerPasskeyButton": "Registrar Passkey",
    "auth.passkeyIspOnly": "O registro de passkeys está disponível apenas para operadores ISP autenticados.",
    "engRemote.consoleInfo": "Terminal SSH interativo via xterm.js",
    "engRemote.tr069Info": "Query direta TR-069 para o GenieACS",
    "nodeSelector.local": "Local",
    "nodeSelector.selectNode": "Selecionar Nó",
    "nodeSelector.remoteNodes": "Nós Remotos",

    // Task 14 — Clients / Contracts / Billing Plans pages
    "billing.clients.subtitle": "Cadastro de clientes do provedor",
    "billing.clients.newClient": "Novo cliente",
    "billing.clients.searchPlaceholder": "Buscar por nome ou documento",
    "billing.clients.clearSearch": "Limpar busca",
    "billing.clients.colName": "Nome",
    "billing.clients.colDocument": "Documento",
    "billing.clients.colEmail": "E-mail",
    "billing.clients.colPhone": "Telefone",
    "billing.clients.colOrigin": "Origem",
    "billing.clients.colCreatedAt": "Criado em",
    "billing.clients.colActions": "Ações",
    "billing.clients.originControl": "RC Control",
    "billing.clients.originLocal": "Local",
    "billing.clients.emptyList": "Nenhum cliente cadastrado.",
    "billing.clients.noResults": "Nenhum cliente encontrado para a busca.",
    "billing.clients.loadMore": "Carregar mais",
    "billing.clients.loading": "Carregando clientes...",
    "billing.clients.showingCount": "Exibindo {current} de {total} clientes",
    "billing.clients.dialogCreateTitle": "Novo cliente",
    "billing.clients.dialogEditTitle": "Editar cliente",
    "billing.clients.dialogDescription": "Busque o cliente no RC Control ou preencha os dados manualmente.",
    "billing.clients.controlSearchTitle": "Buscar no RC Control",
    "billing.clients.controlSearchHelp": "A busca consulta o RC Control pelo nome do assinante. Se o serviço estiver indisponível, o cadastro manual continua disponível.",
    "billing.clients.controlSearchPlaceholder": "Nome do assinante no RC Control",
    "billing.clients.controlSearchButton": "Buscar",
    "billing.clients.controlSearchSearching": "Buscando...",
    "billing.clients.controlSearchNoResults": "Nenhum assinante encontrado no RC Control.",
    "billing.clients.controlSearchSelect": "Usar este cliente",
    "billing.clients.fieldName": "Nome",
    "billing.clients.fieldNamePlaceholder": "Nome completo ou razão social",
    "billing.clients.fieldDocument": "Documento",
    "billing.clients.fieldDocumentPlaceholder": "CPF ou CNPJ",
    "billing.clients.fieldEmail": "E-mail",
    "billing.clients.fieldEmailPlaceholder": "contato@provedor.com.br",
    "billing.clients.fieldPhone": "Telefone",
    "billing.clients.fieldPhonePlaceholder": "(00) 00000-0000",
    "billing.clients.fieldControlId": "ID no RC Control",
    "billing.clients.fieldControlIdHelp": "Preenchido automaticamente quando o cliente vem do RC Control.",
    "billing.clients.save": "Salvar",
    "billing.clients.saving": "Salvando...",
    "billing.clients.cancel": "Cancelar",
    "billing.clients.editButton": "Editar",
    "billing.clients.deleteButton": "Excluir",
    "billing.clients.deleteConfirmTitle": "Excluir cliente",
    "billing.clients.deleteConfirmMessage": "Excluir o cliente {name}? Esta ação não pode ser desfeita.",
    "billing.clients.deleteConfirmLabel": "Excluir",
    "billing.clients.cancelLabel": "Cancelar",
    "billing.clients.duplicateDocumentError": "Já existe um cliente cadastrado com este documento.",
    "billing.clients.duplicateControlEntityError": "Este cliente do RC Control já está vinculado a outro cadastro.",
    "billing.clients.clientHasContractsError": "Este cliente possui contratos e não pode ser excluído.",
    "billing.clients.validationNameRequired": "Informe o nome do cliente.",
    "billing.clients.validationDocumentRequired": "Informe o documento do cliente.",
    "billing.clients.fieldControlIdPlaceholder": "Ex.: 1045",
    "billing.clients.controlSearchUnavailable": "Busca no RC Control indisponível no momento. Cadastre o cliente manualmente.",
    "billing.clients.genericError": "Não foi possível concluir a operação. Tente novamente.",
    "billing.contracts.subtitle": "Contratos de assinantes, status e histórico de faturamento",
    "billing.contracts.newContract": "Novo contrato",
    "billing.contracts.filterClient": "Cliente",
    "billing.contracts.filterClientAll": "Todos os clientes",
    "billing.contracts.filterStatus": "Status",
    "billing.contracts.filterStatusAll": "Todos os status",
    "billing.contracts.statusActive": "Ativo",
    "billing.contracts.statusWarning": "Atenção",
    "billing.contracts.statusBlocked": "Bloqueado",
    "billing.contracts.statusRevoked": "Revogado",
    "billing.contracts.colId": "ID",
    "billing.contracts.colClient": "Cliente",
    "billing.contracts.colPlan": "Plano",
    "billing.contracts.colStatus": "Status",
    "billing.contracts.colStartDate": "Início",
    "billing.contracts.colEndDate": "Término",
    "billing.contracts.colActions": "Ações",
    "billing.contracts.emptyList": "Nenhum contrato encontrado.",
    "billing.contracts.loading": "Carregando contratos...",
    "billing.contracts.actionDetails": "Detalhes",
    "billing.contracts.actionEdit": "Editar",
    "billing.contracts.actionDelete": "Excluir",
    "billing.contracts.transitionTitle": "Alterar status do contrato",
    "billing.contracts.transitionDescription": "Informe o motivo da transição e, se aplicável, o ticket relacionado.",
    "billing.contracts.transitionReason": "Motivo",
    "billing.contracts.transitionReasonPlaceholder": "Descreva o motivo da mudança de status",
    "billing.contracts.transitionTicket": "Ticket",
    "billing.contracts.transitionTicketPlaceholder": "Número do ticket (opcional)",
    "billing.contracts.transitionConfirm": "Confirmar transição",
    "billing.contracts.transitionCancel": "Cancelar",
    "billing.contracts.dialogCreateTitle": "Novo contrato",
    "billing.contracts.dialogEditTitle": "Editar contrato",
    "billing.contracts.dialogDescription": "Informe cliente, plano e vigência do contrato.",
    "billing.contracts.fieldClient": "Cliente",
    "billing.contracts.fieldClientSelect": "Selecione o cliente",
    "billing.contracts.fieldPlan": "Plano",
    "billing.contracts.fieldPlanSelect": "Selecione o plano",
    "billing.contracts.fieldStartDate": "Data de início",
    "billing.contracts.fieldEndDate": "Data de término",
    "billing.contracts.fieldNotes": "Observações",
    "billing.contracts.fieldNotesPlaceholder": "Observações sobre o contrato (opcional)",
    "billing.contracts.save": "Salvar",
    "billing.contracts.saving": "Salvando...",
    "billing.contracts.cancel": "Cancelar",
    "billing.contracts.deleteConfirmTitle": "Excluir contrato",
    "billing.contracts.deleteConfirmMessage": "Excluir o contrato {id}? Esta ação não pode ser desfeita.",
    "billing.contracts.deleteConfirmLabel": "Excluir",
    "billing.contracts.cancelLabel": "Cancelar",
    "billing.contracts.contractHasEnginesError": "Este contrato possui engines vinculadas e não pode ser excluído.",
    "billing.contracts.contractDeleteBlockedError": "Exclusão bloqueada: remova o vínculo das engines antes de excluir o contrato.",
    "billing.contracts.invalidTransitionError": "Transição de status não permitida para este contrato.",
    "billing.contracts.endDateBeforeStartDateError": "A data de término não pode ser anterior à data de início.",
    "billing.contracts.validationRequiredFields": "Preencha todos os campos obrigatórios.",
    "billing.contracts.detailsTitle": "Detalhes do contrato",
    "billing.contracts.detailsSubtitle": "Engines vinculadas e histórico de faturamento",
    "billing.contracts.enginesSectionTitle": "Engines vinculadas",
    "billing.contracts.noEnginesLinked": "Nenhuma engine vinculada a este contrato.",
    "billing.contracts.engineNodeId": "Engine",
    "billing.contracts.engineStatus": "Status",
    "billing.contracts.engineBillingStatus": "Faturamento",
    "billing.contracts.periodsSectionTitle": "Histórico de faturamento",
    "billing.contracts.periodsTruncated": "Histórico parcial: a leitura de períodos está limitada a 1000 registros. Períodos mais antigos podem não aparecer.",
    "billing.contracts.noPeriodsRecorded": "Nenhum período de faturamento registrado.",
    "billing.contracts.periodRange": "Período",
    "billing.contracts.periodCpeDays": "CPE-dias",
    "billing.contracts.periodDueDate": "Vencimento",
    "billing.contracts.periodAmount": "Valor",
    "billing.contracts.periodStatus": "Status",
    "billing.contracts.periodStatusOpen": "Aberto",
    "billing.contracts.periodStatusPaid": "Pago",
    "billing.contracts.registerPaymentAction": "Registrar pagamento",
    "billing.contracts.paymentRestoredSuccess": "Pagamento registrado e contrato restaurado para ativo.",
    "billing.contracts.paymentRecordedNotRestored": "Pagamento registrado. A reativação dos assinantes não foi aplicada.",
    "billing.contracts.paymentPeriodNotUpdated": "Pagamento registrado, mas o período de faturamento não foi atualizado. Confira o histórico antes de repetir a operação.",
    "billing.contracts.periodStatusUnknown": "Desconhecido",
    "billing.contracts.showingCount": "Exibindo {current} de {total} contratos",
    "billing.contracts.loadMore": "Carregar mais",
    "billing.contracts.dropdownTruncated": "… e mais {hidden} não exibidos. Refine a busca para localizar os demais.",
    "billing.contracts.closeDetails": "Fechar detalhes",
    "billing.contracts.genericError": "Não foi possível concluir a operação. Tente novamente.",
    "billing.plans.subtitle": "Planos de faturamento e faixas de preço por consumo",
    "billing.plans.newPlan": "Novo plano",
    "billing.plans.colName": "Nome",
    "billing.plans.colMetric": "Métrica",
    "billing.plans.colPeriodType": "Periodicidade",
    "billing.plans.colTiersSummary": "Faixas de preço",
    "billing.plans.colDefault": "Padrão",
    "billing.plans.colCreatedAt": "Criado em",
    "billing.plans.colActions": "Ações",
    "billing.plans.defaultBadge": "Padrão",
    "billing.plans.emptyList": "Nenhum plano de faturamento cadastrado.",
    "billing.plans.loading": "Carregando planos...",
    "billing.plans.actionEdit": "Editar",
    "billing.plans.actionDelete": "Excluir",
    "billing.plans.dialogCreateTitle": "Novo plano",
    "billing.plans.dialogEditTitle": "Editar plano",
    "billing.plans.dialogDescription": "Defina a métrica e as faixas de preço do plano.",
    "billing.plans.fieldName": "Nome",
    "billing.plans.fieldNamePlaceholder": "Ex.: Plano 300 Mbps",
    "billing.plans.fieldMetric": "Métrica de cobrança",
    "billing.plans.fieldDefault": "Definir como padrão",
    "billing.plans.fieldDefaultHelp": "O plano padrão é usado quando nenhum plano é informado.",
    "billing.plans.tiersSectionTitle": "Faixas de preço",
    "billing.plans.tiersSectionHelp": "Informe faixas crescentes. A última faixa pode ficar sem limite (ilimitada).",
    "billing.plans.tierColIndex": "#",
    "billing.plans.tierColUpTo": "Até",
    "billing.plans.tierColPrice": "Preço",
    "billing.plans.tierColActions": "Ações",
    "billing.plans.tierUpToUnlimited": "Ilimitado",
    "billing.plans.tierUpToPlaceholder": "Vazio = ilimitado",
    "billing.plans.tierPricePlaceholder": "0,00",
    "billing.plans.addTier": "Adicionar faixa",
    "billing.plans.removeTier": "Remover faixa",
    "billing.plans.save": "Salvar",
    "billing.plans.saving": "Salvando...",
    "billing.plans.cancel": "Cancelar",
    "billing.plans.deleteConfirmTitle": "Excluir plano",
    "billing.plans.deleteConfirmMessage": "Excluir o plano selecionado? Esta ação não pode ser desfeita.",
    "billing.plans.deleteConfirmLabel": "Excluir",
    "billing.plans.cancelLabel": "Cancelar",
    "billing.plans.planHasContractsError": "Este plano está vinculado a contratos e não pode ser excluído.",
    "billing.plans.validationNameRequired": "Informe o nome do plano.",
    "billing.plans.validationMinTiers": "Informe ao menos uma faixa de preço.",
    "billing.plans.validationMaxTiers": "Uma faixa de preços aceita no máximo 20 faixas.",
    "billing.plans.validationNullOnlyOnLastTier": "Somente a última faixa pode ficar sem limite.",
    "billing.plans.validationPositiveIntegerUpTo": "O limite da faixa deve ser um número inteiro maior ou igual a zero.",
    "billing.plans.validationStrictlyAscending": "As faixas devem ser estritamente crescentes.",
    "billing.plans.validationPricePositive": "O preço da faixa deve ser maior ou igual a zero.",
    "billing.plans.validationPriceFormat": "Informe um decimal simples (ex.: 12.50), sem expoente nem notação hexadecimal.",
    "billing.plans.validationPriceCeiling": "O preço da faixa deve ser no máximo 99999999.99.",
    "billing.plans.yes": "Sim",
    "billing.plans.fieldMetricPlaceholder": "cpe-days",
    "billing.plans.tierSummaryUpTo": "até {up_to} — {price}",
    "billing.plans.tierSummaryUnlimited": "ilimitado — {price}",
    "billing.plans.genericError": "Não foi possível concluir a operação. Tente novamente.",
    "billing.errValidation": "Dados inválidos:",
    "billing.errDuplicateDocument": "Já existe um registro com este documento ou vínculo.",
    "billing.errClientHasContracts": "Este cliente possui contratos e não pode ser excluído.",
    "billing.errPlanHasContracts": "Este plano está vinculado a contratos e não pode ser excluído.",
    "billing.errContractHasEngines": "Este contrato tem engines vinculadas e não pode ser excluído ou bloqueado.",
    "billing.errInvalidTransition": "Transição de status não permitida para este contrato.",
    "billing.errContractNotFound": "Contrato não encontrado.",
    "billing.errContractNotActive": "O contrato não está ativo para esta operação.",
    "billing.errContractDeleteBlocked": "Este contrato possui faturamento vinculado (registros de faturamento) e não pode ser excluído.",
    "billing.errorLoading": "Não foi possível carregar os dados.",
    "billing.retry": "Tentar novamente",
    "billing.errGeneric": "Não foi possível concluir a operação. Tente novamente.",
    "billing.commonValidationError": "valor inválido",

  },
  "pt-PT": {
    "common.logout": "Sair",
    "common.language": "Idioma",
    "common.backToCore": "Voltar para o Core",
    "common.toggleMenu": "Alternar menu",
    "common.toggleSidebar": "Alternar barra lateral",
    "common.breadcrumb": "Navegação estrutural",
    "common.themeSwitchDark": "Alternar para modo escuro",
    "common.themeSwitchLight": "Alternar para modo claro",
    "common.save": "Guardar",
    "common.saving": "A guardar...",
    "common.cancel": "Cancelar",
    "common.close": "Fechar",
    "common.delete": "Eliminar",
    "createEngine.apiTokenPendingHint": "O API Token do node será apresentado quando a geração do token de instalação concluir.",
    "common.edit": "Editar",
    "common.create": "Criar",
    "common.loading": "A carregar...",
    "common.error": "Erro",
    "common.search": "Pesquisar",
    "common.actions": "Ações",
    "common.status": "Estado",
    "common.name": "Nome",
    "common.description": "Descrição",
    "common.active": "Ativo",
    "common.inactive": "Inativo",
    "common.tryAgain": "Tentar novamente",
    "common.back": "Voltar",
    "common.version": "Versão",
    "common.updatedAt": "Atualizado em",
    "common.noData": "Nenhum dado disponível",
    "common.noResults": "Nenhum resultado encontrado",
    "common.saveError": "Falha ao guardar.",
    "common.deleteConfirm": "Esta ação não pode ser anulada.",
    "common.confirm": "Confirmar",
    "common.continue": "Continuar",
    "common.region": "Região",
    "common.script": "Script",
    "common.value": "Valor",
    "common.email": "Email",
    "common.type": "Tipo",
    "common.download": "Download",
    "common.previous": "Anterior",
    "common.next": "Seguinte",
    "common.page": "Página",
    "common.showing": "A mostrar",
    "common.of": "de",
    "common.suspend": "Suspender",
    "common.activate": "Ativar",
    "common.enabled": "Habilitado",
    "common.disabled": "Desabilitado",
    "common.all": "Todos",
    "common.filter": "Filtrar",
    "common.copy": "Copiar",
    "common.copied": "Copiado",
    "common.nameRequired": "Nome é obrigatório.",
    "common.suspended": "Suspenso",
    "common.success": "Sucesso",
    "common.warning": "Aviso",
    "common.danger": "Perigo",
    "common.info": "Informação",
    "common.accessDenied": "Acesso negado",

    "syncstatus.title": "Estado de Sincronização",
    "syncstatus.subtitle": "Observabilidade do ciclo de sincronização de engenharia entre a Engine e o Core",
    "syncstatus.colEngine": "Engine",
    "syncstatus.colLastCycle": "Último Ciclo",
    "syncstatus.colVersion": "Versão",
    "syncstatus.colStatus": "Estado",
    "syncstatus.colPushed": "Enviados",
    "syncstatus.colDeleted": "Removidos",
    "syncstatus.colErrors": "Erros",
    "syncstatus.colDuration": "Duração",
    "syncstatus.neverSynced": "Nunca sincronizou",
    "syncstatus.historyTitle": "Histórico de Sincronização",
    "syncstatus.lastError": "Último Erro",
    "syncstatus.noHistory": "Nenhum histórico disponível",
    "syncstatus.statusOk": "OK",
    "syncstatus.statusPushError": "Erro de Push",
    "syncstatus.statusError": "Erro",
    "syncstatus.loadingHistory": "A carregar histórico...",
    "syncstatus.relativeNow": "Agora mesmo",
    "syncstatus.relativeSeconds": "há {n}s",
    "syncstatus.relativeMinutes": "há {n}min",
    "syncstatus.relativeHours": "há {n}h",
    "syncstatus.relativeDays": "há {n}d",
    "syncstatus.pushedDel": "Enviados: {pushed} / Removidos: {deleted}",
    "syncstatus.errorsCount": "Erros: {n}",
    "syncstatus.noErrorDetail": "Sem detalhe",

    "dynvarsoverride.title": "Variáveis Configuráveis",
    "dynvarsoverride.description": "Personalize os valores de variáveis globais para este Engine Node",
    "dynvarsoverride.tableKey": "Chave",
    "dynvarsoverride.tableName": "Nome",
    "dynvarsoverride.tableGlobal": "Valor Global",
    "dynvarsoverride.tableLocal": "Valor Local",
    "dynvarsoverride.tableStatus": "Estado",
    "dynvarsoverride.tableUsage": "Uso",
    "dynvarsoverride.statusDefault": "A usar o padrão do Core",
    "dynvarsoverride.statusOverridden": "Sobrescrito localmente",
    "dynvarsoverride.empty": "Nenhuma variável configurável pelo Engine.",
    "dynvarsoverride.emptyDesc": "O Core não definiu variáveis que possam ser alteradas localmente.",
    "dynvarsoverride.editTitle": "Editar Valor Local",
    "dynvarsoverride.editDesc": "Altere o valor desta variável para este Engine.",
    "dynvarsoverride.formValue": "Valor",
    "dynvarsoverride.removeOverride": "Remover sobrescrita",
    "dynvarsoverride.removeConfirm": "Restaurar valor global de",
    "dynvarsoverride.save": "Guardar Alteração",
    "dynvarsoverride.error": "Valor inválido para o tipo de dado.",
    "dynvarsoverride.statusBlockedByCore": "Bloqueada pelo Core",
    "dynvarsoverride.statusOrphaned": "Variável removida no Core",
    "dynvarsoverride.orphanedHelp": "Esta variável já não existe no Core. A sobrescrita local está inativa — remova-a.",
    "dynvarsoverride.deleteOrphanedOverride": "Remover sobrescrita",
    "dynvarsoverride.globalRef": "Padrão do Core:",
    "dynvarsoverride.reapplyEnqueued": "Guardado. Reaplicação agendada para os dispositivos afetados.",
    "dynvarsoverride.reapplyDeduped": "Reaplicação já em curso — valor guardado.",
    "dynvarsoverride.reapplyCooldown": "Reaplicação em espera (cooldown) — valor guardado.",
    "dynvarsoverride.reapplyFailed": "Valor guardado, mas falha ao reaplicar nos dispositivos afetados.",
    "route.dynVarOverrides": "Variáveis Configuráveis",
    "sidebar.opsDynVarOverrides": "Variáveis Configuráveis",
    "route.nocDashboard": "Painel NOC",
    "route.engineRegistry": "Registo de Engines",
    "route.engVirtualParams": "Parâmetros Virtuais",
    "route.engProvisions": "Provisões",
    "route.engPresets": "Presets",
    "route.engDynamicVars": "Variáveis Dinâmicas",
    "route.engCwmpConfigs": "Configurações CWMP",
    "route.engVendorMappings": "Mapeamentos de Vendor",
    "route.engCpeModels": "Modelos CPE",
    "route.engFirmware": "Firmware",
    "route.engFiles": "Ficheiros",
    "route.engTr369Controllers": "Controladores TR-369",
    "route.engOlt": "OLT",
    "route.adminUsers": "Utilizadores",
    "route.adminSettings": "Configurações",
    "route.adminBackups": "Cópias de Segurança",
    "route.adminBilling": "Faturação",
    "route.adminBillingPlans": "Planos de Faturação",
    "route.adminBillingClients": "Clientes",
    "route.adminBillingContracts": "Contratos",
    "route.defaultTitle": "RC ACS",
    "route.dashboard": "Dashboard",
    "route.devices": "Dispositivos",
    "route.oltManagement": "Gestão OLT",
    "route.reports": "Relatórios",
    "route.operations": "Operações",
    "route.faults": "Falhas",
    "route.syncDrift": "Desvio de Sincronização",
    "route.operators": "Operadores",
    "route.engineDefault": "Gestão da Engine",
    "route.profile": "Perfil",
    "route.olts": "OLTs",

    "sidebar.dashboard": "Painel",
    "sidebar.engineRegistry": "Registo de Engines",
    "sidebar.engineering": "Engenharia",
    "sidebar.engTr069": "TR-069 (GenieACS)",
    "sidebar.engTr369": "TR-369 (USP)",
    "sidebar.engDevices": "Dispositivos",
    "sidebar.engOlt": "OLT",
    "sidebar.engOltManagement": "Gestão OLT",
    "sidebar.engDynamicVars": "Variáveis Dinâmicas",
    "sidebar.engSyncStatus": "Estado de Sincronização",
    "sidebar.engVirtualParams": "Parâmetros Virtuais",
    "sidebar.engProvisions": "Provisões",
    "sidebar.engPresets": "Presets",
    "sidebar.engCwmpConfigs": "Configurações CWMP",
    "sidebar.engFiles": "Ficheiros",
    "sidebar.engTr369Controllers": "Controladores",
    "sidebar.engVendorMappings": "Mapeamentos de Vendor",
    "sidebar.engCpeModels": "Modelos CPE",
    "sidebar.engFirmware": "Firmware",
    "sidebar.admin": "Administração",
    "sidebar.adminUsers": "Utilizadores",
    "sidebar.adminSettings": "Configurações",
    "sidebar.adminBackups": "Cópias de Segurança",
    "sidebar.adminBilling": "Faturação",
    "sidebar.adminBillingPlans": "Planos",
    "sidebar.adminBillingClients": "Clientes",
    "sidebar.adminBillingContracts": "Contratos",
    "sidebar.devices": "Dispositivos",
    "sidebar.olts": "OLTs",
    "sidebar.opsEngRemote": "Eng. Remota",
    "sidebar.opsFiles": "Arquivos",
    "sidebar.opsHealth": "Saúde",
    "sidebar.opsHostServices": "Serviços de Host",
    "sidebar.opsOnboarding": "Onboarding",
    "sidebar.opsPresets": "Presets",
    "sidebar.opsProvisions": "Provisões",
    "sidebar.opsSettings": "Configurações",
    "sidebar.opsStatus": "Status",
    "sidebar.opsOperators": "Operadores",
    "sidebar.opsVirtualParams": "Parâmetros Virtuais",
    "sidebar.reports": "Relatórios",
    "sidebar.operations": "Operações",
    "sidebar.faults": "Falhas",
    "sidebar.syncDrift": "Desvio de Sincronização",
    "login.coreTitle": "Inicie sessão para aceder à Plataforma de Gestão Core",
    "login.coreSubtitle": "Inicie sessão com a sua conta RC Control para aceder ao painel",
    "login.loginButton": "Entrar com RC Control",
    "login.redirectNotice": "Será redirecionado para o serviço de autenticação RC Control.",
    "login.acs": "RC ACS",
    "login.engineTitle": "Aceda ao painel de gestão local",
    "login.engineNode": "Engine Node",
    "login.localLogin": "Login local",
    "login.localLoginPasskey": "Login local (Passkey)",
    "login.backToCore": "Voltar para o Core",
    "login.authenticating": "A autenticar...",
    "login.verifying": "A verificar...",
    "login.ispTab": "Operador ISP",
    "login.passkeyTab": "Passkey",
    "login.username": "Utilizador",
    "login.password": "Palavra-passe",
    "login.ispLoginButton": "Entrar",
    "login.ispLoginError": "Utilizador ou palavra-passe inválidos.",
    "login.ispLoginFailed": "Falha na autenticação. Tente novamente.",
    "login.passkeyFailed": "Não foi possível autenticar com a passkey. Tente novamente.",
    "login.passkeyCancelled": "Autenticação com passkey cancelada.",
    "login.passkeyUnsupported": "Este navegador não suporta passkeys. Utilize utilizador e palavra-passe.",
    "login.sessionExpired": "Sessão expirada. Inicie sessão novamente.",
    "login.ssoInvalidToken": "Token de acesso inválido ou expirado.",
    "login.localTab": "Utilizador Local",
    "login.localUsername": "Utilizador",
    "login.localPassword": "Palavra-passe",
    "login.localLoginButton": "Entrar",
    "login.localLoginError": "Utilizador ou palavra-passe inválidos.",
    "login.invalidCredentials": "Utilizador ou palavra-passe inválidos.",
    "login.rateLimited": "Demasiadas tentativas de início de sessão. Tente novamente dentro de alguns minutos.",
    "login.coreOffline": "Core offline — funcionalidade limitada. A sessão SSO não pode ser renovada.",
    "login.serviceUnavailable": "Serviço indisponível de momento. Tente novamente mais tarde.",
    "login.connectionError": "Erro de ligação. Verifique a sua rede e tente novamente.",
    "login.loginUnavailableNode": "Início de sessão não disponível neste nó.",
    "login.directLoginUnavailable": "Início de sessão direto indisponível neste nó. Aceda via SSO do Core.",
    "login.loginWithPasskey": "Entrar com Passkey",
    "login.or": "ou",
    "login.passkeyTooltip": "Autenticação com chave de segurança ou biometria",

    "unauthorized.title": "Acesso negado",
    "unauthorized.message": "Não tem permissão para aceder a esta página.",
    "unauthorized.backToDashboard": "Voltar ao painel",
    "unauthorized.signOut": "Terminar sessão",

    "isp.role.isp_admin": "Administrador ISP",
    "isp.role.isp_operator": "Operador ISP",
    "isp.role.isp_technician": "Técnico",

    "lang.ptBR": "Português (Brasil)",
    "lang.ptPT": "Português (Portugal)",
    "lang.es": "Español",
    "lang.en": "English",
    "noc.title": "Painel NOC",
    "engineRegistry.title": "Registo de Engines",
    "engineRegistry.description": "Gerir todos os Engine Nodes entre regiões e clientes",
    "engineRegistry.newEngine": "Novo Engine",
    "engineRegistry.searchPlaceholder": "Pesquisar engines...",
    "engineRegistry.allStatuses": "Todos os Estados",
    "engineRegistry.statusOnline": "Online",
    "engineRegistry.statusOffline": "Offline",
    "engineRegistry.statusDegraded": "Degradado",
    "engineRegistry.statusSuspended": "Suspenso",
    "engineRegistry.cloud": "Nuvem",
    "engineRegistry.loading": "A carregar engine nodes...",
    "engineRegistry.empty": "Nenhum engine node encontrado",
    "engineRegistry.createFirst": "Criar o seu primeiro Engine Node",
    "engineRegistry.tableName": "Nome",
    "engineRegistry.tableClient": "Cliente",
    "engineRegistry.tableRegion": "Região",
    "engineRegistry.tableHealth": "Saúde",
    "engineRegistry.tableCpes": "CPEs",
    "engineRegistry.tableTier": "Tier",
    "engineRegistry.tableHeartbeat": "Último Heartbeat",
    "engineRegistry.never": "Nunca",
    "engineRegistry.justNow": "Agora mesmo",
    "engineRegistry.secondsAgo": "s atrás",
    "engineRegistry.minutesAgo": "min atrás",
    "engineRegistry.hoursAgo": "h atrás",
    "engineRegistry.daysAgo": "d atrás",
    "engineRegistry.edit": "Editar",
    "engineRegistry.activate": "Ativar",
    "engineRegistry.suspend": "Suspender",
    "engineRegistry.delete": "Eliminar",
    "engineRegistry.deleteTitle": "Eliminar Engine Node",
    "engineRegistry.deleteMessage": "Tem a certeza que deseja eliminar",
    "registry.errorDelete": "Falha ao eliminar o nó. Verifique as dependências e tente novamente.",
    "registry.errorConflict": "Não foi possível eliminar o nó: existem dependências que bloqueiam a eliminação.",
    "registry.errorForbidden": "Não tem permissão de administrador para eliminar este nó.",
    "registry.nameMismatch": "O nome introduzido não corresponde ao nome do nó.",
    "engineDetail.notFound": "Engine Node Não Encontrado",
    "engineDetail.notFoundMsg": "O node com ID especificado não existe.",
    "engineDetail.backToRegistry": "Voltar ao Registo",
    "engineDetail.registryBreadcrumb": "Registo de Engines",
    "engineDetail.healthIndex": "Índice de Saúde",
    "engineDetail.activeCpes": "CPEs Ativos",
    "engineDetail.licenseTier": "Tier de Licença",
    "engineDetail.maxCpes": "Máx. CPEs",
    "engineDetail.heartbeat": "Heartbeat",
    "engineDetail.loading": "A carregar engine node...",
    "engineDetail.backups": "Cópias de Segurança",
    "engineDetail.remoteLogs": "Logs Remotos",
    "engineDetail.healthHistory": "Histórico de Saúde",
    "vp.title": "Parâmetros Virtuais",
    "vp.description": "Gerir scripts de parâmetros virtuais para engenharia de CPEs",
    "vp.newParam": "Novo Parâmetro",
    "vp.loading": "A carregar parâmetros virtuais...",
    "vp.error": "Erro ao carregar parâmetros virtuais.",
    "vp.empty": "Nenhum parâmetro virtual encontrado.",
    "vp.createFirst": "Criar o seu primeiro Parâmetro Virtual",
    "vp.tableName": "Nome",
    "vp.tableDescription": "Descrição",
    "vp.tableScript": "Script",
    "vp.tableVersion": "Versão",
    "vp.tableUpdatedAt": "Atualizado em",
    "vp.tableActions": "Ações",
    "vp.editTitle": "Editar Parâmetro Virtual",
    "vp.newTitle": "Novo Parâmetro Virtual",
    "vp.editDesc": "Edite os campos do parâmetro virtual.",
    "vp.newDesc": "Preencha os campos para criar um novo parâmetro virtual.",
    "vp.formName": "Nome *",
    "vp.formNamePlaceholder": "Ex: SNR Médio",
    "vp.nameRequired": "Nome é obrigatório.",
    "vp.formDesc": "Descrição",
    "vp.formDescPlaceholder": "Ex: Calcula a média do SNR nas últimas 24h",
    "vp.formScript": "Script (JavaScript)",
    "vp.formScriptPlaceholder": "// Escreva o script JavaScript do parâmetro virtual",
    "vp.validateScript": "Validar Script",
    "vp.scriptValid": "Script válido",
    "vp.scriptInvalid": "Script inválido",
    "vp.deleteTitle": "Eliminar Parâmetro Virtual",
    "vp.deleteMessage": "Tem a certeza que deseja eliminar",
    "vp.versionHistory": "Histórico de Versões",
    "vp.versionHistoryDesc": "Versões anteriores do parâmetro virtual.",
    "vp.noVersions": "Nenhuma versão anterior encontrada.",
    "vp.versionLabel": "Versão",
    "vp.reason": "Motivo",
    "provisions.title": "Provisões",
    "provisions.description": "Gerir scripts de provisão para configuração automática de CPEs",
    "provisions.newProvision": "Nova Provisão",
    "provisions.loading": "A carregar provisões...",
    "provisions.error": "Erro ao carregar provisões.",
    "provisions.empty": "Nenhuma provisão encontrada.",
    "provisions.createFirst": "Criar a sua primeira Provisão",
    "provisions.tableName": "Nome",
    "provisions.tableScript": "Script",
    "provisions.editTitle": "Editar Provisão",
    "provisions.newTitle": "Nova Provisão",
    "provisions.editDesc": "Edite os campos da provisão.",
    "provisions.newDesc": "Preencha os campos para criar uma nova provisão.",
    "provisions.formName": "Nome *",
    "provisions.formNamePlaceholder": "Ex: Configuração Inicial PPPoE",
    "provisions.formScript": "Script (JavaScript)",
    "provisions.formScriptPlaceholder": "// Escreva o script JavaScript de provisão",
    "provisions.validate": "Validar",
    "provisions.validScript": "Script válido",
    "provisions.invalidScript": "Script inválido",
    "provisions.deleteTitle": "Eliminar Provisão",
    "provisions.deleteMessage": "Tem a certeza que deseja eliminar",
    "provisions.versionHistoryDesc": "Versões anteriores da provisão.",
    "presets.title": "Presets",
    "presets.description": "Gerir presets de configuração para aprovisionamento de CPEs",
    "presets.newPreset": "Novo Preset",
    "presets.loading": "A carregar presets...",
    "presets.error": "Erro ao carregar presets.",
    "presets.empty": "Nenhum preset encontrado.",
    "presets.createFirst": "Criar o seu primeiro Preset",
    "presets.tableName": "Nome",
    "presets.tableConfig": "Config",
    "presets.editTitle": "Editar Preset",
    "presets.newTitle": "Novo Preset",
    "presets.editDesc": "Edite os campos do preset.",
    "presets.newDesc": "Preencha os campos para criar um novo preset.",
    "presets.formName": "Nome *",
    "presets.formNamePlaceholder": "Ex: Configuração Padrão WiFi",
    "presets.formConfig": "Config (JSON)",
    "presets.channel": "Canal",
    "presets.weight": "Peso",
    "presets.schedule": "Agendamento",
    "presets.scheduleHelp": "Expressão cron (ex: 0 2 * * *)",
    "presets.events": "Eventos",
    "presets.precondition": "Pré-condição",
    "presets.preconditionHelp": "Expressão booleana avaliada antes de aplicar o preset. Ex.: Tags.Provisioned. Suporta operadores GenieACS (AND, OR, NOT, IS NULL). Placeholders {{var}} não são suportados aqui.",
    "presets.provisions": "Provisions",
    "presets.addProvision": "Adicionar Provision",
    "presets.provisionArgs": "Argumentos (JSON)",
    "presets.jsonInvalid": "JSON inválido. Corrija a sintaxe.",
    "presets.deleteTitle": "Eliminar Preset",
    "presets.deleteMessage": "Tem a certeza que deseja eliminar",
    "presets.versionHistoryDesc": "Versões anteriores do preset.",
    "dynvars.title": "Variáveis Dinâmicas",
    "dynvars.description": "Gerir variáveis dinâmicas utilizadas nos scripts de engenharia",
    "dynvars.newVar": "Nova Variável",
    "dynvars.loading": "A carregar variáveis dinâmicas...",
    "dynvars.error": "Erro ao carregar variáveis dinâmicas.",
    "dynvars.empty": "Nenhuma variável dinâmica encontrada.",
    "dynvars.createFirst": "Criar a sua primeira Variável Dinâmica",
    "dynvars.tableKey": "Chave",
    "dynvars.tableName": "Nome",
    "dynvars.tableDataType": "Tipo de Dado",
    "dynvars.tableDefault": "Valor Padrão",
    "dynvars.editTitle": "Editar Variável Dinâmica",
    "dynvars.newTitle": "Nova Variável Dinâmica",
    "dynvars.editDesc": "Edite os campos da variável dinâmica.",
    "dynvars.newDesc": "Preencha os campos para criar uma nova variável dinâmica.",
    "dynvars.formKey": "Chave *",
    "dynvars.formKeyPlaceholder": "ex: cpe_user_web",
    "dynvars.formKeyHelper": "Identificador utilizado nos scripts como {{chave}}. Sincronizado para todos os Engines. Use apenas minúsculas, números e sublinhados (_).",
    "dynvars.keyRequired": "Chave é obrigatória.",
    "dynvars.keyPatternError": "A chave deve começar com letra minúscula e conter apenas letras minúsculas, números e sublinhados (_).",
    "dynvars.previewScript": "Utilizarão nos scripts como:",
    "dynvars.formName": "Nome *",
    "dynvars.formNamePlaceholder": "Ex: Utilizador Web CPE",
    "dynvars.formNameHelper": "Nome descritivo amigável exibido apenas na listagem do painel. Nunca aparece no código dos scripts.",
    "dynvars.formDescHelper": "Anotação interna para a equipa sobre o propósito desta variável. Não é enviada aos scripts.",
    "dynvars.formDescPlaceholder": "Ex: Credencial de acesso para painel web das ONTs Huawei e ZTE",
    "dynvars.formSourceType": "Origem",
    "dynvars.formSourceTypeHelper": "De onde o Engine obtém o valor da variável no momento do envio (push) para o GenieACS.",
    "dynvars.sourceHintStaticTitle": "Valor Fixo (Estático)",
    "dynvars.sourceHintStatic": "O valor é fixo e definido no campo 'Valor Padrão'. Recomendado para a maioria dos casos (credenciais, servidor NTP, fuso horário).",
    "dynvars.sourceHintCwmpTitle": "Configuração CWMP",
    "dynvars.sourceHintCwmp": "Sincroniza valor de uma chave de config do GenieACS. Nota: a resolução automática via CWMP ainda não está implementada no Engine e utilizará o Valor Padrão como fallback.",
    "dynvars.sourceHintSystemTitle": "Configuração do Sistema",
    "dynvars.sourceHintSystem": "Lê uma configuração global do Engine. Formato: setting:CHAVE (ex.: setting:admin.smtp).",
    "dynvars.sourceHintEnvTitle": "Variável de Ambiente",
    "dynvars.sourceHintEnv": "Lê variável de ambiente do anfitrião do Engine. Deve começar com DYNVAR_ (ou ser INFORM_INTERVAL). Nomes com PASSWORD, SECRET, TOKEN, KEY, URI ou URL são bloqueados por segurança.",
    "dynvars.formSourceRef": "Referência da Origem",
    "dynvars.formSourceRefHelper": "Identificador da origem externa a ser consultada pelo Engine.",
    "dynvars.sourceRefPlaceholderCwmp": "ex: cwmp:Device.ManagementServer.URL",
    "dynvars.sourceRefPlaceholderSetting": "ex: setting:admin.smtp",
    "dynvars.sourceRefPlaceholderEnv": "ex: env:DYNVAR_NTP_SERVER",
    "dynvars.formDataType": "Tipo de Dado",
    "dynvars.formDataTypeHelper": "Define como o valor é validado antes do envio (fail-closed: valor inválido para o tipo impede o envio da provisão).",
    "dynvars.dataTypeHintString": "Texto: injetado como string literal JS segura entre aspas.",
    "dynvars.dataTypeHintBoolean": "Booleano: aceita apenas true ou false.",
    "dynvars.dataTypeHintInt": "Inteiro: número inteiro sem casas decimais (ex.: 600).",
    "dynvars.dataTypeHintJson": "JSON: objeto ou array JSON válido (ex.: {\"opcao\": 1}).",
    "dynvars.dataTypeHintEnum": "Enum: restrito aos valores definidos na lista de opções.",
    "dynvars.formDefault": "Valor Padrão",
    "dynvars.formDefaultHelper": "Valor utilizado nos scripts (se Estático) ou fallback caso a origem falhe. Se vazio e sem valor de origem, o provisionamento é cancelado.",
    "dynvars.placeholderDefaultInt": "ex: 600",
    "dynvars.formEnumOptions": "Opções do Enum",
    "dynvars.formEnumOptionsPlaceholder": "ex: bridge, router, pppoe",
    "dynvars.formEnumOptionsHint": "Valores separados por vírgula.",
    "dynvars.formEnumOptionsHelper": "Lista de valores aceites separados por vírgula. Qualquer valor fora desta lista é rejeitado.",
    "dynvars.formActive": "Ativo",
    "dynvars.formActiveHelper": "Variáveis inativas não são resolvidas. Provisões que dependem desta variável são abortadas durante o envio.",
    "dynvars.formDefaultPlaceholder": "Valor padrão (opcional)",
    "dynvars.selectDefault": "Selecione o padrão",
    "dynvars.emptyDesc": "Crie variáveis parametrizadas para injetar valores dinâmicos nos scripts de provisão e parâmetros virtuais do GenieACS sem expor credenciais fixas.",
    "dynvars.sourceType.static": "Estático",
    "dynvars.sourceType.cwmp_config": "Config CWMP",
    "dynvars.sourceType.system_setting": "Configuração do Sistema",
    "dynvars.sourceType.env_var": "Variável de Ambiente",
    "dynvars.dataType.string": "Texto",
    "dynvars.dataType.boolean": "Booleano",
    "dynvars.dataType.int": "Inteiro",
    "dynvars.dataType.json": "JSON",
    "dynvars.dataType.enum": "Enum",
    "dynvars.deleteTitle": "Eliminar Variável Dinâmica",
    "dynvars.deleteMessage": "Tem a certeza que deseja eliminar",
    "dynvars.usageColumn": "Utilização",
    "dynvars.usageFormat": "Utilizada em: {provisions} provisions, {vps} VPs, {presets} presets",
    "dynvars.scope": "Âmbito",
    "dynvars.scopeGlobal": "Global",
    "dynvars.scopeConfigurable": "Configurável",
    "dynvars.scopeConfigurableEngine": "Configurável por Engine",
    "dynvars.scopeGlobalHelper": "Global: valor fixo definido pelo Core; Engines apenas aplicam",
    "dynvars.scopeConfigurableHelper": "Configurável por Engine: o Core envia o valor predefinido; cada Engine pode definir o seu próprio valor na sua UI (aplicado aos scripts locais).",
    "dynvars.scopeToggleWarning": "Atenção: overrides existentes nos Engines ficarão inativos (não são removidos)",

    "hostServices.editInterface": "Editar interface",
    "hostServices.deleteInterface": "Excluir interface",
    "hostServices.deleteInterfaceConfirm": "Confirma exclusão da interface",
    "hostServices.deleteInterfaceWarning": "A interface será removida do kernel e da configuração. Para VLANs, a interface é excluída do sistema. Para interfaces físicas, apenas removida da configuração.",
    "hostServices.interfaceName": "Nome",
    "hostServices.ipv4Address": "Endereço IPv4",
    "hostServices.ipv6Address": "Endereço IPv6",
    "hostServices.subnet4": "Subnet IPv4",
    "hostServices.prefix6": "Prefixo IPv6",
    "hostServices.poolStart": "Início do pool",
    "hostServices.poolEnd": "Fim do pool",
    "hostServices.gateway": "Gateway",
    "hostServices.dnsServers": "Servidores DNS",
    "hostServices.leaseTime": "Tempo de lease (s)",
    "hostServices.cidr.ipv4Cidr": "IPv4 CIDR",
    "hostServices.cidr.ipv6Cidr": "IPv6 CIDR",
    "hostServices.cidr.ipv4Placeholder": "ex: 10.50.0.1/24",
    "hostServices.cidr.ipv6Placeholder": "ex: 2001:db8::1/64",
    "hostServices.cidr.auto": "auto",
    "hostServices.cidr.manual": "manual",
    "hostServices.cidr.reset": "Redefinir para derivado",
    "hostServices.cidr.reserveFirst": "Reservar primeiros",
    "hostServices.cidr.networkId": "Rede",
    "hostServices.cidr.netmask": "Máscara",
    "hostServices.cidr.prefixLength": "Prefixo",
    "hostServices.cidr.broadcast": "Broadcast",
    "hostServices.cidr.usableRange": "Range útil",
    "hostServices.cidr.totalHosts": "Hosts totais",
    "hostServices.cidr.locked": "travado",
    "hostServices.cidr.unlocked": "editável",
    "hostServices.cidr.lock": "Travar gateway",
    "hostServices.cidr.unlock": "Destravar gateway",
    "hostServices.wizard.interfaces": "Interfaces CPE",
    "hostServices.wizard.subnets": "Subnet/Prefixo",
    "hostServices.wizard.step1": "Rede & Pools CPE",
    "hostServices.wizard.step2": "TR-069 & Serviços",
    "hostServices.wizard.step3": "Preview & Commit",
    "hostServices.wizard.services": "TR-069 & Serviços",
    "hostServices.wizard.review": "Revisão",
    "hostServices.wizard.preview.kea4": "Kea DHCPv4",
    "hostServices.wizard.preview.kea6": "Kea DHCPv6",
    "hostServices.wizard.preview.unbound": "Unbound",
    "hostServices.wizard.preview.radvd": "radvd",
    "hostServices.wizard.noInterfacesSelected": "Selecione pelo menos uma interface para continuar.",
    "hostServices.wizard.overlapConflictV4": "Conflito IPv4 entre {a} e {b}",
    "hostServices.wizard.overlapConflictV6": "Conflito IPv6 entre {a} e {b}",
    "hostServices.wizard.autoDnsServers": "DNS para CPEs (auto)",
    "hostServices.wizard.noDnsServers": "nenhum",
    "hostServices.wizard.autoDnsViews": "Views DNS geradas automaticamente",
    "hostServices.wizard.acsUrlPrimary": "ACS URL primária",
    "hostServices.wizard.acsUrlFallback": "ACS URL fallback",
    "hostServices.wizard.dnsForwarders": "DNS forwarders upstream",
    "hostServices.wizard.dnsForwardersPlaceholder": "1.1.1.1, 8.8.8.8",
    "hostServices.wizard.dnsForwardersHelp": "Servidores de encaminhamento separados por vírgula. Deixe em branco para desabilitar o encaminhamento upstream.",
    "hostServices.wizard.reviewInterfaces": "Interfaces",
    "hostServices.wizard.reviewSubnet4": "Subnet IPv4",
    "hostServices.wizard.reviewPrefix6": "Prefixo IPv6",
    "hostServices.wizard.reviewForwarders": "Forwarders",
    "hostServices.wizard.noForwarders": "nenhum",
    "hostServices.wizard.reviewAcsUrl": "ACS primária",
    "hostServices.wizard.activating": "A ativar...",
    "hostServices.wizard.confirmActivate": "Confirmar e ativar",
    "hostServices.vlanIdRange": "VLAN ID deve estar entre 1 e 4094.",
    "hostServices.parentRequired": "Interface pai é obrigatória.",
    "hostServices.wizard.result.title": "Resultado da aplicação",
    "hostServices.wizard.result.applied": "aplicado",
    "hostServices.wizard.result.partial": "Aplicação parcial — alguns serviços falharam.",
    "hostServices.wizard.result.success": "Todos os serviços foram aplicados com sucesso.",
    "hostServices.wizard.result.allFailed": "Nenhum serviço pôde ser aplicado.",
    "hostServices.wizard.result.retry": "Tentar novamente",
    "hostServices.wizard.result.retrying": "A tentar...",
    "hostServices.wizard.result.close": "Fechar",
    "hostServices.wizard.result.backToEdit": "Voltar para edição",
    "hostServices.wizard.result.skipped": "ignorado (sem interfaces qualificadas)",
    "hostServices.wizard.result.noServices": "Nenhum serviço retornado pelo backend.",
    "hostServices.wizard.result.appliedCount": "serviço(s) aplicado(s).",
    "hostServices.wizard.open": "Abrir assistente de configuração",
    "hostServices.wizard.slaacOverlapNote": "Com SLAAC+DHCPv6 simultâneos (modo dual), endereços EUI-64 podem coincidir com o pool DHCPv6; o DAD do cliente resolve duplicidades. Reduza o pool ou desative SLAAC se quiser isolamento total.",

    "tr369.comingSoon": "TR-369 em breve",
    "tr369.description": "A gestão de controladores TR-369 (USP/OpenUSP) será ativada quando a implementação do controlador Go estiver concluída.",
    "vendorMappings.title": "Mapeamentos de Vendor",
    "vendorMappings.description": "Gerir mapeamentos de fabricantes e modelos para TR-069",
    "vendorMappings.newMapping": "Novo Mapeamento",
    "vendorMappings.loading": "A carregar mapeamentos...",
    "vendorMappings.error": "Erro ao carregar mapeamentos.",
    "vendorMappings.empty": "Nenhum mapeamento encontrado.",
    "vendorMappings.createFirst": "Criar o seu primeiro Mapeamento",
    "vendorMappings.tableVendor": "Fabricante",
    "vendorMappings.tableModel": "Modelo",
    "vendorMappings.tablePath": "Path",
    "vendorMappings.editTitle": "Editar Mapeamento",
    "vendorMappings.newTitle": "Novo Mapeamento",
    "vendorMappings.editDesc": "Edite os campos do mapeamento.",
    "vendorMappings.newDesc": "Preencha os campos para criar um novo mapeamento vendor.",
    "vendorMappings.formVendor": "Fabricante *",
    "vendorMappings.formVendorPlaceholder": "Ex: Huawei",
    "vendorMappings.vendorRequired": "Fabricante é obrigatório.",
    "vendorMappings.formModel": "Modelo *",
    "vendorMappings.formModelPlaceholder": "Ex: HG8245H",
    "vendorMappings.modelRequired": "Modelo é obrigatório.",
    "vendorMappings.formPath": "TR-069 Path",
    "vendorMappings.formPathPlaceholder": "Ex: InternetGatewayDevice.LANDevice.1.WLANConfiguration.1",
    "vendorMappings.formDescPlaceholder": "Ex: Mapeamento para ONTs Huawei GPON",
    "vendorMappings.deleteTitle": "Eliminar Mapeamento",
    "vendorMappings.deleteMessage": "Tem a certeza que deseja eliminar",
    "cpeModels.title": "Modelos CPE",
    "cpeModels.description": "Gerir modelos de equipamentos de clientes (CPE)",
    "cpeModels.newModel": "Novo Modelo",
    "cpeModels.loading": "A carregar modelos CPE...",
    "cpeModels.error": "Erro ao carregar modelos CPE.",
    "cpeModels.empty": "Nenhum modelo CPE encontrado.",
    "cpeModels.createFirst": "Criar o seu primeiro Modelo CPE",
    "cpeModels.tableVendor": "Fabricante",
    "cpeModels.tableModel": "Modelo",
    "cpeModels.tableFwVersion": "Versão FW",
    "cpeModels.tableWifi": "WiFi",
    "cpeModels.tablePorts": "Portas",
    "cpeModels.editTitle": "Editar Modelo CPE",
    "cpeModels.newTitle": "Novo Modelo CPE",
    "cpeModels.editDesc": "Edite os campos do modelo CPE.",
    "cpeModels.newDesc": "Preencha os campos para criar um novo modelo CPE.",
    "cpeModels.formFwVersion": "Versão Firmware",
    "cpeModels.formFwPlaceholder": "Ex: V3R017C00S100",
    "cpeModels.formWifi": "Bandas WiFi",
    "cpeModels.formWifiPlaceholder": "Ex: 2.4GHz, 5GHz",
    "cpeModels.formPorts": "Portas",
    "cpeModels.formPortsPlaceholder": "Ex: 4x GE, 2x POTS, 1x USB",
    "cpeModels.formDescPlaceholder": "Ex: ONT GPON para clientes residenciais",
    "cpeModels.deleteTitle": "Eliminar Modelo CPE",
    "cpeModels.deleteMessage": "Tem a certeza que deseja eliminar",
    "firmware.title": "Firmware",
    "firmware.description": "Gerir versões de firmware e realizar deploys em engines",
    "firmware.newFirmware": "Novo Firmware",
    "firmware.loading": "A carregar firmware...",
    "firmware.error": "Erro ao carregar firmware.",
    "firmware.empty": "Nenhum firmware encontrado.",
    "firmware.createFirst": "Criar o seu primeiro Firmware",
    "firmware.tableName": "Nome",
    "firmware.tableVersion": "Versão",
    "firmware.tableModel": "Modelo",
    "firmware.tableSize": "Tamanho",
    "firmware.editTitle": "Editar Firmware",
    "firmware.newTitle": "Novo Firmware",
    "firmware.editDesc": "Edite os campos do firmware.",
    "firmware.newDesc": "Preencha os campos para cadastrar um novo firmware.",
    "firmware.formVersion": "Versão *",
    "firmware.formVersionPlaceholder": "Ex: 3.2.1",
    "firmware.versionRequired": "Versão é obrigatória.",
    "firmware.formModel": "Modelo (ID)",
    "firmware.formModelPlaceholder": "Ex: 5",
    "firmware.formFile": "Ficheiro",
    "firmware.uploadFuture": "Upload de ficheiro via API disponível em versão futura.",
    "firmware.formDesc": "Descrição",
    "firmware.formDescPlaceholder": "Ex: Release notes da versão",
    "firmware.deleteTitle": "Eliminar Firmware",
    "firmware.deleteMessage": "Tem a certeza que deseja eliminar",
    "firmware.deployTitle": "Deploy de Firmware",
    "firmware.deployDesc": "Implantar nos engines selecionados.",
    "firmware.deployEngines": "Engine UUIDs",
    "firmware.deployPlaceholder": "UUID1, UUID2, UUID3 (separados por vírgula)",
    "firmware.deploySeparator": "Separe múltiplos UUIDs por vírgula.",
    "firmware.deploying": "A implantar...",
    "firmware.deploy": "Implantar",
    "firmware.deployError": "Falha ao implantar.",
    "firmware.deploySuccess": "Deploy iniciado com sucesso!",
    "files.title": "Ficheiros",
    "files.description": "Gerir ficheiros de firmware, configuração e outros recursos",
    "files.newFile": "Novo Ficheiro",
    "files.loading": "A carregar ficheiros...",
    "files.error": "Erro ao carregar ficheiros.",
    "files.empty": "Nenhum ficheiro encontrado.",
    "files.createFirst": "Criar o seu primeiro Ficheiro",
    "files.tableType": "Tipo",
    "files.tableSize": "Tamanho",
    "files.fileTypeImage": "Imagem",
    "files.fileTypeText": "Texto",
    "files.fileTypeBinary": "Binário",
    "files.editTitle": "Editar Ficheiro",
    "files.newTitle": "Novo Ficheiro",
    "files.editDesc": "Edite os metadados do ficheiro.",
    "files.newDesc": "Preencha os campos para adicionar um novo ficheiro.",
    "files.formNamePlaceholder": "Ex: fw_hg8245h_v3.2.1.bin",
    "files.formMime": "Descrição / Tipo MIME",
    "files.formMimePlaceholder": "Ex: application/octet-stream",
    "files.deleteTitle": "Eliminar Ficheiro",
    "files.deleteMessage": "Tem a certeza que deseja eliminar",
    "users.title": "Utilizadores",
    "users.newUser": "Novo Utilizador",
    "users.searchPlaceholder": "Pesquisar por nome ou email...",
    "users.allRoles": "Todas as funções",
    "users.roleDeveloper": "Desenvolvedor",
    "users.roleAdmin": "Admin",
    "users.roleOperator": "Operador",
    "users.tableName": "Nome",
    "users.tableEmail": "Email",
    "users.tableRole": "Função",
    "users.tableStatus": "Estado",
    "users.tableActions": "Ações",
    "users.empty": "Nenhum utilizador encontrado",
    "users.deleteTitle": "Eliminar utilizador",
    "users.deleteMessage": "Tem a certeza que deseja eliminar este utilizador? Esta ação não pode ser anulada.",

    "operators.pageTitle": "Operadores",
    "operators.newOperator": "Novo Operador",
    "operators.never": "Nunca",
    "operators.searchPlaceholder": "Pesquisar por nome ou email...",
    "operators.empty": "Nenhum operador encontrado",
    "operators.tableUsername": "Utilizador",
    "operators.tableFullName": "Nome",
    "operators.tableEmail": "Email",
    "operators.tableRole": "Papel",
    "operators.tableLastLogin": "Último início de sessão",
    "operators.tableStatus": "Estado",
    "operators.tableActions": "Ações",
    "operators.statusActive": "Ativo",
    "operators.statusInactive": "Inativo",
    "operators.reactivate": "Reativar",
    "operators.createTitle": "Novo Operador",
    "operators.password": "Palavra-passe",
    "operators.showPassword": "Mostrar palavra-passe",
    "operators.hidePassword": "Ocultar palavra-passe",
    "operators.deactivateButton": "Desativar",
    "operators.deactivateTitle": "Desativar operador?",
    "operators.deactivateMessage": "O operador \"{{name}}\" será desativado (soft delete).",
    "operators.deleteButton": "Eliminar",
    "operators.deleteTitle": "Eliminar operador?",
    "operators.deleteMessage": "Eliminar definitivamente {{name}}? Esta ação é permanente e remove também as passkeys do operador.",
    "operators.setPasswordButton": "Definir palavra-passe",
    "operators.setPasswordTitle": "Definir palavra-passe",
    "operators.setPasswordFor": "Definir palavra-passe para {{name}}",
    "operators.currentPassword": "Palavra-passe atual",
    "operators.newPassword": "Nova palavra-passe",
    "operators.confirmPassword": "Confirmar nova palavra-passe",
    "operators.passwordMismatch": "As palavras-passe não coincidem",

    "profile.changePassword.title": "Altere a sua palavra-passe",
    "profile.changePassword.description": "Por segurança, deve definir uma nova palavra-passe antes de continuar.",
    "profile.changePassword.currentPassword": "Palavra-passe atual",
    "profile.changePassword.newPassword": "Nova palavra-passe",
    "profile.changePassword.confirmPassword": "Confirmar nova palavra-passe",
    "profile.changePassword.submit": "Guardar nova palavra-passe",
    "profile.changePassword.required": "Preencha todos os campos.",
    "profile.changePassword.mismatch": "As palavras-passe não coincidem.",
    "profile.changePassword.weakPassword": "A nova palavra-passe deve ter pelo menos 12 caracteres, com maiúscula, minúscula, número e símbolo.",
    "profile.changePassword.invalidCurrent": "Palavra-passe atual incorreta.",
    "profile.changePassword.error": "Não foi possível alterar a palavra-passe. Tente novamente.",

    "settings.title": "Configurações",
    "backups.title": "Cópias de Segurança",
    "billing.title": "Faturação",
    "profile.title": "Perfil",
    "profile.name": "Nome",
    "profile.email": "Email",
    "profile.role": "Função",
    "profile.roleDeveloper": "Desenvolvedor",
    "profile.roleAdmin": "Administrador",
    "profile.roleOperator": "Operador",
    "profile.sessionExpires": "Sessão expira em",
    "profile.authorizedEngines": "Engines autorizados",
    "profile.none": "Nenhum",
    "profile.notAuthenticated": "Não autenticado",
    "profile.authMethod": "Método de autenticação",
    "profile.authWebauthn": "WebAuthn Local",
    "profile.authSso": "SSO (Core)",
    "profile.authPassword": "Palavra-passe local",
    "profile.session": "Sessão",
    "profile.renewSession": "Renovar sessão",
    "profile.localSessionNote": "As sessões locais não podem ser renovadas (sem ligação ao Core).",
    "profile.coreConnection": "Ligação ao Core",
    "profile.connected": "Ligado",
    "profile.disconnected": "Offline",
    "profile.passkeys.title": "Passkeys",
    "profile.passkeys.empty": "Nenhuma passkey registada.",
    "profile.passkeys.created": "Criada em",
    "profile.passkeys.revoke": "Revogar passkey",
    "profile.passkeys.revokeTitle": "Revogar passkey?",
    "profile.passkeys.revokeMessage": "A passkey \"{{name}}\" será revogada e não poderá mais ser usada para iniciar sessão. Esta ação não pode ser desfeita.",
    "profile.passkeys.revokeConfirm": "Revogar",
    "profile.passkeys.revoked": "Passkey revogada com sucesso.",
    "profile.passkeys.error": "Falha ao carregar as passkeys. Tente novamente.",
    "profile.passkeys.notFound": "Passkey não encontrada ou não disponível.",
    "notFound.message": "Página não encontrada",
    "createEngine.title": "Criar Engine Node",
    "createEngine.description": "Registrar um novo Engine Node no registo.",
    "createEngine.searchMinChars": "Digite ao menos 3 caracteres para pesquisar",
    "createEngine.formUrl": "URL Base",
    "createEngine.formUrlPlaceholder": "https://engine.cliente.com",
    "createEngine.formChannel": "Canal",
    "createEngine.channelStable": "Stable",
    "createEngine.channelBeta": "Beta",
    "createEngine.channelAlfa": "Alfa",
    "createEngine.formCloudHosted": "Hospedado na Nuvem",
    "createEngine.formIpv4": "IPv4 do Servidor",
    "createEngine.formIpv6": "IPv6 do Servidor",
    "createEngine.formCaaEmail": "E-mail CAA (TLS)",
    "createEngine.creating": "A criar...",
    "createEngine.createButton": "Criar Engine",
    "createEngine.createError": "Falha ao criar engine node.",
    "createEngine.tokenWarning": "Guarde este token com segurança — exibido uma única vez. O API Token do node pode ser regenerado depois no painel de ações do node.",
    "createEngine.installTokenTitle": "Token de Instalação",
    "createEngine.installTokenHint24h": "Expira em 24 horas — uso único",
    "createEngine.installTokenPlaybookHint": "Use com o playbook installer/install-engine.yml",
    "createEngine.installTokenGenerating": "A gerar token de instalação...",
    "createEngine.installTokenGenerateError": "Não foi possível gerar o token de instalação. O node foi criado — gere o token no painel de ações do node.",
    "createEngine.installTokenNotStarted": "Não foi possível iniciar a geração do token de instalação. Utilize o painel de ações do node para gerar um novo.",
    "createEngine.installTokenPendingHint": "Token de instalação ainda não solicitado.",
    "createEngine.goToNodeActions": "Abrir Ações do node",
    "createEngine.apiTokenLabel": "API Token do node",
    "createEngine.apiTokenDesc": "Token M2M usado pelo node para autenticar pedidos à API do Core.",
    "engine.create.clientSearchMode": "Modo de pesquisa",
    "engine.create.clientSearch": "Pesquisar cliente",
    "engine.create.clientSearchPlaceholder": "Pesquisar por nome ou NIF/documento...",
    "engine.create.clientSearchMinChars": "Introduza pelo menos 3 caracteres ou 11 dígitos",
    "engine.create.clientSearchError": "Erro ao pesquisar clientes",
    "engine.create.clientsFound": "Clientes encontrados",
    "engine.create.controlClientsFound": "Clientes no RC Control (clique para importar)",
    "engine.create.noClientsFound": "Nenhum cliente encontrado",
    "engine.create.newClient": "Registar novo cliente",
    "engine.create.newClientTitle": "Novo Cliente Local",
    "engine.create.clientName": "Nome do Cliente",
    "engine.create.clientDocument": "NIF / Documento",
    "engine.create.clientEmail": "E-mail (opcional)",
    "engine.create.createClientBtn": "Registar e Selecionar",
    "engine.create.creatingClient": "A registar...",
    "engine.create.clientCreateError": "Erro ao registar cliente",
    "engine.create.selectedClient": "Cliente Selecionado",
    "engine.create.changeClient": "Alterar",
    "engine.create.selectContract": "Contrato Ativo (obrigatório)",
    "engine.create.selectContractPlaceholder": "Selecione um contrato ativo...",
    "engine.create.contractRequired": "É obrigatório selecionar um contrato ativo",
    "engine.create.noActiveContracts": "Nenhum contrato ativo encontrado para este cliente.",
    "engine.create.goToContracts": "Ir para Contratos",
    "engine.create.contractNotActive": "O contrato selecionado já não se encontra ativo. Por favor, selecione outro contrato.",
    "engine.create.loadingContracts": "A carregar contratos ativos...",
    "engine.create.contractsLoadError": "Erro ao carregar contratos ativos",
    "engine.create.clientMatchAmbiguous": "Cliente já registado, mas não foi possível associá-lo automaticamente.",
    "engine.create.clientNameAndDocumentRequired": "Nome e NIF/documento são obrigatórios",
    "engine.create.clientDocumentInvalid": "NIF/documento inválido (deve conter 11 ou 14 dígitos)",
    "engine.create.clientAlreadyExists": "Já existe um cliente registado com este documento",
    "engine.create.clientCreateBadRequest": "Dados inválidos para registo de cliente",
    "engine.create.noPlan": "Sem plano",
    "engine.create.noEndDate": "Sem termo",
    "engine.create.contractNotFound": "Contrato não encontrado",
    "engine.create.createBadRequest": "Pedido inválido para criação do engine",
    "engine.contracts.linkedContract": "Contrato Vinculado",
    "engine.contracts.contractLabel": "Contrato",
    "engine.contracts.planLabel": "Plano",
    "engine.contracts.clientLabel": "Cliente",
    "engine.contracts.validity": "Vigência",
    "engine.contracts.status.active": "Ativo",
    "engine.contracts.status.warning": "Aviso",
    "engine.contracts.status.blocked": "Bloqueado",
    "engine.contracts.status.revoked": "Revogado",
    "engine.contracts.unavailable": "Informações do contrato indisponíveis",
    "engine.contracts.noContract": "Nenhum contrato vinculado",
    "engine.contracts.changeContract": "Alterar Contrato",
    "engine.contracts.unlinkContract": "Desvincular Contrato",
    "engine.contracts.selectActiveContract": "Selecionar Contrato Ativo",
    "engine.contracts.pickerTitle": "Vincular Contrato ao Engine",
    "engine.contracts.pickerDescription": "Selecione um contrato ativo do cliente para vincular a este Engine.",
    "engine.contracts.pickerDescriptionAny": "Selecione um contrato ativo para vincular a este Engine.",
    "engine.contracts.saving": "A guardar...",
    "engine.contracts.saveSuccess": "Contrato atualizado com sucesso",
    "engine.contracts.saveError": "Erro ao atualizar contrato",
    "engine.contracts.pendingChange": "Alteração pendente",
    "engineActions.activateDesc": "Habilitar este Engine Node.",
    "engineActions.cloudAutoActivate": "Nodes em nuvem auto-ativam.",
    "engineActions.openEngine": "Abrir Engine",
    "engineActions.openEngineDesc": "Aceder à interface do Engine Node.",
    "engineActions.restorePayment": "Restaurar Pagamento",
    "engineActions.restorePaymentDesc": "Reativar faturação do Engine Node.",
    "engineActions.confirmRevoke": "Confirmar Revogação",
    "engineActions.confirmRevokeDesc": "Confirma revogação definitiva do Engine Node.",
    "engineActions.suspendDesc": "Suspender temporariamente a gestão de CPEs.",
    "engineActions.rotateToken": "Rotacionar Token API",
    "engineActions.rotateTokenDesc": "Gerar novo segredo HMAC. Token antigo invalidado imediatamente.",
    "engineActions.tokenWarning": "Guarde com segurança — não verá novamente.",
    "engineActions.deleteDesc": "Remover permanentemente. Elimina em cascata cópias de segurança e logs.",
    "engineActions.suspendTitle": "Suspender Engine Node",
    "engineActions.suspendMessage": "Tem a certeza que deseja suspender",
    "engineActions.deleteTitle": "Eliminar Engine Node",
    "engineActions.deleteMessage": "Está prestes a eliminar permanentemente",
    "engineActions.confirmDelete": "Confirmar Eliminação",
    "engineActions.confirmDeletePrompt": "Digite o nome do engine para confirmar:",
    "engineActions.deletePermanently": "Eliminar Permanentemente",
    "engineActions.copied": "Copiado",
    "engineActions.copy": "Copiar",
    "engineActions.rotateTokenButton": "Rotacionar Token",
    "engineActions.installToken": "Token de Instalação",
    "engineActions.installTokenDesc": "Gerar token temporário (24h) para o instalador Ansible baixar a configuração.",
    "engineActions.installTokenButton": "Gerar Install Token",
    "engineActions.installTokenWarning": "Guarde este token — válido por 24h, uso único.",
    "engine.actions.openPanelFailed": "Falha ao gerar token de acesso. Tente novamente.",
    "engineInfo.title": "Informações do Engine",
    "engineInfo.description": "Campos editáveis podem ser alterados inline — clique em Guardar Alterações para persistir.",
    "engineInfo.configSection": "Configuração",
    "engineInfo.reportedBy": "Relatado pelo Engine",
    "engineInfo.nodeId": "Node ID",
    "engineInfo.slug": "Slug",
    "engineInfo.billingStatus": "Estado de Faturação",
    "engineInfo.contractVersion": "Versão do Contrato",
    "engineInfo.containerVersions": "Versões dos Contentores",
    "engineInfo.lastHeartbeat": "Último Heartbeat",
    "engineInfo.lastHealthScore": "Último Índice de Saúde",
    "engineInfo.cpeCount": "CPEs Ativos",
    "engineInfo.cpeOverage": "Excedente CPE",
    "engineInfo.tokenRotated": "Token API Rotacionado",
    "engineInfo.createdAt": "Criado em",
    "engineInfo.updatedAt": "Atualizado em",
    "engineInfo.suspendedAt": "Suspenso em",
    "engineInfo.revokedAt": "Revogado em",
    "engineInfo.haproxyIp": "HAProxy IP",
    "engineInfo.redisEndpoint": "Redis Endpoint",
    "engineInfo.clickhouseEndpoint": "ClickHouse Endpoint",
    "engineInfo.billingPeriod": "Período de Faturação",
    "engineInfo.periodStart": "Início do Período",
    "engineInfo.periodEnd": "Fim do Período",
    "engineInfo.uspVersion": "Versão USP",
    "engineInfo.uspCpes": "CPEs USP",
    "engineInfo.formClientId": "ID do Cliente",
    "engineInfo.formWsUrl": "WS URL",
    "engineInfo.formWsUrlPlaceholder": "wss://engine.example.com",
    "engineInfo.cloudHosted": "Hospedado na Nuvem",
    "engineInfo.formMaxCpes": "Máx. CPEs",
    "engineInfo.formSoftLimit": "Limite Flexível",
    "engineInfo.formHardLimit": "Limite Rígido",
    "engineInfo.saveChanges": "Guardar Alterações",
    "engineInfo.saveError": "Falha ao guardar",
    "engineBackups.loading": "A carregar cópias de segurança...",
    "engineBackups.empty": "Nenhuma cópia de segurança disponível",
    "engineBackups.tableFilename": "Nome do Ficheiro",
    "engineBackups.tableChecksum": "Checksum",
    "engineBackups.tableCreatedAt": "Criado em",
    "engineBackups.statusUnknown": "Desconhecido",
    "engineBackups.restore": "Restaurar",
    "engineBackups.restoreConfirmTitle": "Restaurar cópia de segurança no nó",
    "engineBackups.restoreConfirmBody": "Esta operação substitui os dados do nó pelo conteúdo desta cópia de segurança. O nó será reiniciado durante o restauro.",
    "engineBackups.restoreConfirmNodePrompt": "Escreva o nome do nó ({{node}}) para confirmar:",
    "engineBackups.restoreConfirmInputLabel": "Nome do nó para confirmação",
    "engineBackups.restoreConfirmPlaceholder": "Nome do nó",
    "engineBackups.restoreConfirmAction": "Restaurar cópia",
    "engineBackups.restorePending": "Restauro em curso",
    "engineBackups.restoreApplied": "Restauro aplicado",
    "engineBackups.restoreFailed": "O restauro falhou",
    "engineBackups.restoreSessionTitle": "Restauros desta sessão",
    "engineBackups.restoreErrorLegacy": "Cópias de segurança por nó não são suportadas — recrie a cópia com o instalador atual.",
    "engineBackups.restoreErrorKeyMismatch": "A cópia foi cifrada para outra chave e não pode ser restaurada.",
    "engineBackups.restoreErrorNodeUnavailable": "Nó offline ou sem suporte a restauro de cópias.",
    "engineBackups.restoreErrorAlreadyActive": "Já existe outro restauro ativo neste nó.",
    "engineBackups.restoreErrorDecryptFailed": "Falha ao decifrar a cópia — consulte os registos do Core.",
    "engineBackups.restoreErrorGeneric": "Falha ao iniciar o restauro.",
    "engineBackups.restoreErrorBusy": "O nó está ocupado com outra operação de restauro.",
    "engineBackups.restoreErrorKeyUnavailable": "Chave global de cópias indisponível no Core — tente novamente.",
    "engineBackups.restoreErrorNotFound": "Registo do restauro não encontrado.",
    "engineBackups.restoreErrorNodeDisconnected": "O nó desligou-se durante o restauro.",
    "engineBackups.restoreErrorTimeout": "O restauro excedeu o tempo limite.",
    "engineBackups.restoreErrorFileUnavailable": "Ficheiro de cópia indisponível no armazenamento do Core.",
    "engineBackups.restoreErrorSendFailed": "Falha ao entregar a cópia ao nó.",
    "engineBackups.restoreErrorOverLimit": "A cópia excede o limite de tamanho de restauro.",
    "engineBackups.restoreErrorEmptyPlaintext": "O conteúdo decifrado da cópia está vazio.",
    "engineBackups.restoreErrorReconciled": "Restauro antigo reconciliado pelo Core — inicie uma nova operação.",
    "engineBackups.restoreErrorFailed": "O restauro falhou no nó.",
    "engineBackups.restoreErrorOther": "O restauro falhou com um código não reconhecido — consulte os registos do Core.",
    "engine.backupTitle": "Backups",
    "engine.download": "Transferir",
    "engine.noBackups": "Nenhum backup disponível",
    "engine.downloadFailed": "Falha ao transferir backup",
    "engine.backupSize": "Tamanho",
    "engine.backupDate": "Data",
    "engineHealth.loading": "A carregar dados de saúde...",
    "engineHealth.empty": "Sem dados de saúde ainda",
    "engineHealth.last24h": "Últimas 24 horas",
    "engineHealth.chartLabel": "Índice de Saúde",
    "engineHealth.details": "Detalhamento do índice de saúde",
    "engineHealth.currentScore": "Índice atual",
    "engineHealth.lastUpdate": "Última atualização",
    "engineHealth.component.db": "Base de dados",
    "engineHealth.component.redis": "Redis",
    "engineHealth.component.genieacs": "GenieACS",
    "engineHealth.component.core_ws": "WebSocket Core",
    "engineHealth.statusOk": "OK",
    "engineHealth.statusDown": "Indisponível",
    "engineHealth.statusUnconfigured": "Não configurado",
    "engineHealth.pointsLost": "Pontos perdidos",
    "engineHealth.noPenalty": "Sem penalidade",
    "engineHealth.cpu": "CPU",
    "engineHealth.producerHeartbeat": "Fonte: heartbeat do Engine",
    "engineHealth.producerSentinel": "Fonte: telemetria do Sentinel",
    "engineHealth.formulaHeartbeat":
      "Base 100; -20 por serviço essencial indisponível (DB, Redis, GenieACS); -10 se o WebSocket com o Core estiver desligado.",
    "engineHealth.formulaSentinel":
      "Base 100; -15 por serviço não saudável; -20 se CPU > 90%; -10 se CPU > 75%.",
    "engineHealth.breakdownPending":
      "Detalhamento disponível a partir do próximo heartbeat.",
    "engineHealth.noHistory24h": "Sem histórico nas últimas 24 horas.",
    "engineLogs.loading": "A carregar logs...",
    "engineLogs.filterLevel": "Filtrar por nível",
    "engineLogs.allLevels": "Todos os Níveis",
    "engineLogs.levelInfo": "Info",
    "engineLogs.levelWarn": "Aviso",
    "engineLogs.levelError": "Erro",
    "engineLogs.autoRefresh": "Atualização automática a cada 10s",
    "engineLogs.empty": "Nenhum registo de log ainda",
    "versionHistory.title": "Histórico de Versões",
    "versionHistory.noVersions": "Nenhuma versão anterior encontrada.",
    "versionHistory.reason": "Motivo",
    "noc.totalCpes": "Total de CPEs",
    "noc.onlineEngines": "Engines Online",
    "noc.activeOutages": "Rompimentos Ativos",
    "noc.inactiveDevices": "Dispositivos Inativos",
    "noc.cpeCountChart": "Contagem de CPEs (24h)",
    "noc.noHistoryData": "Sem dados históricos",
    "noc.engineHealth": "Saúde dos Engines",
    "noc.healthIndex": "Índice de Saúde",
    "noc.noEngines": "Nenhum engine registado",
    "noc.statusOnline": "Online",
    "noc.statusOffline": "Offline",
    "noc.statusDegraded": "Degradado",
    "noc.statusSuspended": "Suspenso",
    "noc.recentOutages": "Rompimentos Recentes",
    "noc.noOutages": "Nenhum rompimento nas últimas 24h",
    "noc.avgCpu": "CPU Médio",
    "noc.totalRam": "RAM Total",
    "noc.totalDisk": "Disco Total",
    "engine.cpu": "CPU",
    "engine.ram": "RAM",
    "engine.disk": "Disco",
    "engine.services": "Serviços",
    "engine.containers": "Containers",
    "engine.infrastructure": "Infraestrutura",
    "engine.serviceName": "Serviço",
    "engine.serviceStatus": "Estado",
    "engine.containerImage": "Imagem",
    "noc.active": "Ativo",
    "noc.resolved": "Resolvido",
    "noc.engineRegistry": "Registo de Engines",
    "noc.liveEvents": "Eventos ao Vivo",
    "noc.filterAll": "Todos",
    "noc.filterFaults": "Falhas",
    "noc.filterOutages": "Rompimentos",
    "noc.filterSignals": "Sinais",
    "noc.noEvents": "Nenhum evento ainda",
    "noc.device": "Dispositivo",
    "noc.olt": "OLT",
    "settings.tabGeneral": "Geral",
    "settings.tabSmtp": "SMTP",
    "settings.tabIntegrations": "Integrações",
    "settings.tabSecurity": "Segurança",
    "settings.timezone": "Fuso Horário",
    "settings.timezoneHelp": "Os tempos na interface serão apresentados neste fuso horário. Servidores sempre UTC.",
    "settings.smtpHost": "Host",
    "settings.smtpPort": "Porta",
    "settings.smtpUser": "Utilizador",
    "settings.smtpPassword": "Senha",
    "settings.smtpFrom": "Remetente (Email)",
    "settings.smtpEncryption": "Encriptação",
    "settings.smtpEncryptionNone": "Nenhuma",
    "settings.smtpEncryptionTls": "TLS (implícito)",
    "settings.smtpEncryptionStarttls": "STARTTLS",
    "settings.testEmail": "Testar Email",
    "settings.sending": "A enviar...",
    "settings.testEmailSent": "Email enviado com sucesso!",
    "settings.loadingSettings": "A carregar configurações...",
    "settings.saveSettings": "Guardar Configurações",
    "settings.savedSuccess": "Configurações guardadas com sucesso!",
    "settings.saveError": "Erro ao guardar",
    "settings.rcControlToken": "Token API RC Control",
    "settings.rcControlTokenPlaceholder": "Bearer token gerado no painel admin do RC Control",
    "settings.rcControlTokenHelp": "Gerado em: RC Control → Configurações → Tokens de API. Âmbitos: read:clients ou *.",
    "settings.saveIntegration": "Guardar Integração",
    "settings.tokenSavedSuccess": "Token guardado com sucesso!",
    "settings.testToken": "Testar token",
    "settings.testing": "Testando...",
    "settings.tokenValid": "Token válido",
    "settings.tokenInvalid": "Token inválido",
    "settings.sessionTimeout": "Tempo de Sessão (minutos)",
    "settings.sessionTimeoutHelp": "As alterações exigem reinicialização do Core para ter efeito.",
    "settings.rateLimit": "Rate Limit (por minuto)",
    "settings.tabUpdateChannels": "Canais de Update",
    "settings.tabRegistry": "Registry",
    "settings.tabBackup": "Cópias de Segurança",
    "settings.backupRetentionTitle": "Retenção de Cópias de Segurança",
    "settings.backupRetentionDescription": "Política global de retenção aplicada a todos os nós na limpeza automática (envio e worker).",
    "settings.backupRetentionMaxCount": "Máximo de cópias de segurança por nó",
    "settings.backupRetentionMaxCountHelp": "0 desativa o critério de quantidade. O sistema mantém um teto absoluto de 50 cópias de segurança por nó (rede de segurança).",
    "settings.backupRetentionMaxCountError": "Indique um número inteiro entre 0 e 1000.",
    "settings.backupRetentionMaxAgeDays": "Idade máxima das cópias de segurança (dias)",
    "settings.backupRetentionMaxAgeDaysHelp": "0 desativa o critério de idade. As cópias de segurança mais antigas do que este limite são removidas pela limpeza automática.",
    "settings.backupRetentionMaxAgeDaysError": "Indique um número inteiro entre 0 e 3650.",
    "settings.backupRetentionSave": "Guardar retenção",
    "settings.backupRetentionSaved": "Retenção guardada com sucesso.",
    "settings.updateChannels.title": "Canais de Atualização",
    "settings.updateChannels.channel": "Canal",
    "settings.updateChannels.latestTag": "Tag Atual",
    "settings.updateChannels.releasedAt": "Lançado em",
    "settings.updateChannels.sentinelVersion": "Versão Sentinel",
    "settings.updateChannels.scheduleMode": "Modo de Agendamento",
    "settings.updateChannels.immediate": "Imediato",
    "settings.updateChannels.maintenanceWindow": "Janela de Manutenção",
    "settings.updateChannels.manual": "Manual",
    "settings.updateChannels.paused": "Pausado",
    "settings.updateChannels.windowStart": "Início",
    "settings.updateChannels.windowEnd": "Fim",
    "settings.updateChannels.timezone": "Fuso Horário",
    "settings.updateChannels.pushUpdateNow": "Forçar Update Agora",
    "settings.updateChannels.save": "Salvar",
    "settings.updateChannels.cancel": "Cancelar",
    "settings.registry.title": "Configurações do Registry",
    "settings.registry.url": "URL do Registry",
    "settings.registry.repository": "Repositório",
    "settings.registry.username": "Utilizador",
    "settings.registry.password": "Palavra-passe",
    "settings.registry.passwordSaved": "Palavra-passe guardada",
    "settings.registry.showPassword": "Mostrar palavra-passe",
    "settings.registry.hidePassword": "Ocultar palavra-passe",
    "settings.registry.passwordKeepHint": "Deixe em branco para manter a palavra-passe atual.",
    "settings.smtp.passwordSaved": "Palavra-passe guardada",
    "settings.smtp.passwordKeepHint": "Deixe em branco para manter a palavra-passe atual.",
    "settings.registry.testConnection": "Testar Ligação",
    "settings.registry.save": "Guardar",
    "settings.registry.connectionSuccess": "Ligação bem-sucedida",
    "settings.registry.connectionFailed": "Falha na ligação",
    "backups.trigger": "Disparar Backup",
    "backups.triggering": "A disparar...",
    "backups.tableId": "ID",
    "backups.tableDate": "Data",
    "backups.tableSize": "Tamanho",
    "backups.tableType": "Tipo",
    "backups.tableNode": "Engine",
    "backups.typeDatabase": "Base de Dados",
    "backups.typeConfig": "Configuração",
    "backups.manual": "Manual",
    "backups.automatic": "Automático",
    "backups.noBackups": "Nenhuma cópia de segurança encontrada",
    "backups.deleteTitle": "Eliminar cópia de segurança",
    "backups.deleteMessage": "Tem a certeza que deseja eliminar esta cópia de segurança?",
    "backups.effectiveRetention": "Retenção efetiva (por nó)",
    "backups.effectiveRetentionValue": "{{count}} / {{days}}d",
    "billing.monthlyClose": "Fechamento Mensal",
    "billing.loadingSummary": "A carregar resumo...",
    "billing.totalEngines": "Total de Engines",
    "billing.activeCpes": "CPEs Ativos",
    "billing.activeEngines": "Engines Ativos",
    "billing.warningEngines": "Em Aviso",
    "billing.enginesBilling": "Engines - Billing",
    "billing.overageAlerts": "Alertas de Overage",
    "billing.noOverage": "Nenhum engine excede o soft limit.",
    "billing.antiFraudAlerts": "Alertas Anti-Fraude",
    "billing.antiFraudDetected": "alertas detetados.",
    "billing.noAnomalies": "Nenhuma anomalia detetada.",
    "billing.revenueTrend": "Tendência de Receita",
    "billing.revenueChartPlaceholder": "Gráfico de receita - disponível após F08-BILLING",
    "billing.closeConfirmTitle": "Fechamento Mensal",
    "billing.closeConfirmMessage": "Confirma o fechamento de faturação para o período atual? Esta ação aciona o BillingCalculatorWorker e gera as faturas do mês.",
    "billing.closeConfirmLabel": "Confirmar Fechamento",
    "billing.overviewSubtitle": "Visão geral e fecho de faturação de contratos e engines.",
    "billing.selectPeriod": "Mês de Referência",
    "billing.periodsHistory": "Histórico de Fechamentos",
    "billing.periodsCount": "período(s) registado(s)",
    "billing.scope": "Âmbito",
    "billing.scopeContract": "Contrato",
    "billing.scopeEngine": "Engine",
    "billing.reference": "Referência",
    "billing.period": "Período",
    "billing.cpeDays": "CPE-Dias / Média",
    "billing.amount": "Valor",
    "billing.status": "Estado",
    "billing.dueDate": "Vencimento",
    "billing.breakdown": "Detalhamento",
    "billing.viewBreakdown": "Ver Detalhamento",
    "billing.noPeriods": "Nenhum período de faturação registado.",
    "billing.periodsClosed": "período(s) de contrato fechado(s).",
    "billing.engineCloseLegacy": "Fechamento Engine (Legado)",
    "billing.engineCloseConfirmTitle": "Fechamento de Engine (Legado)",
    "billing.engineCloseConfirmMessage": "Confirma o fechamento legado por engine? Esta ação aciona o BillingCalculatorWorker via ClickHouse.",
    "billing.engineCloseResult": "Resultado do Fecho Engine",
    "billing.contractsOverview": "Faturação por Contrato",
    "billing.totalContracts": "Total de Contratos",
    "billing.totalLabel": "Total",
    "billing.engineRef": "Engine",
    "billing.loadingPeriods": "A carregar histórico de períodos...",
    "billing.closeError": "Erro ao realizar fecho mensal.",
    "billing.engineCloseError": "Erro ao realizar fecho legado de engine.",
    "billing.revenueEstimate": "Estimativa de Receita (Todos os Âmbitos)",
    "billing.contracts.title": "Contratos",
    "billing.contracts.id": "Contrato",
    "billing.contracts.client": "Cliente",
    "billing.contracts.plan": "Plano",
    "billing.contracts.periodsClosed": "Períodos Fechados",
    "billing.contracts.avgCpes": "Média CPEs",
    "billing.contracts.faixa": "Faixa",
    "billing.contracts.totalAmount": "Total Faturado",
    "billing.contracts.noContracts": "Nenhum contrato com períodos fechados.",
    "billing.plans.title": "Planos de Faturação",
    "billing.plans.default": "Padrão",
    "billing.clients.title": "Clientes",

    "versioning.versionControl": "Controlo de Versão",
    "versioning.commit": "Confirmar",
    "versioning.commitMessage": "Mensagem de commit",
    "versioning.history": "Histórico",
    "versioning.branch": "Branch",
    "versioning.branches": "Branches",
    "versioning.createBranch": "Criar Branch",
    "versioning.branchName": "Nome da Branch",
    "versioning.baseRef": "Referência Base",
    "versioning.merge": "Fundir",
    "versioning.mergeFrom": "Fundir de",
    "versioning.mergeTo": "Fundir para",
    "versioning.cherryPick": "Cherry Pick",
    "versioning.rollback": "Reverter",
    "versioning.rollbackTo": "Reverter para",
    "versioning.publish": "Publicar",
    "versioning.publishTo": "Publicar para",
    "versioning.publishAll": "Publicar Tudo",
    "versioning.staged": "Preparado",
    "versioning.staging": "Preparação",
    "versioning.stage": "Preparar",
    "versioning.unstage": "Remover da preparação",
    "versioning.clearStaging": "Limpar preparação",
    "versioning.stagedChanges": "Alterações Preparadas",
    "versioning.noCommits": "Nenhum commit ainda",
    "versioning.noBranches": "Nenhuma branch disponível",
    "versioning.currentBranch": "Branch atual",
    "versioning.version": "Versão",
    "versioning.author": "Autor",
    "versioning.date": "Data",
    "versioning.message": "Mensagem",
    "versioning.compare": "Comparar",
    "versioning.diff": "Diff",
    "versioning.from": "De",
    "versioning.to": "Para",
    "versioning.field": "Campo",
    "versioning.noChanges": "Nenhuma alteração",
    "versioning.showAll": "Mostrar Todos",
    "versioning.showChangesOnly": "Mostrar Apenas Alterações",
    "staging.title": "Alterações em Etapa",
    "staging.empty": "Nenhuma alteração em etapa",
    "staging.remove": "Remover",
    "staging.channel": "Canal",
    "staging.clearAll": "Limpar Tudo",
    "staging.publishAll": "Publicar Tudo",
    "staging.published": "{count} itens publicados com sucesso",
    "staging.publishError": "Erro ao publicar",
    "staging.badgeLabel": "Alterações em Etapa",
    "staging.addToStaging": "Adicionar à Etapa",
    "cwmp.cwmpConfigs": "Configurações CWMP",
    "cwmp.configKey": "Chave de Configuração",
    "cwmp.configValue": "Valor de Configuração",
    "cwmp.dataType": "Tipo de Dado",
    "cwmp.configType": "Tipo de Config",
    "cwmp.tagValue": "Valor da Tag",
    "cwmp.objectName": "Nome do Objeto",
    "cwmp.ageValue": "Idade (segundos)",
    "cwmp.provisionArgs": "Argumentos da Provision",
    "cwmp.createConfig": "Criar Configuração",
    "cwmp.editConfig": "Editar Configuração",
    "cwmp.deleteConfig": "Excluir Configuração",
    "cwmp.configName": "Nome da Configuração",
    "cwmp.configDescription": "Descrição da Configuração",
    "cwmp.active": "Ativo",
    "branch.channel": "Canal",
    "branch.feature": "Funcionalidade",
    "branch.deleteBranch": "Eliminar Branch",
    "branch.cannotDeleteChannel": "Não é possível eliminar uma branch de canal",
    "branch.cannotDelete": "Não foi possível eliminar a branch",
    "diff.fieldByField": "Campo por Campo",
    "diff.noDifferences": "Nenhuma diferença encontrada",
    "publish.publishWarning": "Os Engines receberão esta atualização na próxima sincronização. Esta ação não pode ser desfeita.",
    "publish.publishConfirm": "Confirmar Publicação",
    "publish.publishSuccess": "Publicação concluída com sucesso",
    "publish.publishFailed": "Falha ao publicar",
    "common.searchPlaceholder": "Procurar...",
    "common.searchDevice": "Procurar dispositivo...",
    "common.loadError": "Falha ao carregar. Tente novamente.",
    "common.notFound": "Não encontrado",
    "common.notFoundTitle": "404",
    "common.notFoundMessage": "Página não encontrada",
    "common.comingSoon": "Em breve",
    "common.critical": "Crítico",
    "common.ignoreReasonPrompt": "Motivo para ignorar?",
    "common.assignOltPrompt": "Atribuir ao ID da OLT:",
    "common.examplePlaceholder": "ex. 8.8.8.8",
    "common.cidrPlaceholder": "192.168.1.0/24",
    "common.oltId": "ID da OLT",
    "faults.title": "Gestão de Falhas",
    "faults.searchCommands": "Procurar comandos...",
    "faults.searchBySerial": "Procurar por serial...",
    "engine.vp.title": "Parâmetros Virtuais (Cache Local)",
    "engine.provisions.title": "Provisions (Cache Local)",
    "engine.files.title": "Arquivos (Cache Local)",
    "onboarding.title": "Onboarding",
    "health.scoreTitle": "Pontuação de Saúde",
    "health.factorsTitle": "Fatores",
    "health.trendTitle": "Tendência (30 dias)",
    "status.title": "Estado do Sistema",
    "status.workers": "Workers",
    "status.mariadb": "MariaDB",
    "settings.general": "Geral",
    "settings.erpTest": "Teste de ERP",
    "settings.systemLogs": "Logs do Sistema",
    "hostServices.title": "Serviços do Host (On-Premise)",
    "hostServices.config": "Configuração",
    "hostServices.configApplied": "Configuração aplicada",
    "hostServices.apply": "Aplicar",
    "hostServices.advanced": "Avançado",
    "hostServices.vendorClasses": "Classes de Vendor",
    "hostServices.testClassification": "Testar classificação",
    "hostServices.matchType": "Tipo de match",
    "hostServices.matchValue": "Valor de match",
    "hostServices.urlMode": "Modo URL",
    "hostServices.protocol": "Protocolo",
    "hostServices.acsUrl": "ACS URL",
    "hostServices.simulate": "Simular",
    "hostServices.matchedClass": "Classe encontrada",
    "hostServices.wizardCompleted": "Wizard concluído",
    "hostServices.configLoading": "A carregar config...",
    "hostServices.wizardApplied": "Serviços aplicados",
    "hostServices.wizardApplyPartial": "Aplicado com falhas parciais",
    "hostServices.wizardApplyError": "Erro ao aplicar configuração",
    "hostServices.parentInterface": "Interface pai",
    "hostServices.unbound.forwarders": "Forwarders (separados por vírgula)",
    "hostServices.unbound.forwardersPlaceholder": "8.8.8.8, 8.8.4.4",
    "hostServices.unbound.port": "Porta",
    "hostServices.unbound.generate": "Gerar config",
    "hostServices.unbound.noInterfacesSelected": "Nenhuma interface selecionada",
    "hostServices.unbound.interfaceListHelp": "As DNS views são geradas automaticamente para as interfaces selecionadas:",
    "hostServices.unbound.derivedAcls": "ACLs derivadas das interfaces:",
    "hostServices.unbound.parsedAcls": "ACLs detetadas na configuração atual (apenas leitura):",
    "hostServices.unbound.openResolverWarning": "Resolver aberto detetado: access-control 0.0.0.0/0 allow ou ::/0 allow encontrado. Remova para evitar recursão DNS aberta.",
    "hostServices.restart": "Reiniciar",
    "hostServices.restarting": "A reiniciar...",
    "hostServices.restartSuccess": "Serviço reiniciado",
    "hostServices.restartError": "Erro ao reiniciar serviço",
    "auth.passkeyTitle": "Passkeys (WebAuthn)",
    "auth.registerChallengeError": "Falha ao obter desafio de registo",
    "auth.registerVerificationError": "Falha na verificação do registo",
    "auth.registerPasskeyError": "Falha ao registar passkey",
    "auth.registerPasskeySuccess": "Passkey registada com sucesso",
    "auth.registeringPasskey": "A registar...",
    "auth.registerPasskeyButton": "Registar Passkey",
    "auth.passkeyIspOnly": "O registo de passkeys está disponível apenas para operadores ISP autenticados.",
    "engRemote.consoleInfo": "Terminal SSH interativo via xterm.js",
    "engRemote.tr069Info": "Query direta TR-069 para o GenieACS",
    "nodeSelector.local": "Local",
    "nodeSelector.selectNode": "Selecionar Nó",
    "nodeSelector.remoteNodes": "Nós Remotos",

    // Task 14 — Clients / Contracts / Billing Plans pages
    "billing.clients.subtitle": "Registo de clientes do operador",
    "billing.clients.newClient": "Novo cliente",
    "billing.clients.searchPlaceholder": "Pesquisar por nome ou documento",
    "billing.clients.clearSearch": "Limpar pesquisa",
    "billing.clients.colName": "Nome",
    "billing.clients.colDocument": "Documento",
    "billing.clients.colEmail": "E-mail",
    "billing.clients.colPhone": "Telefone",
    "billing.clients.colOrigin": "Origem",
    "billing.clients.colCreatedAt": "Criado em",
    "billing.clients.colActions": "Ações",
    "billing.clients.originControl": "RC Control",
    "billing.clients.originLocal": "Local",
    "billing.clients.emptyList": "Nenhum cliente registado.",
    "billing.clients.noResults": "Nenhum cliente encontrado para a pesquisa.",
    "billing.clients.loadMore": "Carregar mais",
    "billing.clients.loading": "A carregar clientes...",
    "billing.clients.showingCount": "A mostrar {current} de {total} clientes",
    "billing.clients.dialogCreateTitle": "Novo cliente",
    "billing.clients.dialogEditTitle": "Editar cliente",
    "billing.clients.dialogDescription": "Pesquise o cliente no RC Control ou preencha os dados manualmente.",
    "billing.clients.controlSearchTitle": "Pesquisar no RC Control",
    "billing.clients.controlSearchHelp": "A pesquisa consulta o RC Control pelo nome do assinante. Se o serviço estiver indisponível, o registo manual continua disponível.",
    "billing.clients.controlSearchPlaceholder": "Nome do assinante no RC Control",
    "billing.clients.controlSearchButton": "Pesquisar",
    "billing.clients.controlSearchSearching": "A pesquisar...",
    "billing.clients.controlSearchNoResults": "Nenhum assinante encontrado no RC Control.",
    "billing.clients.controlSearchSelect": "Usar este cliente",
    "billing.clients.fieldName": "Nome",
    "billing.clients.fieldNamePlaceholder": "Nome completo ou designação social",
    "billing.clients.fieldDocument": "Documento",
    "billing.clients.fieldDocumentPlaceholder": "NIF ou NIPC",
    "billing.clients.fieldEmail": "E-mail",
    "billing.clients.fieldEmailPlaceholder": "contacto@operador.pt",
    "billing.clients.fieldPhone": "Telefone",
    "billing.clients.fieldPhonePlaceholder": "+351 000 000 000",
    "billing.clients.fieldControlId": "ID no RC Control",
    "billing.clients.fieldControlIdHelp": "Preenchido automaticamente quando o cliente vem do RC Control.",
    "billing.clients.save": "Guardar",
    "billing.clients.saving": "A guardar...",
    "billing.clients.cancel": "Cancelar",
    "billing.clients.editButton": "Editar",
    "billing.clients.deleteButton": "Eliminar",
    "billing.clients.deleteConfirmTitle": "Eliminar cliente",
    "billing.clients.deleteConfirmMessage": "Eliminar o cliente {name}? Esta ação não pode ser revertida.",
    "billing.clients.deleteConfirmLabel": "Eliminar",
    "billing.clients.cancelLabel": "Cancelar",
    "billing.clients.duplicateDocumentError": "Já existe um cliente registado com este documento.",
    "billing.clients.duplicateControlEntityError": "Este cliente do RC Control já está associado a outro registo.",
    "billing.clients.clientHasContractsError": "Este cliente tem contratos e não pode ser eliminado.",
    "billing.clients.validationNameRequired": "Indique o nome do cliente.",
    "billing.clients.validationDocumentRequired": "Indique o documento do cliente.",
    "billing.clients.fieldControlIdPlaceholder": "Ex.: 1045",
    "billing.clients.controlSearchUnavailable": "Pesquisa no RC Control indisponível de momento. Registe o cliente manualmente.",
    "billing.clients.genericError": "Não foi possível concluir a operação. Tente novamente.",
    "billing.contracts.subtitle": "Contratos de assinantes, estado e histórico de faturação",
    "billing.contracts.newContract": "Novo contrato",
    "billing.contracts.filterClient": "Cliente",
    "billing.contracts.filterClientAll": "Todos os clientes",
    "billing.contracts.filterStatus": "Estado",
    "billing.contracts.filterStatusAll": "Todos os estados",
    "billing.contracts.statusActive": "Ativo",
    "billing.contracts.statusWarning": "Atenção",
    "billing.contracts.statusBlocked": "Bloqueado",
    "billing.contracts.statusRevoked": "Revogado",
    "billing.contracts.colId": "ID",
    "billing.contracts.colClient": "Cliente",
    "billing.contracts.colPlan": "Plano",
    "billing.contracts.colStatus": "Estado",
    "billing.contracts.colStartDate": "Início",
    "billing.contracts.colEndDate": "Fim",
    "billing.contracts.colActions": "Ações",
    "billing.contracts.emptyList": "Nenhum contrato encontrado.",
    "billing.contracts.loading": "A carregar contratos...",
    "billing.contracts.actionDetails": "Detalhes",
    "billing.contracts.actionEdit": "Editar",
    "billing.contracts.actionDelete": "Eliminar",
    "billing.contracts.transitionTitle": "Alterar estado do contrato",
    "billing.contracts.transitionDescription": "Indique o motivo da transição e, se aplicável, o ticket associado.",
    "billing.contracts.transitionReason": "Motivo",
    "billing.contracts.transitionReasonPlaceholder": "Descreva o motivo da alteração de estado",
    "billing.contracts.transitionTicket": "Ticket",
    "billing.contracts.transitionTicketPlaceholder": "Número do ticket (opcional)",
    "billing.contracts.transitionConfirm": "Confirmar transição",
    "billing.contracts.transitionCancel": "Cancelar",
    "billing.contracts.dialogCreateTitle": "Novo contrato",
    "billing.contracts.dialogEditTitle": "Editar contrato",
    "billing.contracts.dialogDescription": "Indique o cliente, o plano e a vigência do contrato.",
    "billing.contracts.fieldClient": "Cliente",
    "billing.contracts.fieldClientSelect": "Selecione o cliente",
    "billing.contracts.fieldPlan": "Plano",
    "billing.contracts.fieldPlanSelect": "Selecione o plano",
    "billing.contracts.fieldStartDate": "Data de início",
    "billing.contracts.fieldEndDate": "Data de fim",
    "billing.contracts.fieldNotes": "Observações",
    "billing.contracts.fieldNotesPlaceholder": "Observações sobre o contrato (opcional)",
    "billing.contracts.save": "Guardar",
    "billing.contracts.saving": "A guardar...",
    "billing.contracts.cancel": "Cancelar",
    "billing.contracts.deleteConfirmTitle": "Eliminar contrato",
    "billing.contracts.deleteConfirmMessage": "Eliminar o contrato {id}? Esta ação não pode ser revertida.",
    "billing.contracts.deleteConfirmLabel": "Eliminar",
    "billing.contracts.cancelLabel": "Cancelar",
    "billing.contracts.contractHasEnginesError": "Este contrato tem engines associadas e não pode ser eliminado.",
    "billing.contracts.contractDeleteBlockedError": "Eliminação bloqueada: remova a associação das engines antes de eliminar o contrato.",
    "billing.contracts.invalidTransitionError": "Transição de estado não permitida para este contrato.",
    "billing.contracts.endDateBeforeStartDateError": "A data de fim não pode ser anterior à data de início.",
    "billing.contracts.validationRequiredFields": "Preencha todos os campos obrigatórios.",
    "billing.contracts.detailsTitle": "Detalhes do contrato",
    "billing.contracts.detailsSubtitle": "Engines associadas e histórico de faturação",
    "billing.contracts.enginesSectionTitle": "Engines associadas",
    "billing.contracts.noEnginesLinked": "Nenhuma engine associada a este contrato.",
    "billing.contracts.engineNodeId": "Engine",
    "billing.contracts.engineStatus": "Estado",
    "billing.contracts.engineBillingStatus": "Faturação",
    "billing.contracts.periodsSectionTitle": "Histórico de faturação",
    "billing.contracts.periodsTruncated": "Histórico parcial: a leitura de períodos está limitada a 1000 registos. Períodos mais antigos podem não aparecer.",
    "billing.contracts.noPeriodsRecorded": "Nenhum período de faturação registado.",
    "billing.contracts.periodRange": "Período",
    "billing.contracts.periodCpeDays": "CPE-dias",
    "billing.contracts.periodDueDate": "Vencimento",
    "billing.contracts.periodAmount": "Valor",
    "billing.contracts.periodStatus": "Estado",
    "billing.contracts.periodStatusOpen": "Aberto",
    "billing.contracts.periodStatusPaid": "Pago",
    "billing.contracts.registerPaymentAction": "Registar pagamento",
    "billing.contracts.paymentRestoredSuccess": "Pagamento registado e contrato restaurado para ativo.",
    "billing.contracts.paymentRecordedNotRestored": "Pagamento registado. A reativação dos assinantes não foi aplicada.",
    "billing.contracts.paymentPeriodNotUpdated": "Pagamento registado, mas o período de faturação não foi atualizado. Verifique o histórico antes de repetir a operação.",
    "billing.contracts.periodStatusUnknown": "Desconhecido",
    "billing.contracts.showingCount": "A mostrar {current} de {total} contratos",
    "billing.contracts.loadMore": "Carregar mais",
    "billing.contracts.dropdownTruncated": "… e mais {hidden} não apresentados. Refine a pesquisa para localizar os restantes.",
    "billing.contracts.closeDetails": "Fechar detalhes",
    "billing.contracts.genericError": "Não foi possível concluir a operação. Tente novamente.",
    "billing.plans.subtitle": "Planos de faturação e escalões de preço por consumo",
    "billing.plans.newPlan": "Novo plano",
    "billing.plans.colName": "Nome",
    "billing.plans.colMetric": "Métrica",
    "billing.plans.colPeriodType": "Periodicidade",
    "billing.plans.colTiersSummary": "Escalões de preço",
    "billing.plans.colDefault": "Predefinido",
    "billing.plans.colCreatedAt": "Criado em",
    "billing.plans.colActions": "Ações",
    "billing.plans.defaultBadge": "Predefinido",
    "billing.plans.emptyList": "Nenhum plano de faturação registado.",
    "billing.plans.loading": "A carregar planos...",
    "billing.plans.actionEdit": "Editar",
    "billing.plans.actionDelete": "Eliminar",
    "billing.plans.dialogCreateTitle": "Novo plano",
    "billing.plans.dialogEditTitle": "Editar plano",
    "billing.plans.dialogDescription": "Defina a métrica e os escalões de preço do plano.",
    "billing.plans.fieldName": "Nome",
    "billing.plans.fieldNamePlaceholder": "Ex.: Plano 300 Mbps",
    "billing.plans.fieldMetric": "Métrica de faturação",
    "billing.plans.fieldDefault": "Definir como predefinido",
    "billing.plans.fieldDefaultHelp": "O plano predefinido é usado quando não é indicado nenhum plano.",
    "billing.plans.tiersSectionTitle": "Escalões de preço",
    "billing.plans.tiersSectionHelp": "Indique escalões crescentes. O último escalão pode ficar sem limite (ilimitado).",
    "billing.plans.tierColIndex": "#",
    "billing.plans.tierColUpTo": "Até",
    "billing.plans.tierColPrice": "Preço",
    "billing.plans.tierColActions": "Ações",
    "billing.plans.tierUpToUnlimited": "Ilimitado",
    "billing.plans.tierUpToPlaceholder": "Vazio = ilimitado",
    "billing.plans.tierPricePlaceholder": "0,00",
    "billing.plans.addTier": "Adicionar escalão",
    "billing.plans.removeTier": "Remover escalão",
    "billing.plans.save": "Guardar",
    "billing.plans.saving": "A guardar...",
    "billing.plans.cancel": "Cancelar",
    "billing.plans.deleteConfirmTitle": "Eliminar plano",
    "billing.plans.deleteConfirmMessage": "Eliminar o plano selecionado? Esta ação não pode ser revertida.",
    "billing.plans.deleteConfirmLabel": "Eliminar",
    "billing.plans.cancelLabel": "Cancelar",
    "billing.plans.planHasContractsError": "Este plano está associado a contratos e não pode ser eliminado.",
    "billing.plans.validationNameRequired": "Indique o nome do plano.",
    "billing.plans.validationMinTiers": "Indique pelo menos um escalão de preço.",
    "billing.plans.validationMaxTiers": "Uma tabela de preços aceita no máximo 20 escalões.",
    "billing.plans.validationNullOnlyOnLastTier": "Apenas o último escalão pode ficar sem limite.",
    "billing.plans.validationPositiveIntegerUpTo": "O limite do escalão deve ser um número inteiro maior ou igual a zero.",
    "billing.plans.validationStrictlyAscending": "Os escalões devem ser estritamente crescentes.",
    "billing.plans.validationPricePositive": "O preço do escalão deve ser maior ou igual a zero.",
    "billing.plans.validationPriceFormat": "Indique um decimal simples (ex.: 12.50), sem expoente nem notação hexadecimal.",
    "billing.plans.validationPriceCeiling": "O preço do escalão deve ser no máximo 99999999.99.",
    "billing.plans.yes": "Sim",
    "billing.plans.fieldMetricPlaceholder": "cpe-days",
    "billing.plans.tierSummaryUpTo": "até {up_to} — {price}",
    "billing.plans.tierSummaryUnlimited": "ilimitado — {price}",
    "billing.plans.genericError": "Não foi possível concluir a operação. Tente novamente.",
    "billing.errValidation": "Dados inválidos:",
    "billing.errDuplicateDocument": "Já existe um registo com este documento ou vínculo.",
    "billing.errClientHasContracts": "Este cliente tem contratos e não pode ser eliminado.",
    "billing.errPlanHasContracts": "Este plano está associado a contratos e não pode ser eliminado.",
    "billing.errContractHasEngines": "Este contrato tem engines associadas e não pode ser eliminado ou bloqueado.",
    "billing.errInvalidTransition": "Transição de estado não permitida para este contrato.",
    "billing.errContractNotFound": "Contrato não encontrado.",
    "billing.errContractNotActive": "O contrato não está ativo para esta operação.",
    "billing.errContractDeleteBlocked": "Este contrato tem faturação associada (registos de faturação) e não pode ser eliminado.",
    "billing.errorLoading": "Não foi possível carregar os dados.",
    "billing.retry": "Tentar novamente",
    "billing.errGeneric": "Não foi possível concluir a operação. Tente novamente.",
    "billing.commonValidationError": "valor inválido"
  },
  "es": {
    "common.logout": "Cerrar sesión",
    "common.language": "Idioma",
    "common.backToCore": "Volver al Core",
    "common.toggleMenu": "Alternar menú",
    "common.toggleSidebar": "Alternar barra lateral",
    "common.breadcrumb": "Navegación estructural",
    "common.themeSwitchDark": "Cambiar a modo oscuro",
    "common.themeSwitchLight": "Cambiar a modo claro",
    "common.save": "Guardar",
    "common.saving": "Guardando...",
    "common.cancel": "Cancelar",
    "common.close": "Cerrar",
    "common.delete": "Eliminar",
    "createEngine.apiTokenPendingHint": "El API Token del nodo se mostrará cuando concluya la generación del token de instalación.",
    "common.edit": "Editar",
    "common.create": "Crear",
    "common.loading": "Cargando...",
    "common.error": "Error",
    "common.search": "Buscar",
    "common.actions": "Acciones",
    "common.status": "Estado",
    "common.name": "Nombre",
    "common.description": "Descripción",
    "common.active": "Activo",
    "common.inactive": "Inactivo",
    "common.tryAgain": "Intentar de nuevo",
    "common.back": "Volver",
    "common.version": "Versión",
    "common.updatedAt": "Actualizado el",
    "common.noData": "Sin datos disponibles",
    "common.noResults": "Sin resultados",
    "common.saveError": "Error al guardar.",
    "common.deleteConfirm": "Esta acción no se puede deshacer.",
    "common.confirm": "Confirmar",
    "common.continue": "Continuar",
    "common.region": "Región",
    "common.script": "Script",
    "common.value": "Valor",
    "common.email": "Email",
    "common.type": "Tipo",
    "common.download": "Descargar",
    "common.previous": "Anterior",
    "common.next": "Siguiente",
    "common.page": "Página",
    "common.showing": "Mostrando",
    "common.of": "de",
    "common.suspend": "Suspender",
    "common.activate": "Activar",
    "common.enabled": "Habilitado",
    "common.disabled": "Deshabilitado",
    "common.all": "Todos",
    "common.filter": "Filtrar",
    "common.copy": "Copiar",
    "common.copied": "Copiado",
    "common.nameRequired": "El nombre es obligatorio.",
    "common.suspended": "Suspendido",
    "common.success": "Éxito",
    "common.warning": "Aviso",
    "common.danger": "Peligro",
    "common.info": "Información",
    "common.accessDenied": "Acceso denegado",

    "syncstatus.title": "Estado de Sincronización",
    "syncstatus.subtitle": "Observabilidad del ciclo de sincronización de ingeniería entre Engine y Core",
    "syncstatus.colEngine": "Engine",
    "syncstatus.colLastCycle": "Último Ciclo",
    "syncstatus.colVersion": "Versión",
    "syncstatus.colStatus": "Estado",
    "syncstatus.colPushed": "Enviados",
    "syncstatus.colDeleted": "Eliminados",
    "syncstatus.colErrors": "Errores",
    "syncstatus.colDuration": "Duración",
    "syncstatus.neverSynced": "Nunca se sincronizó",
    "syncstatus.historyTitle": "Historial de Sincronización",
    "syncstatus.lastError": "Último Error",
    "syncstatus.noHistory": "No hay historial disponible",
    "syncstatus.statusOk": "OK",
    "syncstatus.statusPushError": "Error de Push",
    "syncstatus.statusError": "Error",
    "syncstatus.loadingHistory": "Cargando historial...",
    "syncstatus.relativeNow": "Ahora mismo",
    "syncstatus.relativeSeconds": "hace {n}s",
    "syncstatus.relativeMinutes": "hace {n}min",
    "syncstatus.relativeHours": "hace {n}h",
    "syncstatus.relativeDays": "hace {n}d",
    "syncstatus.pushedDel": "Enviados: {pushed} / Eliminados: {deleted}",
    "syncstatus.errorsCount": "Errores: {n}",
    "syncstatus.noErrorDetail": "Sin detalle",

    "dynvarsoverride.title": "Variables Configurables",
    "dynvarsoverride.description": "Personalice los valores de las variables globales para este Engine Node",
    "dynvarsoverride.tableKey": "Clave",
    "dynvarsoverride.tableName": "Nombre",
    "dynvarsoverride.tableGlobal": "Valor Global",
    "dynvarsoverride.tableLocal": "Valor Local",
    "dynvarsoverride.tableStatus": "Estado",
    "dynvarsoverride.tableUsage": "Uso",
    "dynvarsoverride.statusDefault": "Usando el valor predeterminado del Core",
    "dynvarsoverride.statusOverridden": "Sobrescrito localmente",
    "dynvarsoverride.empty": "Ninguna variable configurable por el Engine.",
    "dynvarsoverride.emptyDesc": "El Core no definió variables que puedan modificarse localmente.",
    "dynvarsoverride.editTitle": "Editar Valor Local",
    "dynvarsoverride.editDesc": "Modifique el valor de esta variable para este Engine.",
    "dynvarsoverride.formValue": "Valor",
    "dynvarsoverride.removeOverride": "Quitar sobrescritura",
    "dynvarsoverride.removeConfirm": "Restaurar valor global de",
    "dynvarsoverride.save": "Guardar Cambio",
    "dynvarsoverride.error": "Valor inválido para el tipo de dato.",
    "dynvarsoverride.statusBlockedByCore": "Bloqueada por el Core",
    "dynvarsoverride.statusOrphaned": "Variable eliminada en el Core",
    "dynvarsoverride.orphanedHelp": "Esta variable ya no existe en el Core. La sobrescritura local está inactiva — elimínela.",
    "dynvarsoverride.deleteOrphanedOverride": "Quitar sobrescritura",
    "dynvarsoverride.globalRef": "Predeterminado del Core:",
    "dynvarsoverride.reapplyEnqueued": "Guardado. Reaplicación programada para los dispositivos afectados.",
    "dynvarsoverride.reapplyDeduped": "Reaplicación ya en curso — valor guardado.",
    "dynvarsoverride.reapplyCooldown": "Reaplicación en espera (cooldown) — valor guardado.",
    "dynvarsoverride.reapplyFailed": "Valor guardado, pero fallo al reaplicar en los dispositivos afectados.",
    "route.dynVarOverrides": "Variables Configurables",
    "sidebar.opsDynVarOverrides": "Variables Configurables",
    "route.nocDashboard": "Panel NOC",
    "route.engineRegistry": "Registro de Engines",
    "route.engVirtualParams": "Parámetros Virtuales",
    "route.engProvisions": "Provisiones",
    "route.engPresets": "Presets",
    "route.engDynamicVars": "Variables Dinámicas",
    "route.engCwmpConfigs": "Configuraciones CWMP",
    "route.engVendorMappings": "Mapeos de Vendor",
    "route.engCpeModels": "Modelos CPE",
    "route.engFirmware": "Firmware",
    "route.engFiles": "Archivos",
    "route.engTr369Controllers": "Controladores TR-369",
    "route.engOlt": "OLT",
    "route.adminUsers": "Usuarios",
    "route.adminSettings": "Configuración",
    "route.adminBackups": "Copias de Seguridad",
    "route.adminBilling": "Facturación",
    "route.adminBillingPlans": "Planes de Facturación",
    "route.adminBillingClients": "Clientes",
    "route.adminBillingContracts": "Contratos",
    "route.defaultTitle": "RC ACS",
    "route.dashboard": "Panel",
    "route.devices": "Dispositivos",
    "route.oltManagement": "Gestión OLT",
    "route.reports": "Reportes",
    "route.operations": "Operaciones",
    "route.faults": "Fallas",
    "route.syncDrift": "Desvío de Sincronización",
    "route.operators": "Operadores",
    "route.engineDefault": "Gestión de la Engine",
    "route.profile": "Perfil",
    "route.olts": "OLTs",

    "sidebar.dashboard": "Panel",
    "sidebar.engineRegistry": "Registro de Engines",
    "sidebar.engineering": "Ingeniería",
    "sidebar.engTr069": "TR-069 (GenieACS)",
    "sidebar.engTr369": "TR-369 (USP)",
    "sidebar.engDevices": "Dispositivos",
    "sidebar.engOlt": "OLT",
    "sidebar.engOltManagement": "Gestión OLT",
    "sidebar.engDynamicVars": "Variables Dinámicas",
    "sidebar.engSyncStatus": "Estado de Sincronización",
    "sidebar.engVirtualParams": "Parámetros Virtuales",
    "sidebar.engProvisions": "Provisiones",
    "sidebar.engPresets": "Presets",
    "sidebar.engCwmpConfigs": "Configuraciones CWMP",
    "sidebar.engFiles": "Archivos",
    "sidebar.engTr369Controllers": "Controladores",
    "sidebar.engVendorMappings": "Mapeos de Vendor",
    "sidebar.engCpeModels": "Modelos CPE",
    "sidebar.engFirmware": "Firmware",
    "sidebar.admin": "Administración",
    "sidebar.adminUsers": "Usuarios",
    "sidebar.adminSettings": "Configuración",
    "sidebar.adminBackups": "Copias de Seguridad",
    "sidebar.adminBilling": "Facturación",
    "sidebar.adminBillingPlans": "Planes",
    "sidebar.adminBillingClients": "Clientes",
    "sidebar.adminBillingContracts": "Contratos",
    "sidebar.devices": "Dispositivos",
    "sidebar.olts": "OLTs",
    "sidebar.opsEngRemote": "Ing. Remota",
    "sidebar.opsFiles": "Arquivos",
    "sidebar.opsHealth": "Saúde",
    "sidebar.opsHostServices": "Servicios de Host",
    "sidebar.opsOnboarding": "Onboarding",
    "sidebar.opsPresets": "Presets",
    "sidebar.opsProvisions": "Provisões",
    "sidebar.opsSettings": "Configuración",
    "sidebar.opsStatus": "Estado",
    "sidebar.opsOperators": "Operadores",
    "sidebar.opsVirtualParams": "Parámetros Virtuales",
    "sidebar.reports": "Reportes",
    "sidebar.operations": "Operaciones",
    "sidebar.faults": "Fallas",
    "sidebar.syncDrift": "Desvío de Sincronización",
    "login.coreTitle": "Inicie sesión para acceder a la Plataforma de Gestión Core",
    "login.coreSubtitle": "Inicie sesión con su cuenta RC Control para acceder al panel",
    "login.loginButton": "Entrar con RC Control",
    "login.redirectNotice": "Será redirigido al servicio de autenticación RC Control.",
    "login.acs": "RC ACS",
    "login.engineTitle": "Acceda al panel de gestión local",
    "login.engineNode": "Engine Node",
    "login.localLogin": "Inicio de sesión local",
    "login.localLoginPasskey": "Inicio de sesión local (Passkey)",
    "login.backToCore": "Volver al Core",
    "login.authenticating": "Autenticando...",
    "login.verifying": "Verificando...",
    "login.ispTab": "Operador ISP",
    "login.passkeyTab": "Passkey",
    "login.username": "Usuario",
    "login.password": "Contraseña",
    "login.ispLoginButton": "Entrar",
    "login.ispLoginError": "Usuario o contraseña inválidos.",
    "login.ispLoginFailed": "Error al autenticar. Inténtelo de nuevo.",
    "login.passkeyFailed": "No se pudo autenticar con la passkey. Inténtelo de nuevo.",

    "unauthorized.title": "Acceso denegado",
    "unauthorized.message": "No tiene permiso para acceder a esta página.",
    "unauthorized.backToDashboard": "Volver al panel",
    "unauthorized.signOut": "Cerrar sesión",
    "login.passkeyCancelled": "Autenticación con passkey cancelada.",
    "login.passkeyUnsupported": "Este navegador no admite passkeys. Use usuario y contraseña.",
    "login.sessionExpired": "Sesión expirada. Inicie sesión de nuevo.",
    "login.ssoInvalidToken": "Token de acceso inválido o expirado.",
    "login.localTab": "Usuario Local",
    "login.localUsername": "Usuario",
    "login.localPassword": "Contraseña",
    "login.localLoginButton": "Entrar",
    "login.localLoginError": "Usuario o contraseña inválidos.",
    "login.invalidCredentials": "Usuario o contraseña inválidos.",
    "login.rateLimited": "Demasiados intentos de inicio de sesión. Inténtelo de nuevo en unos minutos.",
    "login.coreOffline": "Core sin conexión: funcionalidad limitada. No se puede renovar la sesión SSO.",
    "login.serviceUnavailable": "Servicio no disponible. Inténtelo de nuevo más tarde.",
    "login.connectionError": "Error de conexión. Compruebe su red e inténtelo de nuevo.",
    "login.loginUnavailableNode": "Inicio de sesión no disponible en este nodo.",
    "login.directLoginUnavailable": "El inicio de sesión directo no está disponible en este nodo. Acceda mediante SSO del Core.",
    "login.loginWithPasskey": "Iniciar sesión con Passkey",
    "login.or": "o",
    "login.passkeyTooltip": "Autenticación con llave de seguridad o biometría",

    "isp.role.isp_admin": "Administrador ISP",
    "isp.role.isp_operator": "Operador ISP",
    "isp.role.isp_technician": "Técnico",

    "lang.ptBR": "Português (Brasil)",
    "lang.ptPT": "Português (Portugal)",
    "lang.es": "Español",
    "lang.en": "English",
    "noc.title": "Panel NOC",
    "engineRegistry.title": "Registro de Engines",
    "engineRegistry.description": "Gestione todos los Engine Nodes entre regiones y clientes",
    "engineRegistry.newEngine": "Nuevo Engine",
    "engineRegistry.searchPlaceholder": "Buscar engines...",
    "engineRegistry.allStatuses": "Todos los Estados",
    "engineRegistry.statusOnline": "Online",
    "engineRegistry.statusOffline": "Offline",
    "engineRegistry.statusDegraded": "Degradado",
    "engineRegistry.statusSuspended": "Suspendido",
    "engineRegistry.cloud": "Nube",
    "engineRegistry.loading": "Cargando engine nodes...",
    "engineRegistry.empty": "Ningún engine node encontrado",
    "engineRegistry.createFirst": "Crear su primer Engine Node",
    "engineRegistry.tableName": "Nombre",
    "engineRegistry.tableClient": "Cliente",
    "engineRegistry.tableRegion": "Región",
    "engineRegistry.tableHealth": "Salud",
    "engineRegistry.tableCpes": "CPEs",
    "engineRegistry.tableTier": "Tier",
    "engineRegistry.tableHeartbeat": "Último Heartbeat",
    "engineRegistry.never": "Nunca",
    "engineRegistry.justNow": "Ahora mismo",
    "engineRegistry.secondsAgo": "s atrás",
    "engineRegistry.minutesAgo": "min atrás",
    "engineRegistry.hoursAgo": "h atrás",
    "engineRegistry.daysAgo": "d atrás",
    "engineRegistry.edit": "Editar",
    "engineRegistry.activate": "Activar",
    "engineRegistry.suspend": "Suspender",
    "engineRegistry.delete": "Eliminar",
    "engineRegistry.deleteTitle": "Eliminar Engine Node",
    "engineRegistry.deleteMessage": "¿Está seguro que desea eliminar",
    "registry.errorDelete": "Error al eliminar el nodo. Verifique las dependencias e inténtelo de nuevo.",
    "registry.errorConflict": "No se pudo eliminar el nodo: existen dependencias que bloquean la eliminación.",
    "registry.errorForbidden": "No tiene permisos de administrador para eliminar este nodo.",
    "registry.nameMismatch": "El nombre escrito no coincide con el nombre del nodo.",
    "engineDetail.notFound": "Engine Node No Encontrado",
    "engineDetail.notFoundMsg": "El node con ID especificado no existe.",
    "engineDetail.backToRegistry": "Volver al Registro",
    "engineDetail.registryBreadcrumb": "Registro de Engines",
    "engineDetail.healthIndex": "Índice de Salud",
    "engineDetail.activeCpes": "CPEs Activos",
    "engineDetail.licenseTier": "Tier de Licencia",
    "engineDetail.maxCpes": "Máx. CPEs",
    "engineDetail.heartbeat": "Heartbeat",
    "engineDetail.loading": "Cargando engine node...",
    "engineDetail.backups": "Copias de Seguridad",
    "engineDetail.remoteLogs": "Logs Remotos",
    "engineDetail.healthHistory": "Histórico de Salud",
    "vp.title": "Parámetros Virtuales",
    "vp.description": "Gestione scripts de parámetros virtuales para ingeniería de CPEs",
    "vp.newParam": "Nuevo Parámetro",
    "vp.loading": "Cargando parámetros virtuales...",
    "vp.error": "Error al cargar parámetros virtuales.",
    "vp.empty": "Ningún parámetro virtual encontrado.",
    "vp.createFirst": "Crear su primer Parámetro Virtual",
    "vp.tableName": "Nombre",
    "vp.tableDescription": "Descripción",
    "vp.tableScript": "Script",
    "vp.tableVersion": "Versión",
    "vp.tableUpdatedAt": "Actualizado el",
    "vp.tableActions": "Acciones",
    "vp.editTitle": "Editar Parâmetro Virtual",
    "vp.newTitle": "Nuevo Parámetro Virtual",
    "vp.editDesc": "Edite los campos del parámetro virtual.",
    "vp.newDesc": "Rellene los campos para crear un nuevo parámetro virtual.",
    "vp.formName": "Nombre *",
    "vp.formNamePlaceholder": "Ej: SNR Medio",
    "vp.nameRequired": "El nombre es obligatorio.",
    "vp.formDesc": "Descripción",
    "vp.formDescPlaceholder": "Ej: Calcula la media del SNR en las últimas 24h",
    "vp.formScript": "Script (JavaScript)",
    "vp.formScriptPlaceholder": "// Escriba el script JavaScript del parámetro virtual",
    "vp.validateScript": "Validar Script",
    "vp.scriptValid": "Script válido",
    "vp.scriptInvalid": "Script inválido",
    "vp.deleteTitle": "Eliminar Parámetro Virtual",
    "vp.deleteMessage": "¿Está seguro que desea eliminar",
    "vp.versionHistory": "Histórico de Versiones",
    "vp.versionHistoryDesc": "Versiones anteriores del parámetro virtual.",
    "vp.noVersions": "No se encontraron versiones anteriores.",
    "vp.versionLabel": "Versión",
    "vp.reason": "Motivo",
    "provisions.title": "Provisiones",
    "provisions.description": "Gestione scripts de provisión para configuración automática de CPEs",
    "provisions.newProvision": "Nueva Provisión",
    "provisions.loading": "Cargando provisiones...",
    "provisions.error": "Error al cargar provisiones.",
    "provisions.empty": "Ninguna provisión encontrada.",
    "provisions.createFirst": "Crear su primera Provisión",
    "provisions.tableName": "Nombre",
    "provisions.tableScript": "Script",
    "provisions.editTitle": "Editar Provisão",
    "provisions.newTitle": "Nueva Provisión",
    "provisions.editDesc": "Edite los campos de la provisión.",
    "provisions.newDesc": "Rellene los campos para crear una nueva provisión.",
    "provisions.formName": "Nombre *",
    "provisions.formNamePlaceholder": "Ej: Configuración Inicial PPPoE",
    "provisions.formScript": "Script (JavaScript)",
    "provisions.formScriptPlaceholder": "// Escriba el script JavaScript de provisión",
    "provisions.validate": "Validar",
    "provisions.validScript": "Script válido",
    "provisions.invalidScript": "Script inválido",
    "provisions.deleteTitle": "Eliminar Provisión",
    "provisions.deleteMessage": "¿Está seguro que desea eliminar",
    "provisions.versionHistoryDesc": "Versiones anteriores de la provisión.",
    "presets.title": "Presets",
    "presets.description": "Gestione presets de configuración para aprovisionamiento de CPEs",
    "presets.newPreset": "Nuevo Preset",
    "presets.loading": "Cargando presets...",
    "presets.error": "Error al cargar presets.",
    "presets.empty": "Ningún preset encontrado.",
    "presets.createFirst": "Crear su primer Preset",
    "presets.tableName": "Nombre",
    "presets.tableConfig": "Config",
    "presets.editTitle": "Editar Preset",
    "presets.newTitle": "Nuevo Preset",
    "presets.editDesc": "Edite los campos del preset.",
    "presets.newDesc": "Rellene los campos para crear un nuevo preset.",
    "presets.formName": "Nombre *",
    "presets.formNamePlaceholder": "Ej: Configuración Predeterminada WiFi",
    "presets.formConfig": "Config (JSON)",
    "presets.channel": "Canal",
    "presets.weight": "Peso",
    "presets.schedule": "Programación",
    "presets.scheduleHelp": "Expresión cron (ej: 0 2 * * *)",
    "presets.events": "Eventos",
    "presets.precondition": "Precondición",
    "presets.preconditionHelp": "Expresión booleana evaluada antes de aplicar el preset. Ej.: Tags.Provisioned. Soporta los operadores GenieACS (AND, OR, NOT, IS NULL). Los placeholders {{var}} no se admiten aquí.",
    "presets.provisions": "Provisions",
    "presets.addProvision": "Añadir Provision",
    "presets.provisionArgs": "Argumentos (JSON)",
    "presets.jsonInvalid": "JSON inválido. Corrija la sintaxis.",
    "presets.deleteTitle": "Eliminar Preset",
    "presets.deleteMessage": "¿Está seguro que desea eliminar",
    "presets.versionHistoryDesc": "Versiones anteriores del preset.",
    "dynvars.title": "Variables Dinámicas",
    "dynvars.description": "Gestione variables dinámicas utilizadas en los scripts de ingeniería",
    "dynvars.newVar": "Nueva Variable",
    "dynvars.loading": "Cargando variables dinámicas...",
    "dynvars.error": "Error al cargar variables dinámicas.",
    "dynvars.empty": "Ninguna variable dinámica encontrada.",
    "dynvars.createFirst": "Crear su primera Variable Dinámica",
    "dynvars.tableKey": "Clave",
    "dynvars.tableName": "Nombre",
    "dynvars.tableDataType": "Tipo de Dato",
    "dynvars.tableDefault": "Valor Predeterminado",
    "dynvars.editTitle": "Editar Variable Dinámica",
    "dynvars.newTitle": "Nueva Variable Dinámica",
    "dynvars.editDesc": "Edite los campos de la variable dinámica.",
    "dynvars.newDesc": "Rellene los campos para crear una nueva variable dinámica.",
    "dynvars.formKey": "Clave *",
    "dynvars.formKeyPlaceholder": "ej: cpe_user_web",
    "dynvars.formKeyHelper": "Identificador usado dentro de los scripts de provisión y VP como {{clave}}. Sincronizado con todos los Engines (solo minúsculas y _).",
    "dynvars.keyRequired": "La clave es obligatoria.",
    "dynvars.keyPatternError": "La clave debe comenzar con letra minúscula y contener solo letras minúsculas, números y guiones bajos (_).",
    "dynvars.previewScript": "Provisiones y Parámetros Virtuales usarán:",
    "dynvars.formName": "Nombre *",
    "dynvars.formNamePlaceholder": "Ej: Usuario Web CPE",
    "dynvars.formNameHelper": "Nombre descriptivo amigable mostrado solo en el listado del panel. Nunca aparece en el código de los scripts.",
    "dynvars.formDescHelper": "Nota interna para el equipo sobre el propósito o alcance de esta variable. No se envía a los scripts.",
    "dynvars.formDescPlaceholder": "Ej: Credencial de acceso para panel web de ONTs Huawei y ZTE",
    "dynvars.formSourceType": "Origen",
    "dynvars.formSourceTypeHelper": "De dónde obtiene el Engine el valor de la variable en el momento del push a GenieACS.",
    "dynvars.sourceHintStaticTitle": "Valor Fijo Estático",
    "dynvars.sourceHintStatic": "Valor fijo. El campo Valor Predeterminado es el valor que se usará en los scripts. Recomendado para la mayoría de los casos (credenciales, servidor NTP, zona horaria).",
    "dynvars.sourceHintCwmpTitle": "Aviso de Soporte CWMP",
    "dynvars.sourceHintCwmp": "Resolución automática mediante configuración CWMP aún no implementada en el Engine — el sistema usará el Valor Predeterminado como fallback.",
    "dynvars.sourceHintSystemTitle": "Configuración del Sistema",
    "dynvars.sourceHintSystem": "Lee una configuración del sistema Engine. Formato esperado: setting:CLAVE (ej.: setting:admin.smtp).",
    "dynvars.sourceHintEnvTitle": "Restricción de Seguridad de Entorno",
    "dynvars.sourceHintEnv": "Lee variable de entorno del host del Engine. Por seguridad, debe comenzar con DYNVAR_ (o ser INFORM_INTERVAL). Nombres con PASSWORD, SECRET, TOKEN, KEY, URI o URL están bloqueados.",
    "dynvars.formSourceRef": "Referencia de Origen",
    "dynvars.formSourceRefHelper": "Clave o identificador del origen externo a ser consultado por el Engine.",
    "dynvars.sourceRefPlaceholderCwmp": "ej: cwmp:Device.ManagementServer.URL",
    "dynvars.sourceRefPlaceholderSetting": "ej: setting:admin.smtp",
    "dynvars.sourceRefPlaceholderEnv": "ej: env:DYNVAR_NTP_SERVER",
    "dynvars.formDataType": "Tipo de Dato",
    "dynvars.formDataTypeHelper": "Define cómo se valida el valor antes del envío (fail-closed: valor inválido para el tipo impide el push de la provisión).",
    "dynvars.dataTypeHintString": "Texto: inyectado como string literal JavaScript seguro entre comillas.",
    "dynvars.dataTypeHintBoolean": "Booleano: acepta solo true o false.",
    "dynvars.dataTypeHintInt": "Entero: número entero sin decimales (ej.: 600).",
    "dynvars.dataTypeHintJson": "JSON: objeto o array JSON sintácticamente válido (ej.: {\"opcion\": 1}).",
    "dynvars.dataTypeHintEnum": "Enum: restringido exclusivamente a los valores definidos en la lista de opciones.",
    "dynvars.formDefault": "Valor Predeterminado",
    "dynvars.formDefaultHelper": "Valor usado en los scripts (si es Estático) o fallback si el origen falla. Si está vacío y sin valor de origen, la provisión se cancela.",
    "dynvars.placeholderDefaultInt": "ej: 600",
    "dynvars.formEnumOptions": "Opciones del Enum",
    "dynvars.formEnumOptionsPlaceholder": "ej: bridge, router, pppoe",
    "dynvars.formEnumOptionsHint": "Valores separados por coma.",
    "dynvars.formEnumOptionsHelper": "Lista de valores aceptados separados por coma. Cualquier valor fuera de esta lista es rechazado.",
    "dynvars.formActive": "Activo",
    "dynvars.formActiveHelper": "Variables inactivas no se resuelven. Provisiones que dependen de esta variable se cancelan durante el push.",
    "dynvars.formDefaultPlaceholder": "Valor predeterminado (opcional)",
    "dynvars.selectDefault": "Seleccione el predeterminado",
    "dynvars.emptyDesc": "Cree variables parametrizadas para inyectar valores dinámicos en los scripts de provisión y parámetros virtuales de GenieACS sin exponer credenciales fijas.",
    "dynvars.sourceType.static": "Estático",
    "dynvars.sourceType.cwmp_config": "Config CWMP",
    "dynvars.sourceType.system_setting": "Configuración del Sistema",
    "dynvars.sourceType.env_var": "Variable de Entorno",
    "dynvars.dataType.string": "Texto",
    "dynvars.dataType.boolean": "Booleano",
    "dynvars.dataType.int": "Entero",
    "dynvars.dataType.json": "JSON",
    "dynvars.dataType.enum": "Enum",
    "dynvars.deleteTitle": "Eliminar Variable Dinámica",
    "dynvars.deleteMessage": "¿Está seguro que desea eliminar",
    "dynvars.usageColumn": "Uso",
    "dynvars.usageFormat": "Usada en: {provisions} provisions, {vps} VPs, {presets} presets",
    "dynvars.scope": "Ámbito",
    "dynvars.scopeGlobal": "Global",
    "dynvars.scopeConfigurable": "Configurable",
    "dynvars.scopeConfigurableEngine": "Configurable por Engine",
    "dynvars.scopeGlobalHelper": "Global: valor fijo definido por el Core; los Engines solo aplican",
    "dynvars.scopeConfigurableHelper": "Configurable por Engine: el Core envía el valor predeterminado; cada Engine puede definir su propio valor en su UI (aplicado a los scripts locales).",
    "dynvars.scopeToggleWarning": "Atención: los overrides existentes en los Engines quedarán inactivos (no se eliminan)",

    "tr369.comingSoon": "TR-369 próximamente",
    "tr369.description": "La gestión de controladores TR-369 (USP/OpenUSP) se habilitará cuando la implementación del controlador Go esté completa.",
    "vendorMappings.title": "Mapeos de Vendor",
    "vendorMappings.description": "Gestione mapeos de fabricantes y modelos para TR-069",
    "vendorMappings.newMapping": "Nuevo Mapeo",
    "vendorMappings.loading": "Cargando mapeos...",
    "vendorMappings.error": "Error al cargar mapeos.",
    "vendorMappings.empty": "Ningún mapeo encontrado.",
    "vendorMappings.createFirst": "Crear su primer Mapeo",
    "vendorMappings.tableVendor": "Fabricante",
    "vendorMappings.tableModel": "Modelo",
    "vendorMappings.tablePath": "Path",
    "vendorMappings.editTitle": "Editar Mapeo",
    "vendorMappings.newTitle": "Nuevo Mapeo",
    "vendorMappings.editDesc": "Edite los campos del mapeo.",
    "vendorMappings.newDesc": "Rellene los campos para crear un nuevo mapeo vendor.",
    "vendorMappings.formVendor": "Fabricante *",
    "vendorMappings.formVendorPlaceholder": "Ej: Huawei",
    "vendorMappings.vendorRequired": "El fabricante es obligatorio.",
    "vendorMappings.formModel": "Modelo *",
    "vendorMappings.formModelPlaceholder": "Ej: HG8245H",
    "vendorMappings.modelRequired": "El modelo es obligatorio.",
    "vendorMappings.formPath": "TR-069 Path",
    "vendorMappings.formPathPlaceholder": "Ej: InternetGatewayDevice.LANDevice.1.WLANConfiguration.1",
    "vendorMappings.formDescPlaceholder": "Ej: Mapeo para ONTs Huawei GPON",
    "vendorMappings.deleteTitle": "Eliminar Mapeo",
    "vendorMappings.deleteMessage": "¿Está seguro que desea eliminar",
    "cpeModels.title": "Modelos CPE",
    "cpeModels.description": "Gestione modelos de equipos de clientes (CPE)",
    "cpeModels.newModel": "Nuevo Modelo",
    "cpeModels.loading": "Cargando modelos CPE...",
    "cpeModels.error": "Error al cargar modelos CPE.",
    "cpeModels.empty": "Ningún modelo CPE encontrado.",
    "cpeModels.createFirst": "Crear su primer Modelo CPE",
    "cpeModels.tableVendor": "Fabricante",
    "cpeModels.tableModel": "Modelo",
    "cpeModels.tableFwVersion": "Versión FW",
    "cpeModels.tableWifi": "WiFi",
    "cpeModels.tablePorts": "Puertos",
    "cpeModels.editTitle": "Editar Modelo CPE",
    "cpeModels.newTitle": "Nuevo Modelo CPE",
    "cpeModels.editDesc": "Edite los campos del modelo CPE.",
    "cpeModels.newDesc": "Rellene los campos para crear un nuevo modelo CPE.",
    "cpeModels.formFwVersion": "Versión Firmware",
    "cpeModels.formFwPlaceholder": "Ej: V3R017C00S100",
    "cpeModels.formWifi": "Bandas WiFi",
    "cpeModels.formWifiPlaceholder": "Ej: 2.4GHz, 5GHz",
    "cpeModels.formPorts": "Puertos",
    "cpeModels.formPortsPlaceholder": "Ej: 4x GE, 2x POTS, 1x USB",
    "cpeModels.formDescPlaceholder": "Ej: ONT GPON para clientes residenciales",
    "cpeModels.deleteTitle": "Eliminar Modelo CPE",
    "cpeModels.deleteMessage": "¿Está seguro que desea eliminar",
    "firmware.title": "Firmware",
    "firmware.description": "Gestione versiones de firmware y realice deploys en engines",
    "firmware.newFirmware": "Nuevo Firmware",
    "firmware.loading": "Cargando firmware...",
    "firmware.error": "Error al cargar firmware.",
    "firmware.empty": "Ningún firmware encontrado.",
    "firmware.createFirst": "Crear su primer Firmware",
    "firmware.tableName": "Nombre",
    "firmware.tableVersion": "Versión",
    "firmware.tableModel": "Modelo",
    "firmware.tableSize": "Tamaño",
    "firmware.editTitle": "Editar Firmware",
    "firmware.newTitle": "Nuevo Firmware",
    "firmware.editDesc": "Edite los campos del firmware.",
    "firmware.newDesc": "Rellene los campos para registrar un nuevo firmware.",
    "firmware.formVersion": "Versión *",
    "firmware.formVersionPlaceholder": "Ej: 3.2.1",
    "firmware.versionRequired": "La versión es obligatoria.",
    "firmware.formModel": "Modelo (ID)",
    "firmware.formModelPlaceholder": "Ej: 5",
    "firmware.formFile": "Archivo",
    "firmware.uploadFuture": "Subida de archivo via API disponible en versión futura.",
    "firmware.formDesc": "Descripción",
    "firmware.formDescPlaceholder": "Ej: Release notes de la versión",
    "firmware.deleteTitle": "Eliminar Firmware",
    "firmware.deleteMessage": "¿Está seguro que desea eliminar",
    "firmware.deployTitle": "Deploy de Firmware",
    "firmware.deployDesc": "Instalar en los engines seleccionados.",
    "firmware.deployEngines": "Engine UUIDs",
    "firmware.deployPlaceholder": "UUID1, UUID2, UUID3 (separados por coma)",
    "firmware.deploySeparator": "Separe múltiples UUIDs por coma.",
    "firmware.deploying": "Instalando...",
    "firmware.deploy": "Instalar",
    "firmware.deployError": "Error al instalar.",
    "firmware.deploySuccess": "Deploy iniciado con éxito!",
    "files.title": "Archivos",
    "files.description": "Gestione archivos de firmware, configuración y otros recursos",
    "files.newFile": "Nuevo Archivo",
    "files.loading": "Cargando archivos...",
    "files.error": "Error al cargar archivos.",
    "files.empty": "Ningún archivo encontrado.",
    "files.createFirst": "Crear su primer Archivo",
    "files.tableType": "Tipo",
    "files.tableSize": "Tamaño",
    "files.fileTypeImage": "Imagen",
    "files.fileTypeText": "Texto",
    "files.fileTypeBinary": "Binario",
    "files.editTitle": "Editar Archivo",
    "files.newTitle": "Nuevo Archivo",
    "files.editDesc": "Edite los metadatos del archivo.",
    "files.newDesc": "Rellene los campos para añadir un nuevo archivo.",
    "files.formNamePlaceholder": "Ej: fw_hg8245h_v3.2.1.bin",
    "files.formMime": "Descripción / Tipo MIME",
    "files.formMimePlaceholder": "Ej: application/octet-stream",
    "files.deleteTitle": "Eliminar Archivo",
    "files.deleteMessage": "¿Está seguro que desea eliminar",
    "hostServices.editInterface": "Editar interfaz",
    "hostServices.deleteInterface": "Eliminar interfaz",
    "hostServices.deleteInterfaceConfirm": "Confirma eliminación de la interfaz",
    "hostServices.deleteInterfaceWarning": "La interfaz será eliminada del kernel y de la configuración. Para VLANs, la interfaz se elimina del sistema. Para interfaces físicas, solo se elimina de la configuración.",
    "hostServices.interfaceName": "Nombre",
    "hostServices.ipv4Address": "Dirección IPv4",
    "hostServices.ipv6Address": "Dirección IPv6",
    "hostServices.subnet4": "Subnet IPv4",
    "hostServices.prefix6": "Prefijo IPv6",
    "hostServices.poolStart": "Inicio del pool",
    "hostServices.poolEnd": "Fin del pool",
    "hostServices.gateway": "Gateway",
    "hostServices.dnsServers": "Servidores DNS",
    "hostServices.leaseTime": "Tiempo de lease (s)",
    "hostServices.cidr.ipv4Cidr": "IPv4 CIDR",
    "hostServices.cidr.ipv6Cidr": "IPv6 CIDR",
    "hostServices.cidr.ipv4Placeholder": "ej: 10.50.0.1/24",
    "hostServices.cidr.ipv6Placeholder": "ej: 2001:db8::1/64",
    "hostServices.cidr.auto": "auto",
    "hostServices.cidr.manual": "manual",
    "hostServices.cidr.reset": "Restablecer a derivado",
    "hostServices.cidr.reserveFirst": "Reservar primeros",
    "hostServices.cidr.networkId": "Red",
    "hostServices.cidr.netmask": "Máscara",
    "hostServices.cidr.prefixLength": "Prefijo",
    "hostServices.cidr.broadcast": "Broadcast",
    "hostServices.cidr.usableRange": "Rango útil",
    "hostServices.cidr.totalHosts": "Hosts totales",
    "hostServices.cidr.locked": "bloqueado",
    "hostServices.cidr.unlocked": "editable",
    "hostServices.cidr.lock": "Bloquear gateway",
    "hostServices.cidr.unlock": "Desbloquear gateway",
    "hostServices.wizard.interfaces": "Interfaces CPE",
    "hostServices.wizard.subnets": "Subnet/Prefijo",
    "hostServices.wizard.step1": "Red & Pools CPE",
    "hostServices.wizard.step2": "TR-069 & Servicios",
    "hostServices.wizard.step3": "Preview & Commit",
    "hostServices.wizard.services": "TR-069 & Servicios",
    "hostServices.wizard.review": "Revisión",
    "hostServices.wizard.preview.kea4": "Kea DHCPv4",
    "hostServices.wizard.preview.kea6": "Kea DHCPv6",
    "hostServices.wizard.preview.unbound": "Unbound",
    "hostServices.wizard.preview.radvd": "radvd",
    "hostServices.wizard.noInterfacesSelected": "Seleccione al menos una interfaz para continuar.",
    "hostServices.wizard.overlapConflictV4": "Conflicto IPv4 entre {a} y {b}",
    "hostServices.wizard.overlapConflictV6": "Conflicto IPv6 entre {a} y {b}",
    "hostServices.wizard.autoDnsServers": "DNS para CPEs (auto)",
    "hostServices.wizard.noDnsServers": "ninguno",
    "hostServices.wizard.autoDnsViews": "Views DNS generadas automáticamente",
    "hostServices.wizard.acsUrlPrimary": "ACS URL primaria",
    "hostServices.wizard.acsUrlFallback": "ACS URL fallback",
    "hostServices.wizard.dnsForwarders": "DNS forwarders upstream",
    "hostServices.wizard.dnsForwardersPlaceholder": "1.1.1.1, 8.8.8.8",
    "hostServices.wizard.dnsForwardersHelp": "Servidores de reenvío separados por comas. Déjelo en blanco para deshabilitar el reenvío upstream.",
    "hostServices.wizard.reviewInterfaces": "Interfaces",
    "hostServices.wizard.reviewSubnet4": "Subnet IPv4",
    "hostServices.wizard.reviewPrefix6": "Prefijo IPv6",
    "hostServices.wizard.reviewForwarders": "Forwarders",
    "hostServices.wizard.noForwarders": "ninguno",
    "hostServices.wizard.reviewAcsUrl": "ACS primaria",
    "hostServices.wizard.activating": "Activando...",
    "hostServices.wizard.confirmActivate": "Confirmar y activar",
    "hostServices.vlanIdRange": "El ID de VLAN debe estar entre 1 y 4094.",
    "hostServices.parentRequired": "La interfaz padre es obligatoria.",
    "hostServices.wizard.result.title": "Resultado de la aplicación",
    "hostServices.wizard.result.applied": "aplicado",
    "hostServices.wizard.result.partial": "Aplicación parcial — algunos servicios fallaron.",
    "hostServices.wizard.result.success": "Todos los servicios se aplicaron correctamente.",
    "hostServices.wizard.result.allFailed": "No se pudo aplicar ningún servicio.",
    "hostServices.wizard.result.retry": "Reintentar",
    "hostServices.wizard.result.retrying": "Reintentando...",
    "hostServices.wizard.result.close": "Cerrar",
    "hostServices.wizard.result.backToEdit": "Volver a editar",
    "hostServices.wizard.result.skipped": "omitido (sin interfaces calificadas)",
    "hostServices.wizard.result.noServices": "El backend no devolvió ningún servicio.",
    "hostServices.wizard.result.appliedCount": "servicio(s) aplicado(s).",
    "hostServices.wizard.open": "Abrir asistente de configuración",
    "hostServices.wizard.slaacOverlapNote": "Con SLAAC+DHCPv6 simultáneos (modo dual), las direcciones EUI-64 pueden coincidir con el pool DHCPv6; el DAD del cliente resuelve las duplicaciones. Reduzca el pool o desactive SLAAC si desea aislamiento total.",

    "users.title": "Usuarios",
    "users.newUser": "Nuevo Usuario",
    "users.searchPlaceholder": "Buscar por nombre o email...",
    "users.allRoles": "Todas las funciones",
    "users.roleDeveloper": "Desarrollador",
    "users.roleAdmin": "Admin",
    "users.roleOperator": "Operador",
    "users.tableName": "Nombre",
    "users.tableEmail": "Email",
    "users.tableRole": "Función",
    "users.tableStatus": "Estado",
    "users.tableActions": "Acciones",
    "users.empty": "Ningún usuario encontrado",
    "users.deleteTitle": "Eliminar usuario",
    "users.deleteMessage": "¿Está seguro que desea eliminar este usuario? Esta acción no se puede deshacer.",

    "operators.pageTitle": "Operadores",
    "operators.newOperator": "Nuevo Operador",
    "operators.never": "Nunca",
    "operators.searchPlaceholder": "Buscar por nombre o email...",
    "operators.empty": "Ningún operador encontrado",
    "operators.tableUsername": "Usuario",
    "operators.tableFullName": "Nombre",
    "operators.tableEmail": "Email",
    "operators.tableRole": "Rol",
    "operators.tableLastLogin": "Último inicio de sesión",
    "operators.tableStatus": "Estado",
    "operators.tableActions": "Acciones",
    "operators.statusActive": "Activo",
    "operators.statusInactive": "Inactivo",
    "operators.reactivate": "Reactivar",
    "operators.createTitle": "Nuevo Operador",
    "operators.password": "Contraseña",
    "operators.showPassword": "Mostrar contraseña",
    "operators.hidePassword": "Ocultar contraseña",
    "operators.deactivateButton": "Desactivar",
    "operators.deactivateTitle": "¿Desactivar operador?",
    "operators.deactivateMessage": "El operador \"{{name}}\" será desactivado (soft delete).",
    "operators.deleteButton": "Eliminar",
    "operators.deleteTitle": "¿Eliminar operador?",
    "operators.deleteMessage": "¿Eliminar definitivamente a {{name}}? Esta acción es permanente y también elimina las passkeys del operador.",
    "operators.setPasswordButton": "Establecer contraseña",
    "operators.setPasswordTitle": "Establecer contraseña",
    "operators.setPasswordFor": "Establecer contraseña para {{name}}",
    "operators.currentPassword": "Contraseña actual",
    "operators.newPassword": "Nueva contraseña",
    "operators.confirmPassword": "Confirmar nueva contraseña",
    "operators.passwordMismatch": "Las contraseñas no coinciden",

    "profile.changePassword.title": "Cambie su contraseña",
    "profile.changePassword.description": "Por seguridad, debe definir una nueva contraseña antes de continuar.",
    "profile.changePassword.currentPassword": "Contraseña actual",
    "profile.changePassword.newPassword": "Nueva contraseña",
    "profile.changePassword.confirmPassword": "Confirmar nueva contraseña",
    "profile.changePassword.submit": "Guardar nueva contraseña",
    "profile.changePassword.required": "Rellene todos los campos.",
    "profile.changePassword.mismatch": "Las contraseñas no coinciden.",
    "profile.changePassword.weakPassword": "La nueva contraseña debe tener al menos 12 caracteres, con mayúscula, minúscula, número y símbolo.",
    "profile.changePassword.invalidCurrent": "Contraseña actual incorrecta.",
    "profile.changePassword.error": "No se pudo cambiar la contraseña. Inténtelo de nuevo.",

    "settings.title": "Configuración",
    "backups.title": "Copias de Seguridad",
    "billing.title": "Facturación",
    "profile.title": "Perfil",
    "profile.name": "Nombre",
    "profile.email": "Email",
    "profile.role": "Función",
    "profile.roleDeveloper": "Desarrollador",
    "profile.roleAdmin": "Administrador",
    "profile.roleOperator": "Operador",
    "profile.sessionExpires": "La sesión expira en",
    "profile.authorizedEngines": "Engines autorizados",
    "profile.none": "Ninguno",
    "profile.notAuthenticated": "No autenticado",
    "profile.authMethod": "Método de autenticación",
    "profile.authWebauthn": "WebAuthn Local",
    "profile.authSso": "SSO (Core)",
    "profile.authPassword": "Contraseña local",
    "profile.session": "Sesión",
    "profile.renewSession": "Renovar sesión",
    "profile.localSessionNote": "Las sesiones locales no se pueden renovar (sin conexión con el Core).",
    "profile.coreConnection": "Conexión con el Core",
    "profile.connected": "Conectado",
    "profile.disconnected": "Sin conexión",
    "profile.passkeys.title": "Passkeys",
    "profile.passkeys.empty": "No hay passkeys registradas.",
    "profile.passkeys.created": "Creada el",
    "profile.passkeys.revoke": "Revocar passkey",
    "profile.passkeys.revokeTitle": "¿Revocar passkey?",
    "profile.passkeys.revokeMessage": "La passkey \"{{name}}\" será revocada y no podrá usarse para iniciar sesión. Esta acción no se puede deshacer.",
    "profile.passkeys.revokeConfirm": "Revocar",
    "profile.passkeys.revoked": "Passkey revocada con éxito.",
    "profile.passkeys.error": "Error al cargar las passkeys. Inténtalo de nuevo.",
    "profile.passkeys.notFound": "Passkey no encontrada o no disponible.",
    "notFound.message": "Página no encontrada",
    "createEngine.title": "Crear Engine Node",
    "createEngine.description": "Registrar un nuevo Engine Node en el registro.",
    "createEngine.searchMinChars": "Escriba al menos 3 caracteres para buscar",
    "createEngine.formUrl": "URL Base",
    "createEngine.formUrlPlaceholder": "https://engine.cliente.com",
    "createEngine.formChannel": "Canal",
    "createEngine.channelStable": "Stable",
    "createEngine.channelBeta": "Beta",
    "createEngine.channelAlfa": "Alfa",
    "createEngine.formCloudHosted": "Hospedado en la Nube",
    "createEngine.formIpv4": "IPv4 del Servidor",
    "createEngine.formIpv6": "IPv6 del Servidor",
    "createEngine.formCaaEmail": "Correo CAA (TLS)",
    "createEngine.creating": "Creando...",
    "createEngine.createButton": "Crear Engine",
    "createEngine.createError": "Error al crear engine node.",
    "createEngine.tokenWarning": "Guarda este token con seguridad — se muestra una sola vez. El API Token del nodo puede regenerarse después en el panel de acciones del nodo.",
    "createEngine.installTokenTitle": "Token de Instalación",
    "createEngine.installTokenHint24h": "Expira en 24 horas — uso único",
    "createEngine.installTokenPlaybookHint": "Úselo con el playbook installer/install-engine.yml",
    "createEngine.installTokenGenerating": "Generando token de instalación...",
    "createEngine.installTokenGenerateError": "No se pudo generar el token de instalación. El node fue creado — genere el token en el panel de acciones del node.",
    "createEngine.installTokenNotStarted": "No se pudo iniciar la generación del token de instalación. Usa el panel de acciones del nodo para generarlo.",
    "createEngine.installTokenPendingHint": "Token de instalación aún no solicitado.",
    "createEngine.goToNodeActions": "Abrir Acciones del node",
    "createEngine.apiTokenLabel": "API Token del node",
    "createEngine.apiTokenDesc": "Token M2M que el node usa para autenticar llamadas a la API del Core.",
    "engine.create.clientSearchMode": "Modo de búsqueda",
    "engine.create.clientSearch": "Buscar cliente",
    "engine.create.clientSearchPlaceholder": "Buscar por nombre o documento/RFC...",
    "engine.create.clientSearchMinChars": "Escriba al menos 3 caracteres u 11 dígitos",
    "engine.create.clientSearchError": "Error al buscar clientes",
    "engine.create.clientsFound": "Clientes encontrados",
    "engine.create.controlClientsFound": "Clientes en RC Control (clic para importar)",
    "engine.create.noClientsFound": "No se encontraron clientes",
    "engine.create.newClient": "Registrar nuevo cliente",
    "engine.create.newClientTitle": "Nuevo Cliente Local",
    "engine.create.clientName": "Nombre del Cliente",
    "engine.create.clientDocument": "Documento / RFC",
    "engine.create.clientEmail": "Correo electrónico (opcional)",
    "engine.create.createClientBtn": "Registrar y Seleccionar",
    "engine.create.creatingClient": "Registrando...",
    "engine.create.clientCreateError": "Error al registrar cliente",
    "engine.create.selectedClient": "Cliente Seleccionado",
    "engine.create.changeClient": "Cambiar",
    "engine.create.selectContract": "Contrato Activo (obligatorio)",
    "engine.create.selectContractPlaceholder": "Seleccione un contrato activo...",
    "engine.create.contractRequired": "Es obligatorio seleccionar un contrato activo",
    "engine.create.noActiveContracts": "No se encontraron contratos activos para este cliente.",
    "engine.create.goToContracts": "Ir a Contratos",
    "engine.create.contractNotActive": "El contrato seleccionado ya no está activo. Por favor, seleccione otro contrato.",
    "engine.create.loadingContracts": "Cargando contratos activos...",
    "engine.create.contractsLoadError": "Error al cargar contratos activos",
    "engine.create.clientMatchAmbiguous": "Cliente ya registrado, pero no fue posible vincularlo automáticamente.",
    "engine.create.clientNameAndDocumentRequired": "Nombre y documento/RFC son obligatorios",
    "engine.create.clientDocumentInvalid": "Documento/RFC inválido (debe contener 11 o 14 dígitos)",
    "engine.create.clientAlreadyExists": "Ya existe un cliente registrado con este documento",
    "engine.create.clientCreateBadRequest": "Datos inválidos para registro de cliente",
    "engine.create.noPlan": "Sin plan",
    "engine.create.noEndDate": "Sin fecha de finalización",
    "engine.create.contractNotFound": "Contrato no encontrado",
    "engine.create.createBadRequest": "Solicitud inválida para creación del engine",
    "engine.contracts.linkedContract": "Contrato Vinculado",
    "engine.contracts.contractLabel": "Contrato",
    "engine.contracts.planLabel": "Plan",
    "engine.contracts.clientLabel": "Cliente",
    "engine.contracts.validity": "Vigencia",
    "engine.contracts.status.active": "Activo",
    "engine.contracts.status.warning": "Advertencia",
    "engine.contracts.status.blocked": "Bloqueado",
    "engine.contracts.status.revoked": "Revocado",
    "engine.contracts.unavailable": "Información del contrato no disponible",
    "engine.contracts.noContract": "Ningún contrato vinculado",
    "engine.contracts.changeContract": "Cambiar Contrato",
    "engine.contracts.unlinkContract": "Desvincular Contrato",
    "engine.contracts.selectActiveContract": "Seleccionar Contrato Activo",
    "engine.contracts.pickerTitle": "Vincular Contrato al Engine",
    "engine.contracts.pickerDescription": "Seleccione un contrato activo del cliente para vincular a este Engine.",
    "engine.contracts.pickerDescriptionAny": "Seleccione un contrato activo para vincular a este Engine.",
    "engine.contracts.saving": "Guardando...",
    "engine.contracts.saveSuccess": "Contrato actualizado con éxito",
    "engine.contracts.saveError": "Error al actualizar contrato",
    "engine.contracts.pendingChange": "Modificación pendiente",
    "engineActions.activateDesc": "Habilitar este Engine Node.",
    "engineActions.cloudAutoActivate": "Nodes en la nube se auto-activan.",
    "engineActions.openEngine": "Abrir Engine",
    "engineActions.openEngineDesc": "Acceder a la interfaz del Engine Node.",
    "engineActions.restorePayment": "Restaurar Pago",
    "engineActions.restorePaymentDesc": "Reactivar facturación del Engine Node.",
    "engineActions.confirmRevoke": "Confirmar Revocación",
    "engineActions.confirmRevokeDesc": "Confirma revocación definitiva del Engine Node.",
    "engineActions.suspendDesc": "Suspender temporalmente la gestión de CPEs.",
    "engineActions.rotateToken": "Rotar Token API",
    "engineActions.rotateTokenDesc": "Generar nuevo secreto HMAC. Token antiguo invalidado inmediatamente.",
    "engineActions.tokenWarning": "Guarde con seguridad — no volverá a verlo.",
    "engineActions.deleteDesc": "Eliminar permanentemente. Elimina en cascada copias de seguridad y logs.",
    "engineActions.suspendTitle": "Suspender Engine Node",
    "engineActions.suspendMessage": "¿Está seguro que desea suspender",
    "engineActions.deleteTitle": "Eliminar Engine Node",
    "engineActions.deleteMessage": "Está a punto de eliminar permanentemente",
    "engineActions.confirmDelete": "Confirmar Eliminación",
    "engineActions.confirmDeletePrompt": "Escriba el nombre del engine para confirmar:",
    "engineActions.deletePermanently": "Eliminar Permanentemente",
    "engineActions.copied": "Copiado",
    "engineActions.copy": "Copiar",
    "engineActions.rotateTokenButton": "Rotar Token",
    "engineActions.installToken": "Token de Instalación",
    "engineActions.installTokenDesc": "Generar token temporal (24h) para que el instalador Ansible descargue la configuración.",
    "engineActions.installTokenButton": "Generar Install Token",
    "engineActions.installTokenWarning": "Guarde este token — válido por 24h, uso único.",
    "engine.actions.openPanelFailed": "Error al generar el token de acceso. Inténtelo de nuevo.",
    "engineInfo.title": "Informaciones del Engine",
    "engineInfo.description": "Los campos editables se pueden cambiar inline — haga clic en Guardar Cambios para persistir.",
    "engineInfo.configSection": "Configuración",
    "engineInfo.reportedBy": "Reportado por el Engine",
    "engineInfo.nodeId": "Node ID",
    "engineInfo.slug": "Slug",
    "engineInfo.billingStatus": "Estado de Facturación",
    "engineInfo.contractVersion": "Versión del Contrato",
    "engineInfo.containerVersions": "Versiones de Contenedores",
    "engineInfo.lastHeartbeat": "Último Heartbeat",
    "engineInfo.lastHealthScore": "Último Índice de Salud",
    "engineInfo.cpeCount": "CPEs Activos",
    "engineInfo.cpeOverage": "Excedente CPE",
    "engineInfo.tokenRotated": "Token API Rotado",
    "engineInfo.createdAt": "Creado el",
    "engineInfo.updatedAt": "Actualizado el",
    "engineInfo.suspendedAt": "Suspendido el",
    "engineInfo.revokedAt": "Revocado el",
    "engineInfo.haproxyIp": "HAProxy IP",
    "engineInfo.redisEndpoint": "Redis Endpoint",
    "engineInfo.clickhouseEndpoint": "ClickHouse Endpoint",
    "engineInfo.billingPeriod": "Período de Facturación",
    "engineInfo.periodStart": "Inicio del Período",
    "engineInfo.periodEnd": "Fin del Período",
    "engineInfo.uspVersion": "Versión USP",
    "engineInfo.uspCpes": "CPEs USP",
    "engineInfo.formClientId": "ID del Cliente",
    "engineInfo.formWsUrl": "WS URL",
    "engineInfo.formWsUrlPlaceholder": "wss://engine.example.com",
    "engineInfo.cloudHosted": "Hospedado en la Nube",
    "engineInfo.formMaxCpes": "Máx. CPEs",
    "engineInfo.formSoftLimit": "Límite Flexible",
    "engineInfo.formHardLimit": "Límite Rígido",
    "engineInfo.saveChanges": "Guardar Cambios",
    "engineInfo.saveError": "Error al guardar",
    "engineBackups.loading": "Cargando backups...",
    "engineBackups.empty": "Sin copias de seguridad",
    "engineBackups.tableFilename": "Nombre del Archivo",
    "engineBackups.tableChecksum": "Checksum",
    "engineBackups.tableCreatedAt": "Creado el",
    "engineBackups.statusUnknown": "Desconocido",
    "engineBackups.restore": "Restaurar",
    "engineBackups.restoreConfirmTitle": "Restaurar copia de seguridad en el nodo",
    "engineBackups.restoreConfirmBody": "Esta operación reemplaza los datos del nodo con el contenido de esta copia de seguridad. El nodo se reiniciará durante la restauración.",
    "engineBackups.restoreConfirmNodePrompt": "Escriba el nombre del nodo ({{node}}) para confirmar:",
    "engineBackups.restoreConfirmInputLabel": "Nombre del nodo para confirmación",
    "engineBackups.restoreConfirmPlaceholder": "Nombre del nodo",
    "engineBackups.restoreConfirmAction": "Restaurar copia",
    "engineBackups.restorePending": "Restauración en curso",
    "engineBackups.restoreApplied": "Restauración aplicada",
    "engineBackups.restoreFailed": "La restauración falló",
    "engineBackups.restoreSessionTitle": "Restauraciones de esta sesión",
    "engineBackups.restoreErrorLegacy": "Las copias por nodo no son compatibles — recree la copia con el instalador actual.",
    "engineBackups.restoreErrorKeyMismatch": "La copia fue cifrada para otra clave y no se puede restaurar.",
    "engineBackups.restoreErrorNodeUnavailable": "Nodo fuera de línea o sin soporte de restauración.",
    "engineBackups.restoreErrorAlreadyActive": "Ya hay otra restauración activa en este nodo.",
    "engineBackups.restoreErrorDecryptFailed": "Error al descifrar la copia — consulte los registros del Core.",
    "engineBackups.restoreErrorGeneric": "Error al iniciar la restauración.",
    "engineBackups.restoreErrorBusy": "El nodo está ocupado con otra operación de restauración.",
    "engineBackups.restoreErrorKeyUnavailable": "Clave global de copias no disponible en el Core — inténtelo de nuevo.",
    "engineBackups.restoreErrorNotFound": "Registro de la restauración no encontrado.",
    "engineBackups.restoreErrorNodeDisconnected": "El nodo se desconectó durante la restauración.",
    "engineBackups.restoreErrorTimeout": "La restauración excedió el tiempo límite.",
    "engineBackups.restoreErrorFileUnavailable": "Archivo de copia no disponible en el almacenamiento del Core.",
    "engineBackups.restoreErrorSendFailed": "Error al entregar la copia al nodo.",
    "engineBackups.restoreErrorOverLimit": "La copia excede el límite de tamaño de restauración.",
    "engineBackups.restoreErrorEmptyPlaintext": "El contenido descifrado de la copia está vacío.",
    "engineBackups.restoreErrorReconciled": "Restauro antiguo reconciliado por el Core — inicie una nueva operación.",
    "engineBackups.restoreErrorFailed": "La restauración falló en el nodo.",
    "engineBackups.restoreErrorOther": "La restauración falló con un código no reconocido — consulte los registros del Core.",
    "engine.backupTitle": "Copias de Seguridad",
    "engine.download": "Descargar",
    "engine.noBackups": "Sin copias de seguridad",
    "engine.downloadFailed": "Error al descargar backup",
    "engine.backupSize": "Tamaño",
    "engine.backupDate": "Fecha",
    "engineHealth.loading": "Cargando datos de salud...",
    "engineHealth.empty": "Sin datos de salud aún",
    "engineHealth.last24h": "Últimas 24 horas",
    "engineHealth.chartLabel": "Índice de Salud",
    "engineHealth.details": "Desglose del índice de salud",
    "engineHealth.currentScore": "Índice actual",
    "engineHealth.lastUpdate": "Última actualización",
    "engineHealth.component.db": "Base de datos",
    "engineHealth.component.redis": "Redis",
    "engineHealth.component.genieacs": "GenieACS",
    "engineHealth.component.core_ws": "WebSocket Core",
    "engineHealth.statusOk": "OK",
    "engineHealth.statusDown": "No disponible",
    "engineHealth.statusUnconfigured": "No configurado",
    "engineHealth.pointsLost": "Puntos perdidos",
    "engineHealth.noPenalty": "Sin penalización",
    "engineHealth.cpu": "CPU",
    "engineHealth.producerHeartbeat": "Fuente: heartbeat del Engine",
    "engineHealth.producerSentinel": "Fuente: telemetría del Sentinel",
    "engineHealth.formulaHeartbeat":
      "Base 100; -20 por servicio esencial no disponible (DB, Redis, GenieACS); -10 si el WebSocket con el Core está desconectado.",
    "engineHealth.formulaSentinel":
      "Base 100; -15 por servicio no saludable; -20 si CPU > 90%; -10 si CPU > 75%.",
    "engineHealth.breakdownPending":
      "Desglose disponible a partir del próximo heartbeat.",
    "engineHealth.noHistory24h": "Sin histórico en las últimas 24 horas.",
    "engineLogs.loading": "Cargando logs...",
    "engineLogs.filterLevel": "Filtrar por nivel",
    "engineLogs.allLevels": "Todos los Niveles",
    "engineLogs.levelInfo": "Info",
    "engineLogs.levelWarn": "Aviso",
    "engineLogs.levelError": "Error",
    "engineLogs.autoRefresh": "Actualización automática cada 10s",
    "engineLogs.empty": "Ningún registro de log aún",
    "versionHistory.title": "Histórico de Versiones",
    "versionHistory.noVersions": "No se encontraron versiones anteriores.",
    "versionHistory.reason": "Motivo",
    "noc.totalCpes": "Total de CPEs",
    "noc.onlineEngines": "Engines Online",
    "noc.activeOutages": "Cortes Activos",
    "noc.inactiveDevices": "Dispositivos Inactivos",
    "noc.cpeCountChart": "Conteo de CPEs (24h)",
    "noc.noHistoryData": "Sin datos históricos",
    "noc.engineHealth": "Salud de Engines",
    "noc.healthIndex": "Índice de Salud",
    "noc.noEngines": "Ningún engine registrado",
    "noc.statusOnline": "En línea",
    "noc.statusOffline": "Sin conexión",
    "noc.statusDegraded": "Degradado",
    "noc.statusSuspended": "Suspendido",
    "noc.recentOutages": "Cortes Recientes",
    "noc.noOutages": "Ningún corte en las últimas 24h",
    "noc.avgCpu": "CPU Promedio",
    "noc.totalRam": "RAM Total",
    "noc.totalDisk": "Disco Total",
    "engine.cpu": "CPU",
    "engine.ram": "RAM",
    "engine.disk": "Disco",
    "engine.services": "Servicios",
    "engine.containers": "Contenedores",
    "engine.infrastructure": "Infraestructura",
    "engine.serviceName": "Servicio",
    "engine.serviceStatus": "Estado",
    "engine.containerImage": "Imagen",
    "noc.active": "Activo",
    "noc.resolved": "Resuelto",
    "noc.engineRegistry": "Registro de Engines",
    "noc.liveEvents": "Eventos en Vivo",
    "noc.filterAll": "Todos",
    "noc.filterFaults": "Fallas",
    "noc.filterOutages": "Cortes",
    "noc.filterSignals": "Señales",
    "noc.noEvents": "Ningún evento aún",
    "noc.device": "Dispositivo",
    "noc.olt": "OLT",
    "settings.tabGeneral": "General",
    "settings.tabSmtp": "SMTP",
    "settings.tabIntegrations": "Integraciones",
    "settings.tabSecurity": "Seguridad",
    "settings.timezone": "Zona Horaria",
    "settings.timezoneHelp": "Los tiempos en la interfaz se mostrarán en esta zona horaria. Los servidores siempre UTC.",
    "settings.smtpHost": "Host",
    "settings.smtpPort": "Puerto",
    "settings.smtpUser": "Usuario",
    "settings.smtpPassword": "Contraseña",
    "settings.smtpFrom": "Remitente (Email)",
    "settings.smtpEncryption": "Cifrado",
    "settings.smtpEncryptionNone": "Ninguna",
    "settings.smtpEncryptionTls": "TLS (implícito)",
    "settings.smtpEncryptionStarttls": "STARTTLS",
    "settings.testEmail": "Probar Email",
    "settings.sending": "Enviando...",
    "settings.testEmailSent": "¡Email enviado con éxito!",
    "settings.loadingSettings": "Cargando configuración...",
    "settings.saveSettings": "Guardar Configuración",
    "settings.savedSuccess": "¡Configuración guardada con éxito!",
    "settings.saveError": "Error al guardar",
    "settings.rcControlToken": "Token API RC Control",
    "settings.rcControlTokenPlaceholder": "Bearer token generado en el panel admin de RC Control",
    "settings.rcControlTokenHelp": "Generado en: RC Control → Configuración → Tokens de API. Ámbitos: read:clients o *.",
    "settings.saveIntegration": "Guardar Integración",
    "settings.tokenSavedSuccess": "¡Token guardado con éxito!",
    "settings.testToken": "Probar token",
    "settings.testing": "Probando...",
    "settings.tokenValid": "Token válido",
    "settings.tokenInvalid": "Token inválido",
    "settings.sessionTimeout": "Tiempo de Sesión (minutos)",
    "settings.sessionTimeoutHelp": "Los cambios requieren reinicializar el Core para tener efecto.",
    "settings.rateLimit": "Límite de Tasa (por minuto)",
    "settings.tabUpdateChannels": "Canales de Actualización",
    "settings.tabRegistry": "Registry",
    "settings.tabBackup": "Copias de Seguridad",
    "settings.backupRetentionTitle": "Retención de Copias de Seguridad",
    "settings.backupRetentionDescription": "Política global de retención aplicada a todos los nodos en el auto-prune (subida y worker).",
    "settings.backupRetentionMaxCount": "Máximo de copias de seguridad por nodo",
    "settings.backupRetentionMaxCountHelp": "0 desactiva el criterio de cantidad. El sistema mantiene un techo absoluto de 50 copias de seguridad por nodo (red de seguridad).",
    "settings.backupRetentionMaxCountError": "Introduzca un número entero entre 0 y 1000.",
    "settings.backupRetentionMaxAgeDays": "Antigüedad máxima de las copias de seguridad (días)",
    "settings.backupRetentionMaxAgeDaysHelp": "0 desactiva el criterio de antigüedad. Las copias más antiguas que este límite se eliminan con el prune.",
    "settings.backupRetentionMaxAgeDaysError": "Introduzca un número entero entre 0 y 3650.",
    "settings.backupRetentionSave": "Guardar retención",
    "settings.backupRetentionSaved": "Retención guardada correctamente.",
    "settings.updateChannels.title": "Canales de Actualización",
    "settings.updateChannels.channel": "Canal",
    "settings.updateChannels.latestTag": "Etiqueta Actual",
    "settings.updateChannels.releasedAt": "Publicado el",
    "settings.updateChannels.sentinelVersion": "Versión Sentinel",
    "settings.updateChannels.scheduleMode": "Modo de Programación",
    "settings.updateChannels.immediate": "Inmediato",
    "settings.updateChannels.maintenanceWindow": "Ventana de Mantenimiento",
    "settings.updateChannels.manual": "Manual",
    "settings.updateChannels.paused": "Pausado",
    "settings.updateChannels.windowStart": "Inicio",
    "settings.updateChannels.windowEnd": "Fin",
    "settings.updateChannels.timezone": "Zona Horaria",
    "settings.updateChannels.pushUpdateNow": "Forzar Actualización Ahora",
    "settings.updateChannels.save": "Guardar",
    "settings.updateChannels.cancel": "Cancelar",
    "settings.registry.title": "Configuración del Registry",
    "settings.registry.url": "URL del Registry",
    "settings.registry.repository": "Repositorio",
    "settings.registry.username": "Usuario",
    "settings.registry.password": "Contraseña",
    "settings.registry.passwordSaved": "Contraseña guardada",
    "settings.registry.showPassword": "Mostrar contraseña",
    "settings.registry.hidePassword": "Ocultar contraseña",
    "settings.registry.passwordKeepHint": "Deje en blanco para mantener la contraseña actual.",
    "settings.smtp.passwordSaved": "Contraseña guardada",
    "settings.smtp.passwordKeepHint": "Deje en blanco para mantener la contraseña actual.",
    "settings.registry.testConnection": "Probar Conexión",
    "settings.registry.save": "Guardar",
    "settings.registry.connectionSuccess": "Conexión exitosa",
    "settings.registry.connectionFailed": "Error de conexión",
    "backups.trigger": "Disparar Backup",
    "backups.triggering": "Disparando...",
    "backups.tableId": "ID",
    "backups.tableDate": "Fecha",
    "backups.tableSize": "Tamaño",
    "backups.tableType": "Tipo",
    "backups.tableNode": "Engine",
    "backups.typeDatabase": "Base de Datos",
    "backups.typeConfig": "Configuración",
    "backups.manual": "Manual",
    "backups.automatic": "Automático",
    "backups.noBackups": "Ningún backup encontrado",
    "backups.deleteTitle": "Eliminar backup",
    "backups.deleteMessage": "¿Está seguro que desea eliminar este backup?",
    "backups.effectiveRetention": "Retención efectiva (por nodo)",
    "backups.effectiveRetentionValue": "{{count}} / {{days}}d",
    "billing.monthlyClose": "Cierre Mensual",
    "billing.loadingSummary": "Cargando resumen...",
    "billing.totalEngines": "Total de Engines",
    "billing.activeCpes": "CPEs Activos",
    "billing.activeEngines": "Engines Activos",
    "billing.warningEngines": "En Aviso",
    "billing.enginesBilling": "Engines - Billing",
    "billing.overageAlerts": "Alertas de Overage",
    "billing.noOverage": "Ningún engine excede el soft limit.",
    "billing.antiFraudAlerts": "Alertas Anti-Fraude",
    "billing.antiFraudDetected": "alertas detectados.",
    "billing.noAnomalies": "Ninguna anomalía detectada.",
    "billing.revenueTrend": "Tendencia de Ingresos",
    "billing.revenueChartPlaceholder": "Gráfico de ingresos - disponible tras F08-BILLING",
    "billing.closeConfirmTitle": "Cierre Mensual",
    "billing.closeConfirmMessage": "¿Confirma el cierre de facturación para el período actual? Esta acción activa el BillingCalculatorWorker y genera las facturas del mes.",
    "billing.closeConfirmLabel": "Confirmar Cierre",
    "billing.overviewSubtitle": "Resumen y cierre de facturación de contratos y engines.",
    "billing.selectPeriod": "Mes de Referencia",
    "billing.periodsHistory": "Historial de Cierres",
    "billing.periodsCount": "período(s) registrado(s)",
    "billing.scope": "Ámbito",
    "billing.scopeContract": "Contrato",
    "billing.scopeEngine": "Engine",
    "billing.reference": "Referencia",
    "billing.period": "Período",
    "billing.cpeDays": "CPE-Días / Promedio",
    "billing.amount": "Monto",
    "billing.status": "Estado",
    "billing.dueDate": "Vencimiento",
    "billing.breakdown": "Detalle",
    "billing.viewBreakdown": "Ver Detalle",
    "billing.noPeriods": "Ningún período de facturación registrado.",
    "billing.periodsClosed": "período(s) de contrato cerrado(s).",
    "billing.engineCloseLegacy": "Cierre Engine (Legado)",
    "billing.engineCloseConfirmTitle": "Cierre de Engine (Legado)",
    "billing.engineCloseConfirmMessage": "¿Confirma el cierre legado por engine? Esta acción activa el BillingCalculatorWorker mediante ClickHouse.",
    "billing.engineCloseResult": "Resultado del Cierre Engine",
    "billing.contractsOverview": "Facturación por Contrato",
    "billing.totalContracts": "Total de Contratos",
    "billing.totalLabel": "Total",
    "billing.engineRef": "Engine",
    "billing.loadingPeriods": "Cargando historial de períodos...",
    "billing.closeError": "Error al realizar el cierre mensual.",
    "billing.engineCloseError": "Error al realizar el cierre legado de engine.",
    "billing.revenueEstimate": "Estimación de Ingresos (Todos los Ámbitos)",
    "billing.contracts.title": "Contratos",
    "billing.contracts.id": "Contrato",
    "billing.contracts.client": "Cliente",
    "billing.contracts.plan": "Plan",
    "billing.contracts.periodsClosed": "Períodos Cerrados",
    "billing.contracts.avgCpes": "Promedio CPEs",
    "billing.contracts.faixa": "Franja",
    "billing.contracts.totalAmount": "Total Facturado",
    "billing.contracts.noContracts": "Ningún contrato con períodos cerrados.",
    "billing.plans.title": "Planes de Facturación",
    "billing.plans.default": "Predeterminado",
    "billing.clients.title": "Clientes",

    "versioning.versionControl": "Control de Versiones",
    "versioning.commit": "Confirmar",
    "versioning.commitMessage": "Mensaje de commit",
    "versioning.history": "Historial",
    "versioning.branch": "Rama",
    "versioning.branches": "Ramas",
    "versioning.createBranch": "Crear Rama",
    "versioning.branchName": "Nombre de Rama",
    "versioning.baseRef": "Referencia Base",
    "versioning.merge": "Fusionar",
    "versioning.mergeFrom": "Fusionar de",
    "versioning.mergeTo": "Fusionar a",
    "versioning.cherryPick": "Cherry Pick",
    "versioning.rollback": "Revertir",
    "versioning.rollbackTo": "Revertir a",
    "versioning.publish": "Publicar",
    "versioning.publishTo": "Publicar a",
    "versioning.publishAll": "Publicar Todo",
    "versioning.staged": "Preparado",
    "versioning.staging": "Preparación",
    "versioning.stage": "Preparar",
    "versioning.unstage": "Quitar de preparación",
    "versioning.clearStaging": "Limpiar preparación",
    "versioning.stagedChanges": "Cambios Preparados",
    "versioning.noCommits": "Sin commits aún",
    "versioning.noBranches": "Sin ramas disponibles",
    "versioning.currentBranch": "Rama actual",
    "versioning.version": "Versión",
    "versioning.author": "Autor",
    "versioning.date": "Fecha",
    "versioning.message": "Mensaje",
    "versioning.compare": "Comparar",
    "versioning.diff": "Diff",
    "versioning.from": "De",
    "versioning.to": "A",
    "versioning.field": "Campo",
    "versioning.noChanges": "Sin cambios",
    "versioning.showAll": "Mostrar Todos",
    "versioning.showChangesOnly": "Mostrar Solo Cambios",
    "staging.title": "Cambios en Etapa",
    "staging.empty": "Sin cambios en etapa",
    "staging.remove": "Quitar",
    "staging.channel": "Canal",
    "staging.clearAll": "Limpiar Todo",
    "staging.publishAll": "Publicar Todo",
    "staging.published": "{count} ítems publicados con éxito",
    "staging.publishError": "Error al publicar",
    "staging.badgeLabel": "Cambios en Etapa",
    "staging.addToStaging": "Agregar a Etapa",
    "cwmp.cwmpConfigs": "Configuraciones CWMP",
    "cwmp.configKey": "Clave de Configuración",
    "cwmp.configValue": "Valor de Configuración",
    "cwmp.dataType": "Tipo de Dato",
    "cwmp.configType": "Tipo de Config",
    "cwmp.tagValue": "Valor de Tag",
    "cwmp.objectName": "Nombre del Objeto",
    "cwmp.ageValue": "Edad (segundos)",
    "cwmp.provisionArgs": "Argumentos de Provision",
    "cwmp.createConfig": "Crear Configuración",
    "cwmp.editConfig": "Editar Configuración",
    "cwmp.deleteConfig": "Eliminar Configuración",
    "cwmp.configName": "Nombre de la Configuración",
    "cwmp.configDescription": "Descripción de la Configuración",
    "cwmp.active": "Activo",
    "branch.channel": "Canal",
    "branch.feature": "Funcionalidad",
    "branch.deleteBranch": "Eliminar Rama",
    "branch.cannotDeleteChannel": "No se puede eliminar una rama de canal",
    "branch.cannotDelete": "No se pudo eliminar la rama",
    "diff.fieldByField": "Campo por Campo",
    "diff.noDifferences": "No se encontraron diferencias",
    "publish.publishWarning": "Los Engines recibirán esta actualización en la próxima sincronización. Esta acción no se puede deshacer.",
    "publish.publishConfirm": "Confirmar Publicación",
    "publish.publishSuccess": "Publicación completada con éxito",
    "publish.publishFailed": "Error al publicar",
    "common.searchPlaceholder": "Buscar...",
    "common.searchDevice": "Buscar dispositivo...",
    "common.loadError": "Error al cargar. Inténtelo de nuevo.",
    "common.notFound": "No encontrado",
    "common.notFoundTitle": "404",
    "common.notFoundMessage": "Página no encontrada",
    "common.comingSoon": "Próximamente",
    "common.critical": "Crítico",
    "common.ignoreReasonPrompt": "¿Razón para ignorar?",
    "common.assignOltPrompt": "Asignar al ID de OLT:",
    "common.examplePlaceholder": "ej. 8.8.8.8",
    "common.cidrPlaceholder": "192.168.1.0/24",
    "common.oltId": "ID de OLT",
    "faults.title": "Gestión de Fallas",
    "faults.searchCommands": "Buscar comandos...",
    "faults.searchBySerial": "Buscar por serial...",
    "engine.vp.title": "Parámetros Virtuales (Caché Local)",
    "engine.provisions.title": "Provisions (Caché Local)",
    "engine.files.title": "Archivos (Caché Local)",
    "onboarding.title": "Onboarding",
    "health.scoreTitle": "Puntuación de Salud",
    "health.factorsTitle": "Factores",
    "health.trendTitle": "Tendencia (30 días)",
    "status.title": "Estado del Sistema",
    "status.workers": "Workers",
    "status.mariadb": "MariaDB",
    "settings.general": "General",
    "settings.erpTest": "Test de ERP",
    "settings.systemLogs": "Logs del Sistema",
    "hostServices.title": "Servicios del Host (On-Premise)",
    "hostServices.config": "Configuración",
    "hostServices.configApplied": "Configuración aplicada",
    "hostServices.apply": "Aplicar",
    "hostServices.restart": "Reiniciar",
    "hostServices.restarting": "Reiniciando...",
    "hostServices.restartSuccess": "Servicio reiniciado",
    "hostServices.restartError": "Error al reiniciar servicio",
    "hostServices.advanced": "Avanzado",
    "hostServices.vendorClasses": "Clases de Vendor",
    "hostServices.testClassification": "Probar clasificación",
    "hostServices.matchType": "Tipo de match",
    "hostServices.matchValue": "Valor de match",
    "hostServices.urlMode": "Modo URL",
    "hostServices.protocol": "Protocolo",
    "hostServices.acsUrl": "ACS URL",
    "hostServices.simulate": "Simular",
    "hostServices.matchedClass": "Clase encontrada",
    "hostServices.wizardCompleted": "Wizard completado",
    "hostServices.configLoading": "Cargando config...",
    "hostServices.wizardApplied": "Servicios aplicados",
    "hostServices.wizardApplyPartial": "Aplicado con fallos parciales",
    "hostServices.wizardApplyError": "Error al aplicar configuración",
    "hostServices.parentInterface": "Interfaz padre",
    "hostServices.unbound.forwarders": "Forwarders (separados por comas)",
    "hostServices.unbound.forwardersPlaceholder": "8.8.8.8, 8.8.4.4",
    "hostServices.unbound.port": "Puerto",
    "hostServices.unbound.generate": "Generar config",
    "hostServices.unbound.noInterfacesSelected": "Ninguna interfaz seleccionada",
    "hostServices.unbound.interfaceListHelp": "Las DNS views se generan automáticamente para las interfaces seleccionadas:",
    "hostServices.unbound.derivedAcls": "ACLs derivadas de las interfaces:",
    "hostServices.unbound.parsedAcls": "ACLs detectadas en la configuración actual (solo lectura):",
    "hostServices.unbound.openResolverWarning": "Resolver abierto detectado: access-control 0.0.0.0/0 allow o ::/0 allow encontrado. Elimínelo para evitar recursión DNS abierta.",
    "auth.passkeyTitle": "Passkeys (WebAuthn)",
    "auth.registerChallengeError": "Error al obtener desafío de registro",
    "auth.registerVerificationError": "Error en la verificación del registro",
    "auth.registerPasskeyError": "Error al registrar passkey",
    "auth.registerPasskeySuccess": "Passkey registrada correctamente",
    "auth.registeringPasskey": "Registrando...",
    "auth.registerPasskeyButton": "Registrar passkey",
    "auth.passkeyIspOnly": "El registro de passkeys solo está disponible para operadores ISP autenticados.",
    "engRemote.consoleInfo": "Terminal SSH interactivo via xterm.js",
    "engRemote.tr069Info": "Query directa TR-069 para o GenieACS",
    "nodeSelector.local": "Local",
    "nodeSelector.selectNode": "Seleccionar Nodo",
    "nodeSelector.remoteNodes": "Nodos Remotos",

    // Task 14 — Clients / Contracts / Billing Plans pages
    "billing.clients.subtitle": "Registro de clientes del proveedor",
    "billing.clients.newClient": "Nuevo cliente",
    "billing.clients.searchPlaceholder": "Buscar por nombre o documento",
    "billing.clients.clearSearch": "Limpiar búsqueda",
    "billing.clients.colName": "Nombre",
    "billing.clients.colDocument": "Documento",
    "billing.clients.colEmail": "Correo",
    "billing.clients.colPhone": "Teléfono",
    "billing.clients.colOrigin": "Origen",
    "billing.clients.colCreatedAt": "Creado el",
    "billing.clients.colActions": "Acciones",
    "billing.clients.originControl": "RC Control",
    "billing.clients.originLocal": "Local",
    "billing.clients.emptyList": "No hay clientes registrados.",
    "billing.clients.noResults": "No se encontraron clientes para la búsqueda.",
    "billing.clients.loadMore": "Cargar más",
    "billing.clients.loading": "Cargando clientes...",
    "billing.clients.showingCount": "Mostrando {current} de {total} clientes",
    "billing.clients.dialogCreateTitle": "Nuevo cliente",
    "billing.clients.dialogEditTitle": "Editar cliente",
    "billing.clients.dialogDescription": "Busque el cliente en RC Control o complete los datos manualmente.",
    "billing.clients.controlSearchTitle": "Buscar en RC Control",
    "billing.clients.controlSearchHelp": "La búsqueda consulta RC Control por el nombre del suscriptor. Si el servicio no está disponible, el registro manual sigue habilitado.",
    "billing.clients.controlSearchPlaceholder": "Nombre del suscriptor en RC Control",
    "billing.clients.controlSearchButton": "Buscar",
    "billing.clients.controlSearchSearching": "Buscando...",
    "billing.clients.controlSearchNoResults": "No se encontraron suscriptores en RC Control.",
    "billing.clients.controlSearchSelect": "Usar este cliente",
    "billing.clients.fieldName": "Nombre",
    "billing.clients.fieldNamePlaceholder": "Nombre completo o razón social",
    "billing.clients.fieldDocument": "Documento",
    "billing.clients.fieldDocumentPlaceholder": "Documento de identidad o fiscal",
    "billing.clients.fieldEmail": "Correo",
    "billing.clients.fieldEmailPlaceholder": "contacto@proveedor.com",
    "billing.clients.fieldPhone": "Teléfono",
    "billing.clients.fieldPhonePlaceholder": "+00 000 000 000",
    "billing.clients.fieldControlId": "ID en RC Control",
    "billing.clients.fieldControlIdHelp": "Se completa automáticamente cuando el cliente proviene de RC Control.",
    "billing.clients.save": "Guardar",
    "billing.clients.saving": "Guardando...",
    "billing.clients.cancel": "Cancelar",
    "billing.clients.editButton": "Editar",
    "billing.clients.deleteButton": "Eliminar",
    "billing.clients.deleteConfirmTitle": "Eliminar cliente",
    "billing.clients.deleteConfirmMessage": "¿Eliminar el cliente {name}? Esta acción no se puede deshacer.",
    "billing.clients.deleteConfirmLabel": "Eliminar",
    "billing.clients.cancelLabel": "Cancelar",
    "billing.clients.duplicateDocumentError": "Ya existe un cliente registrado con este documento.",
    "billing.clients.duplicateControlEntityError": "Este cliente de RC Control ya está vinculado a otro registro.",
    "billing.clients.clientHasContractsError": "Este cliente tiene contratos y no se puede eliminar.",
    "billing.clients.validationNameRequired": "Indique el nombre del cliente.",
    "billing.clients.validationDocumentRequired": "Indique el documento del cliente.",
    "billing.clients.fieldControlIdPlaceholder": "Ej.: 1045",
    "billing.clients.controlSearchUnavailable": "La búsqueda en RC Control no está disponible en este momento. Registre el cliente manualmente.",
    "billing.clients.genericError": "No fue posible completar la operación. Inténtelo de nuevo.",
    "billing.contracts.subtitle": "Contratos de suscriptores, estado e historial de facturación",
    "billing.contracts.newContract": "Nuevo contrato",
    "billing.contracts.filterClient": "Cliente",
    "billing.contracts.filterClientAll": "Todos los clientes",
    "billing.contracts.filterStatus": "Estado",
    "billing.contracts.filterStatusAll": "Todos los estados",
    "billing.contracts.statusActive": "Activo",
    "billing.contracts.statusWarning": "Atención",
    "billing.contracts.statusBlocked": "Bloqueado",
    "billing.contracts.statusRevoked": "Revocado",
    "billing.contracts.colId": "ID",
    "billing.contracts.colClient": "Cliente",
    "billing.contracts.colPlan": "Plan",
    "billing.contracts.colStatus": "Estado",
    "billing.contracts.colStartDate": "Inicio",
    "billing.contracts.colEndDate": "Fin",
    "billing.contracts.colActions": "Acciones",
    "billing.contracts.emptyList": "No se encontraron contratos.",
    "billing.contracts.loading": "Cargando contratos...",
    "billing.contracts.actionDetails": "Detalles",
    "billing.contracts.actionEdit": "Editar",
    "billing.contracts.actionDelete": "Eliminar",
    "billing.contracts.transitionTitle": "Cambiar estado del contrato",
    "billing.contracts.transitionDescription": "Indique el motivo de la transición y, si corresponde, el ticket relacionado.",
    "billing.contracts.transitionReason": "Motivo",
    "billing.contracts.transitionReasonPlaceholder": "Describa el motivo del cambio de estado",
    "billing.contracts.transitionTicket": "Ticket",
    "billing.contracts.transitionTicketPlaceholder": "Número de ticket (opcional)",
    "billing.contracts.transitionConfirm": "Confirmar transición",
    "billing.contracts.transitionCancel": "Cancelar",
    "billing.contracts.dialogCreateTitle": "Nuevo contrato",
    "billing.contracts.dialogEditTitle": "Editar contrato",
    "billing.contracts.dialogDescription": "Indique cliente, plan y vigencia del contrato.",
    "billing.contracts.fieldClient": "Cliente",
    "billing.contracts.fieldClientSelect": "Seleccione el cliente",
    "billing.contracts.fieldPlan": "Plan",
    "billing.contracts.fieldPlanSelect": "Seleccione el plan",
    "billing.contracts.fieldStartDate": "Fecha de inicio",
    "billing.contracts.fieldEndDate": "Fecha de fin",
    "billing.contracts.fieldNotes": "Observaciones",
    "billing.contracts.fieldNotesPlaceholder": "Observaciones del contrato (opcional)",
    "billing.contracts.save": "Guardar",
    "billing.contracts.saving": "Guardando...",
    "billing.contracts.cancel": "Cancelar",
    "billing.contracts.deleteConfirmTitle": "Eliminar contrato",
    "billing.contracts.deleteConfirmMessage": "¿Eliminar el contrato {id}? Esta acción no se puede deshacer.",
    "billing.contracts.deleteConfirmLabel": "Eliminar",
    "billing.contracts.cancelLabel": "Cancelar",
    "billing.contracts.contractHasEnginesError": "Este contrato tiene engines vinculadas y no se puede eliminar.",
    "billing.contracts.contractDeleteBlockedError": "Eliminación bloqueada: quite la vinculación de las engines antes de eliminar el contrato.",
    "billing.contracts.invalidTransitionError": "Transición de estado no permitida para este contrato.",
    "billing.contracts.endDateBeforeStartDateError": "La fecha de fin no puede ser anterior a la fecha de inicio.",
    "billing.contracts.validationRequiredFields": "Complete todos los campos obligatorios.",
    "billing.contracts.detailsTitle": "Detalles del contrato",
    "billing.contracts.detailsSubtitle": "Engines vinculadas e historial de facturación",
    "billing.contracts.enginesSectionTitle": "Engines vinculadas",
    "billing.contracts.noEnginesLinked": "Ninguna engine vinculada a este contrato.",
    "billing.contracts.engineNodeId": "Engine",
    "billing.contracts.engineStatus": "Estado",
    "billing.contracts.engineBillingStatus": "Facturación",
    "billing.contracts.periodsSectionTitle": "Historial de facturación",
    "billing.contracts.periodsTruncated": "Historial parcial: la lectura de períodos está limitada a 1000 registros. Puede que falten los períodos más antiguos.",
    "billing.contracts.noPeriodsRecorded": "Ningún período de facturación registrado.",
    "billing.contracts.periodRange": "Período",
    "billing.contracts.periodCpeDays": "CPE-días",
    "billing.contracts.periodDueDate": "Vencimiento",
    "billing.contracts.periodAmount": "Importe",
    "billing.contracts.periodStatus": "Estado",
    "billing.contracts.periodStatusOpen": "Abierto",
    "billing.contracts.periodStatusPaid": "Pagado",
    "billing.contracts.registerPaymentAction": "Registrar pago",
    "billing.contracts.paymentRestoredSuccess": "Pago registrado y contrato restaurado a activo.",
    "billing.contracts.paymentRecordedNotRestored": "Pago registrado. No se aplicó la reactivación de los suscriptores.",
    "billing.contracts.paymentPeriodNotUpdated": "Pago registrado, pero el período de facturación no fue actualizado. Revise el historial antes de repetir la operación.",
    "billing.contracts.periodStatusUnknown": "Desconocido",
    "billing.contracts.showingCount": "Mostrando {current} de {total} contratos",
    "billing.contracts.loadMore": "Cargar más",
    "billing.contracts.dropdownTruncated": "… y {hidden} más sin mostrar. Refine la búsqueda para localizar el resto.",
    "billing.contracts.closeDetails": "Cerrar detalles",
    "billing.contracts.genericError": "No fue posible completar la operación. Inténtelo de nuevo.",
    "billing.plans.subtitle": "Planes de facturación y tramos de precio por consumo",
    "billing.plans.newPlan": "Nuevo plan",
    "billing.plans.colName": "Nombre",
    "billing.plans.colMetric": "Métrica",
    "billing.plans.colPeriodType": "Periodicidad",
    "billing.plans.colTiersSummary": "Tramos de precio",
    "billing.plans.colDefault": "Predeterminado",
    "billing.plans.colCreatedAt": "Creado el",
    "billing.plans.colActions": "Acciones",
    "billing.plans.defaultBadge": "Predeterminado",
    "billing.plans.emptyList": "No hay planes de facturación registrados.",
    "billing.plans.loading": "Cargando planes...",
    "billing.plans.actionEdit": "Editar",
    "billing.plans.actionDelete": "Eliminar",
    "billing.plans.dialogCreateTitle": "Nuevo plan",
    "billing.plans.dialogEditTitle": "Editar plan",
    "billing.plans.dialogDescription": "Defina la métrica y los tramos de precio del plan.",
    "billing.plans.fieldName": "Nombre",
    "billing.plans.fieldNamePlaceholder": "Ej.: Plan 300 Mbps",
    "billing.plans.fieldMetric": "Métrica de facturación",
    "billing.plans.fieldDefault": "Definir como predeterminado",
    "billing.plans.fieldDefaultHelp": "El plan predeterminado se usa cuando no se indica ningún plan.",
    "billing.plans.tiersSectionTitle": "Tramos de precio",
    "billing.plans.tiersSectionHelp": "Indique tramos crecientes. El último tramo puede quedar sin límite (ilimitado).",
    "billing.plans.tierColIndex": "#",
    "billing.plans.tierColUpTo": "Hasta",
    "billing.plans.tierColPrice": "Precio",
    "billing.plans.tierColActions": "Acciones",
    "billing.plans.tierUpToUnlimited": "Ilimitado",
    "billing.plans.tierUpToPlaceholder": "Vacío = ilimitado",
    "billing.plans.tierPricePlaceholder": "0,00",
    "billing.plans.addTier": "Añadir tramo",
    "billing.plans.removeTier": "Quitar tramo",
    "billing.plans.save": "Guardar",
    "billing.plans.saving": "Guardando...",
    "billing.plans.cancel": "Cancelar",
    "billing.plans.deleteConfirmTitle": "Eliminar plan",
    "billing.plans.deleteConfirmMessage": "¿Eliminar el plan seleccionado? Esta acción no se puede deshacer.",
    "billing.plans.deleteConfirmLabel": "Eliminar",
    "billing.plans.cancelLabel": "Cancelar",
    "billing.plans.planHasContractsError": "Este plan está vinculado a contratos y no se puede eliminar.",
    "billing.plans.validationNameRequired": "Indique el nombre del plan.",
    "billing.plans.validationMinTiers": "Indique al menos un tramo de precio.",
    "billing.plans.validationMaxTiers": "Una tabla de precios admite como máximo 20 tramos.",
    "billing.plans.validationNullOnlyOnLastTier": "Solo el último tramo puede quedar sin límite.",
    "billing.plans.validationPositiveIntegerUpTo": "El límite del tramo debe ser un número entero mayor o igual a cero.",
    "billing.plans.validationStrictlyAscending": "Los tramos deben ser estrictamente crecientes.",
    "billing.plans.validationPricePositive": "El precio del tramo debe ser mayor o igual a cero.",
    "billing.plans.validationPriceFormat": "Indique un decimal simple (ej.: 12.50), sin exponente ni notación hexadecimal.",
    "billing.plans.validationPriceCeiling": "El precio del tramo debe ser como máximo 99999999.99.",
    "billing.plans.yes": "Sí",
    "billing.plans.fieldMetricPlaceholder": "cpe-days",
    "billing.plans.tierSummaryUpTo": "hasta {up_to} — {price}",
    "billing.plans.tierSummaryUnlimited": "ilimitado — {price}",
    "billing.plans.genericError": "No fue posible completar la operación. Inténtelo de nuevo.",
    "billing.errValidation": "Datos inválidos:",
    "billing.errDuplicateDocument": "Ya existe un registro con este documento o vínculo.",
    "billing.errClientHasContracts": "Este cliente tiene contratos y no puede eliminarse.",
    "billing.errPlanHasContracts": "Este plan está vinculado a contratos y no puede eliminarse.",
    "billing.errContractHasEngines": "Este contrato tiene engines vinculadas y no puede eliminarse ni bloquearse.",
    "billing.errInvalidTransition": "Transición de estado no permitida para este contrato.",
    "billing.errContractNotFound": "Contrato no encontrado.",
    "billing.errContractNotActive": "El contrato no está activo para esta operación.",
    "billing.errContractDeleteBlocked": "Este contrato tiene facturación vinculada (registros de facturación) y no puede eliminarse.",
    "billing.errorLoading": "No fue posible cargar los datos.",
    "billing.retry": "Reintentar",
    "billing.errGeneric": "No fue posible completar la operación. Inténtelo de nuevo.",
    "billing.commonValidationError": "valor inválido",

  },
  "en": {
    "common.logout": "Logout",
    "common.language": "Language",
    "common.backToCore": "Back to Core",
    "common.toggleMenu": "Toggle menu",
    "common.toggleSidebar": "Toggle sidebar",
    "common.breadcrumb": "Breadcrumb",
    "common.themeSwitchDark": "Switch to dark mode",
    "common.themeSwitchLight": "Switch to light mode",
    "common.save": "Save",
    "common.saving": "Saving...",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.delete": "Delete",
    "createEngine.apiTokenPendingHint": "The node's API Token will be shown once install token generation completes.",
    "common.edit": "Edit",
    "common.create": "Create",
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.search": "Search",
    "common.actions": "Actions",
    "common.status": "Status",
    "common.name": "Name",
    "common.description": "Description",
    "common.active": "Active",
    "common.inactive": "Inactive",
    "common.tryAgain": "Try again",
    "common.back": "Back",
    "common.version": "Version",
    "common.updatedAt": "Updated at",
    "common.noData": "No data available",
    "common.noResults": "No results found",
    "common.saveError": "Failed to save.",
    "common.deleteConfirm": "This action cannot be undone.",
    "common.confirm": "Confirm",
    "common.continue": "Continue",
    "common.region": "Region",
    "common.script": "Script",
    "common.value": "Value",
    "common.email": "Email",
    "common.type": "Type",
    "common.download": "Download",
    "common.previous": "Previous",
    "common.next": "Next",
    "common.page": "Page",
    "common.showing": "Showing",
    "common.of": "of",
    "common.suspend": "Suspend",
    "common.activate": "Activate",
    "common.enabled": "Enabled",
    "common.disabled": "Disabled",
    "common.all": "All",
    "common.filter": "Filter",
    "common.copy": "Copy",
    "common.copied": "Copied",
    "common.nameRequired": "Name is required.",
    "common.suspended": "Suspended",
    "common.success": "Success",
    "common.warning": "Warning",
    "common.danger": "Danger",
    "common.info": "Information",
    "common.accessDenied": "Access denied",

    "syncstatus.title": "Sync Status",
    "syncstatus.subtitle": "Observability of the engineering sync cycle between Engine and Core",
    "syncstatus.colEngine": "Engine",
    "syncstatus.colLastCycle": "Last Cycle",
    "syncstatus.colVersion": "Version",
    "syncstatus.colStatus": "Status",
    "syncstatus.colPushed": "Pushed",
    "syncstatus.colDeleted": "Deleted",
    "syncstatus.colErrors": "Errors",
    "syncstatus.colDuration": "Duration",
    "syncstatus.neverSynced": "Never synced",
    "syncstatus.historyTitle": "Sync History",
    "syncstatus.lastError": "Last Error",
    "syncstatus.noHistory": "No history available",
    "syncstatus.statusOk": "OK",
    "syncstatus.statusPushError": "Push Error",
    "syncstatus.statusError": "Error",
    "syncstatus.loadingHistory": "Loading history...",
    "syncstatus.relativeNow": "Just now",
    "syncstatus.relativeSeconds": "{n}s ago",
    "syncstatus.relativeMinutes": "{n}min ago",
    "syncstatus.relativeHours": "{n}h ago",
    "syncstatus.relativeDays": "{n}d ago",
    "syncstatus.pushedDel": "Pushed: {pushed} / Del: {deleted}",
    "syncstatus.errorsCount": "Errors: {n}",
    "syncstatus.noErrorDetail": "No detail",

    "dynvarsoverride.title": "Configurable Variables",
    "dynvarsoverride.description": "Customize global variable values for this Engine Node",
    "dynvarsoverride.tableKey": "Key",
    "dynvarsoverride.tableName": "Name",
    "dynvarsoverride.tableGlobal": "Global Value",
    "dynvarsoverride.tableLocal": "Local Value",
    "dynvarsoverride.tableStatus": "Status",
    "dynvarsoverride.tableUsage": "Usage",
    "dynvarsoverride.statusDefault": "Using Core default",
    "dynvarsoverride.statusOverridden": "Overridden locally",
    "dynvarsoverride.empty": "No variables configurable by the Engine.",
    "dynvarsoverride.emptyDesc": "The Core has not defined variables that can be changed locally.",
    "dynvarsoverride.editTitle": "Edit Local Value",
    "dynvarsoverride.editDesc": "Change the value of this variable for this Engine.",
    "dynvarsoverride.formValue": "Value",
    "dynvarsoverride.removeOverride": "Remove override",
    "dynvarsoverride.removeConfirm": "Restore global value of",
    "dynvarsoverride.save": "Save Change",
    "dynvarsoverride.error": "Invalid value for the data type.",
    "dynvarsoverride.statusBlockedByCore": "Blocked by Core",
    "dynvarsoverride.statusOrphaned": "Variable removed in Core",
    "dynvarsoverride.orphanedHelp": "This variable no longer exists in the Core. The local override is inactive — remove it.",
    "dynvarsoverride.deleteOrphanedOverride": "Remove override",
    "dynvarsoverride.globalRef": "Core default:",
    "dynvarsoverride.reapplyEnqueued": "Saved. Reapplication scheduled for the affected devices.",
    "dynvarsoverride.reapplyDeduped": "Reapplication already in progress — value saved.",
    "dynvarsoverride.reapplyCooldown": "Reapplication on hold (cooldown) — value saved.",
    "dynvarsoverride.reapplyFailed": "Value saved, but reapplication to the affected devices failed.",
    "route.dynVarOverrides": "Configurable Variables",
    "sidebar.opsDynVarOverrides": "Configurable Variables",
    "route.nocDashboard": "NOC Dashboard",
    "route.engineRegistry": "Engine Registry",
    "route.engVirtualParams": "Virtual Parameters",
    "route.engProvisions": "Provisions",
    "route.engPresets": "Presets",
    "route.engDynamicVars": "Dynamic Variables",
    "route.engCwmpConfigs": "CWMP Configs",
    "route.engVendorMappings": "Vendor Mappings",
    "route.engCpeModels": "CPE Models",
    "route.engFirmware": "Firmware",
    "route.engFiles": "Files",
    "route.engTr369Controllers": "TR-369 Controllers",
    "route.engOlt": "OLT",
    "route.adminUsers": "Users",
    "route.adminSettings": "Settings",
    "route.adminBackups": "Backups",
    "route.adminBilling": "Billing",
    "route.adminBillingPlans": "Billing Plans",
    "route.adminBillingClients": "Clients",
    "route.adminBillingContracts": "Contracts",
    "route.defaultTitle": "RC ACS",
    "route.dashboard": "Dashboard",
    "route.devices": "Devices",
    "route.oltManagement": "OLT Management",
    "route.reports": "Reports",
    "route.operations": "Operations",
    "route.faults": "Faults",
    "route.syncDrift": "Sync Drift",
    "route.operators": "Operators",
    "route.engineDefault": "Engine Management",
    "route.profile": "Profile",
    "route.olts": "OLTs",

    "sidebar.dashboard": "Dashboard",
    "sidebar.engineRegistry": "Engine Registry",
    "sidebar.engineering": "Engineering",
    "sidebar.engTr069": "TR-069 (GenieACS)",
    "sidebar.engTr369": "TR-369 (USP)",
    "sidebar.engDevices": "Devices",
    "sidebar.engOlt": "OLT",
    "sidebar.engOltManagement": "OLT Management",
    "sidebar.engDynamicVars": "Dynamic Variables",
    "sidebar.engSyncStatus": "Sync Status",
    "sidebar.engVirtualParams": "Virtual Parameters",
    "sidebar.engProvisions": "Provisions",
    "sidebar.engPresets": "Presets",
    "sidebar.engCwmpConfigs": "CWMP Configs",
    "sidebar.engFiles": "Files",
    "sidebar.engTr369Controllers": "Controllers",
    "sidebar.engVendorMappings": "Vendor Mappings",
    "sidebar.engCpeModels": "CPE Models",
    "sidebar.engFirmware": "Firmware",
    "sidebar.admin": "Administration",
    "sidebar.adminUsers": "Users",
    "sidebar.adminSettings": "Settings",
    "sidebar.adminBackups": "Backups",
    "sidebar.adminBilling": "Billing",
    "sidebar.adminBillingPlans": "Plans",
    "sidebar.adminBillingClients": "Clients",
    "sidebar.adminBillingContracts": "Contracts",
    "sidebar.devices": "Devices",
    "sidebar.olts": "OLTs",
    "sidebar.opsEngRemote": "Eng. Remote",
    "sidebar.opsFiles": "Files",
    "sidebar.opsHealth": "Health",
    "sidebar.opsHostServices": "Host Services",
    "sidebar.opsOnboarding": "Onboarding",
    "sidebar.opsPresets": "Presets",
    "sidebar.opsProvisions": "Provisions",
    "sidebar.opsSettings": "Settings",
    "sidebar.opsStatus": "Status",
    "sidebar.opsOperators": "Operators",
    "sidebar.opsVirtualParams": "Virtual Parameters",
    "sidebar.reports": "Reports",
    "sidebar.operations": "Operations",
    "sidebar.faults": "Faults",
    "sidebar.syncDrift": "Sync Drift",
    "login.coreTitle": "Sign in to access the Core Management Platform",
    "login.coreSubtitle": "Sign in with your RC Control account to access the dashboard",
    "login.loginButton": "Sign in with RC Control",
    "login.redirectNotice": "You will be redirected to the RC Control authentication service.",
    "login.acs": "RC ACS",
    "login.engineTitle": "Access the local management dashboard",
    "login.engineNode": "Engine Node",
    "login.localLogin": "Local login",
    "login.localLoginPasskey": "Local login (Passkey)",
    "login.backToCore": "Back to Core",
    "login.authenticating": "Authenticating...",
    "login.verifying": "Verifying...",
    "login.ispTab": "ISP Operator",
    "login.passkeyTab": "Passkey",
    "login.username": "Username",
    "login.password": "Password",
    "login.ispLoginButton": "Sign in",
    "login.ispLoginError": "Invalid username or password.",
    "login.ispLoginFailed": "Authentication failed. Please try again.",
    "login.passkeyFailed": "Could not sign in with your passkey. Please try again.",

    "unauthorized.title": "Access denied",
    "unauthorized.message": "You do not have permission to access this page.",
    "unauthorized.backToDashboard": "Back to dashboard",
    "unauthorized.signOut": "Sign out",
    "login.passkeyCancelled": "Passkey sign-in cancelled.",
    "login.passkeyUnsupported": "This browser doesn't support passkeys. Use username and password instead.",
    "login.sessionExpired": "Session expired. Please sign in again.",
    "login.ssoInvalidToken": "Access token invalid or expired.",
    "login.localTab": "Local User",
    "login.localUsername": "Username",
    "login.localPassword": "Password",
    "login.localLoginButton": "Sign in",
    "login.localLoginError": "Invalid username or password.",
    "login.invalidCredentials": "Invalid username or password.",
    "login.rateLimited": "Too many login attempts. Please try again in a few minutes.",
    "login.coreOffline": "Core offline — limited functionality. The SSO session cannot be renewed.",
    "login.serviceUnavailable": "Service unavailable. Please try again later.",
    "login.connectionError": "Connection error. Check your network and try again.",
    "login.loginUnavailableNode": "Login is not available on this node.",
    "login.directLoginUnavailable": "Direct login is unavailable on this node. Sign in via Core SSO.",
    "login.loginWithPasskey": "Sign in with Passkey",
    "login.or": "or",
    "login.passkeyTooltip": "Authentication with security key or biometrics",

    "isp.role.isp_admin": "ISP Administrator",
    "isp.role.isp_operator": "ISP Operator",
    "isp.role.isp_technician": "Technician",

    "lang.ptBR": "Portuguese (Brazil)",
    "lang.ptPT": "Portuguese (Portugal)",
    "lang.es": "Spanish",
    "lang.en": "English",
    "noc.title": "NOC Dashboard",
    "engineRegistry.title": "Engine Registry",
    "engineRegistry.description": "Manage all Engine Nodes across regions and clients",
    "engineRegistry.newEngine": "New Engine",
    "engineRegistry.searchPlaceholder": "Search engines...",
    "engineRegistry.allStatuses": "All Statuses",
    "engineRegistry.statusOnline": "Online",
    "engineRegistry.statusOffline": "Offline",
    "engineRegistry.statusDegraded": "Degraded",
    "engineRegistry.statusSuspended": "Suspended",
    "engineRegistry.cloud": "Cloud",
    "engineRegistry.loading": "Loading engine nodes...",
    "engineRegistry.empty": "No engine node found",
    "engineRegistry.createFirst": "Create your first Engine Node",
    "engineRegistry.tableName": "Name",
    "engineRegistry.tableClient": "Client",
    "engineRegistry.tableRegion": "Region",
    "engineRegistry.tableHealth": "Health",
    "engineRegistry.tableCpes": "CPEs",
    "engineRegistry.tableTier": "Tier",
    "engineRegistry.tableHeartbeat": "Last Heartbeat",
    "engineRegistry.never": "Never",
    "engineRegistry.justNow": "Just now",
    "engineRegistry.secondsAgo": "s ago",
    "engineRegistry.minutesAgo": "min ago",
    "engineRegistry.hoursAgo": "h ago",
    "engineRegistry.daysAgo": "d ago",
    "engineRegistry.edit": "Edit",
    "engineRegistry.activate": "Activate",
    "engineRegistry.suspend": "Suspend",
    "engineRegistry.delete": "Delete",
    "engineRegistry.deleteTitle": "Delete Engine Node",
    "engineRegistry.deleteMessage": "Are you sure you want to delete",
    "registry.errorDelete": "Failed to delete the node. Check its dependencies and try again.",
    "registry.errorConflict": "Could not delete the node: dependencies are blocking the deletion.",
    "registry.errorForbidden": "You do not have administrator permission to delete this node.",
    "registry.nameMismatch": "The typed name does not match the node name.",
    "engineDetail.notFound": "Engine Node Not Found",
    "engineDetail.notFoundMsg": "The node with the specified ID does not exist.",
    "engineDetail.backToRegistry": "Back to Registry",
    "engineDetail.registryBreadcrumb": "Engine Registry",
    "engineDetail.healthIndex": "Health Index",
    "engineDetail.activeCpes": "Active CPEs",
    "engineDetail.licenseTier": "License Tier",
    "engineDetail.maxCpes": "Max CPEs",
    "engineDetail.heartbeat": "Heartbeat",
    "engineDetail.loading": "Loading engine node...",
    "engineDetail.backups": "Backups",
    "engineDetail.remoteLogs": "Remote Logs",
    "engineDetail.healthHistory": "Health History",
    "vp.title": "Virtual Parameters",
    "vp.description": "Manage virtual parameter scripts for CPE engineering",
    "vp.newParam": "New Parameter",
    "vp.loading": "Loading virtual parameters...",
    "vp.error": "Error loading virtual parameters.",
    "vp.empty": "No virtual parameter found.",
    "vp.createFirst": "Create your first Virtual Parameter",
    "vp.tableName": "Name",
    "vp.tableDescription": "Description",
    "vp.tableScript": "Script",
    "vp.tableVersion": "Version",
    "vp.tableUpdatedAt": "Updated at",
    "vp.tableActions": "Actions",
    "vp.editTitle": "Edit Virtual Parameter",
    "vp.newTitle": "New Virtual Parameter",
    "vp.editDesc": "Edit the virtual parameter fields.",
    "vp.newDesc": "Fill in the fields to create a new virtual parameter.",
    "vp.formName": "Name *",
    "vp.formNamePlaceholder": "Ex: Average SNR",
    "vp.nameRequired": "Name is required.",
    "vp.formDesc": "Description",
    "vp.formDescPlaceholder": "Ex: Calculates the average SNR over the last 24h",
    "vp.formScript": "Script (JavaScript)",
    "vp.formScriptPlaceholder": "// Write the JavaScript script for the virtual parameter",
    "vp.validateScript": "Validate Script",
    "vp.scriptValid": "Valid script",
    "vp.scriptInvalid": "Invalid script",
    "vp.deleteTitle": "Delete Virtual Parameter",
    "vp.deleteMessage": "Are you sure you want to delete",
    "vp.versionHistory": "Version History",
    "vp.versionHistoryDesc": "Previous versions of the virtual parameter.",
    "vp.noVersions": "No previous versions found.",
    "vp.versionLabel": "Version",
    "vp.reason": "Reason",
    "provisions.title": "Provisions",
    "provisions.description": "Manage provision scripts for automatic CPE configuration",
    "provisions.newProvision": "New Provision",
    "provisions.loading": "Loading provisions...",
    "provisions.error": "Error loading provisions.",
    "provisions.empty": "No provision found.",
    "provisions.createFirst": "Create your first Provision",
    "provisions.tableName": "Name",
    "provisions.tableScript": "Script",
    "provisions.editTitle": "Edit Provision",
    "provisions.newTitle": "New Provision",
    "provisions.editDesc": "Edit the provision fields.",
    "provisions.newDesc": "Fill in the fields to create a new provision.",
    "provisions.formName": "Name *",
    "provisions.formNamePlaceholder": "Ex: Initial PPPoE Configuration",
    "provisions.formScript": "Script (JavaScript)",
    "provisions.formScriptPlaceholder": "// Write the JavaScript provision script",
    "provisions.validate": "Validate",
    "provisions.validScript": "Valid script",
    "provisions.invalidScript": "Invalid script",
    "provisions.deleteTitle": "Delete Provision",
    "provisions.deleteMessage": "Are you sure you want to delete",
    "provisions.versionHistoryDesc": "Previous versions of the provision.",
    "presets.title": "Presets",
    "presets.description": "Manage configuration presets for CPE provisioning",
    "presets.newPreset": "New Preset",
    "presets.loading": "Loading presets...",
    "presets.error": "Error loading presets.",
    "presets.empty": "No preset found.",
    "presets.createFirst": "Create your first Preset",
    "presets.tableName": "Name",
    "presets.tableConfig": "Config",
    "presets.editTitle": "Edit Preset",
    "presets.newTitle": "New Preset",
    "presets.editDesc": "Edit the preset fields.",
    "presets.newDesc": "Fill in the fields to create a new preset.",
    "presets.formName": "Name *",
    "presets.formNamePlaceholder": "Ex: Default WiFi Configuration",
    "presets.formConfig": "Config (JSON)",
    "presets.channel": "Channel",
    "presets.weight": "Weight",
    "presets.schedule": "Schedule",
    "presets.scheduleHelp": "Cron expression (e.g., 0 2 * * *)",
    "presets.events": "Events",
    "presets.precondition": "Precondition",
    "presets.preconditionHelp": "Boolean expression evaluated before applying the preset. E.g.: Tags.Provisioned. Supports GenieACS operators (AND, OR, NOT, IS NULL). Placeholders {{var}} are not supported here.",
    "presets.provisions": "Provisions",
    "presets.addProvision": "Add Provision",
    "presets.provisionArgs": "Arguments (JSON)",
    "presets.jsonInvalid": "Invalid JSON. Fix the syntax.",
    "presets.deleteTitle": "Delete Preset",
    "presets.deleteMessage": "Are you sure you want to delete",
    "presets.versionHistoryDesc": "Previous versions of the preset.",
    "dynvars.title": "Dynamic Variables",
    "dynvars.description": "Manage dynamic variables used in engineering scripts",
    "dynvars.newVar": "New Variable",
    "dynvars.loading": "Loading dynamic variables...",
    "dynvars.error": "Error loading dynamic variables.",
    "dynvars.empty": "No dynamic variable found.",
    "dynvars.createFirst": "Create your first Dynamic Variable",
    "dynvars.tableKey": "Key",
    "dynvars.tableName": "Name",
    "dynvars.tableDataType": "Data Type",
    "dynvars.tableDefault": "Default Value",
    "dynvars.editTitle": "Edit Dynamic Variable",
    "dynvars.newTitle": "New Dynamic Variable",
    "dynvars.editDesc": "Edit the dynamic variable fields.",
    "dynvars.newDesc": "Fill in the fields to create a new dynamic variable.",
    "dynvars.formKey": "Key *",
    "dynvars.formKeyPlaceholder": "e.g. cpe_user_web",
    "dynvars.formKeyHelper": "Identifier used inside provision and VP scripts as {{key}}. Synced to all Engines (lowercase and _ only).",
    "dynvars.keyRequired": "Key is required.",
    "dynvars.keyPatternError": "Key must start with a lowercase letter and contain only lowercase letters, digits, and underscores (_).",
    "dynvars.previewScript": "Provisions and Virtual Parameters will use:",
    "dynvars.formName": "Name *",
    "dynvars.formNamePlaceholder": "Ex: CPE Web User",
    "dynvars.formNameHelper": "Friendly display name shown only in the dashboard table. Never appears in script code.",
    "dynvars.formDescHelper": "Internal memo for the team on the purpose or scope of this variable. Not sent to scripts.",
    "dynvars.formDescPlaceholder": "Ex: Web access credentials for Huawei and ZTE ONTs",
    "dynvars.formSourceType": "Source",
    "dynvars.formSourceTypeHelper": "Where the Engine retrieves the variable value at push time to GenieACS.",
    "dynvars.sourceHintStaticTitle": "Static Fixed Value",
    "dynvars.sourceHintStatic": "Fixed value. The Default Value field is the value used in scripts. Recommended for most cases (credentials, NTP server, timezone).",
    "dynvars.sourceHintCwmpTitle": "CWMP Support Notice",
    "dynvars.sourceHintCwmp": "Automatic resolution via CWMP config is not yet implemented in Engine — the system will fall back to Default Value.",
    "dynvars.sourceHintSystemTitle": "System Setting",
    "dynvars.sourceHintSystem": "Reads an Engine system setting. Expected format: setting:KEY (e.g. setting:admin.smtp).",
    "dynvars.sourceHintEnvTitle": "Environment Security Restriction",
    "dynvars.sourceHintEnv": "Reads an environment variable from the Engine host. For security, must start with DYNVAR_ (or be INFORM_INTERVAL). Names containing PASSWORD, SECRET, TOKEN, KEY, URI, or URL are blocked.",
    "dynvars.formSourceRef": "Source Reference",
    "dynvars.formSourceRefHelper": "Key or identifier of the external source to be queried by the Engine.",
    "dynvars.sourceRefPlaceholderCwmp": "e.g. cwmp:Device.ManagementServer.URL",
    "dynvars.sourceRefPlaceholderSetting": "e.g. setting:admin.smtp",
    "dynvars.sourceRefPlaceholderEnv": "e.g. env:DYNVAR_NTP_SERVER",
    "dynvars.formDataType": "Data Type",
    "dynvars.formDataTypeHelper": "Defines how the value is validated before push (fail-closed: invalid value for type prevents provision push).",
    "dynvars.dataTypeHintString": "Text: spliced as a safe quoted JavaScript string literal.",
    "dynvars.dataTypeHintBoolean": "Boolean: accepts only true or false.",
    "dynvars.dataTypeHintInt": "Integer: whole number without decimals (e.g. 600).",
    "dynvars.dataTypeHintJson": "JSON: syntactically valid JSON object or array (e.g. {\"option\": 1}).",
    "dynvars.dataTypeHintEnum": "Enum: strictly restricted to values defined in the options list.",
    "dynvars.formDefault": "Default Value",
    "dynvars.formDefaultHelper": "Value used in scripts (if Static) or fallback if source fails. If empty and source provides no value, provision is skipped.",
    "dynvars.placeholderDefaultInt": "e.g. 600",
    "dynvars.formEnumOptions": "Enum Options",
    "dynvars.formEnumOptionsPlaceholder": "e.g. bridge, router, pppoe",
    "dynvars.formEnumOptionsHint": "Comma-separated values.",
    "dynvars.formEnumOptionsHelper": "List of accepted values separated by comma. Any value outside this list is rejected.",
    "dynvars.formActive": "Active",
    "dynvars.formActiveHelper": "Inactive variables do not resolve. Provisions that depend on this variable are aborted during push.",
    "dynvars.formDefaultPlaceholder": "Default value (optional)",
    "dynvars.selectDefault": "Select default",
    "dynvars.emptyDesc": "Create parameterized variables to inject dynamic values into GenieACS provision and virtual parameter scripts without hardcoding credentials.",
    "dynvars.sourceType.static": "Static",
    "dynvars.sourceType.cwmp_config": "CWMP Config",
    "dynvars.sourceType.system_setting": "System Setting",
    "dynvars.sourceType.env_var": "Environment Variable",
    "dynvars.dataType.string": "String",
    "dynvars.dataType.boolean": "Boolean",
    "dynvars.dataType.int": "Integer",
    "dynvars.dataType.json": "JSON",
    "dynvars.dataType.enum": "Enum",
    "dynvars.deleteTitle": "Delete Dynamic Variable",
    "dynvars.deleteMessage": "Are you sure you want to delete",
    "dynvars.usageColumn": "Usage",
    "dynvars.usageFormat": "Used in: {provisions} provisions, {vps} VPs, {presets} presets",
    "dynvars.scope": "Scope",
    "dynvars.scopeGlobal": "Global",
    "dynvars.scopeConfigurable": "Configurable",
    "dynvars.scopeConfigurableEngine": "Configurable by Engine",
    "dynvars.scopeGlobalHelper": "Global: fixed value defined by Core; Engines only apply",
    "dynvars.scopeConfigurableHelper": "Configurable by Engine: Core sends the default value; each Engine can set its own value in its UI (applied to local scripts).",
    "dynvars.scopeToggleWarning": "Warning: existing overrides on Engines will become inactive (not removed)",

    "tr369.comingSoon": "TR-369 coming soon",
    "tr369.description": "TR-369 (USP/OpenUSP) controller management will be enabled once the Go controller implementation is complete.",
    "vendorMappings.title": "Vendor Mappings",
    "vendorMappings.description": "Manage vendor and model mappings for TR-069",
    "vendorMappings.newMapping": "New Mapping",
    "vendorMappings.loading": "Loading mappings...",
    "vendorMappings.error": "Error loading mappings.",
    "vendorMappings.empty": "No mapping found.",
    "vendorMappings.createFirst": "Create your first Mapping",
    "vendorMappings.tableVendor": "Vendor",
    "vendorMappings.tableModel": "Model",
    "vendorMappings.tablePath": "Path",
    "vendorMappings.editTitle": "Edit Mapping",
    "vendorMappings.newTitle": "New Mapping",
    "vendorMappings.editDesc": "Edit the mapping fields.",
    "vendorMappings.newDesc": "Fill in the fields to create a new vendor mapping.",
    "vendorMappings.formVendor": "Vendor *",
    "vendorMappings.formVendorPlaceholder": "Ex: Huawei",
    "vendorMappings.vendorRequired": "Vendor is required.",
    "vendorMappings.formModel": "Model *",
    "vendorMappings.formModelPlaceholder": "Ex: HG8245H",
    "vendorMappings.modelRequired": "Model is required.",
    "vendorMappings.formPath": "TR-069 Path",
    "vendorMappings.formPathPlaceholder": "Ex: InternetGatewayDevice.LANDevice.1.WLANConfiguration.1",
    "vendorMappings.formDescPlaceholder": "Ex: Mapping for Huawei GPON ONTs",
    "vendorMappings.deleteTitle": "Delete Mapping",
    "vendorMappings.deleteMessage": "Are you sure you want to delete",
    "cpeModels.title": "CPE Models",
    "cpeModels.description": "Manage customer equipment models (CPE)",
    "cpeModels.newModel": "New Model",
    "cpeModels.loading": "Loading CPE models...",
    "cpeModels.error": "Error loading CPE models.",
    "cpeModels.empty": "No CPE model found.",
    "cpeModels.createFirst": "Create your first CPE Model",
    "cpeModels.tableVendor": "Vendor",
    "cpeModels.tableModel": "Model",
    "cpeModels.tableFwVersion": "FW Version",
    "cpeModels.tableWifi": "WiFi",
    "cpeModels.tablePorts": "Ports",
    "cpeModels.editTitle": "Edit CPE Model",
    "cpeModels.newTitle": "New CPE Model",
    "cpeModels.editDesc": "Edit the CPE model fields.",
    "cpeModels.newDesc": "Fill in the fields to create a new CPE model.",
    "cpeModels.formFwVersion": "Firmware Version",
    "cpeModels.formFwPlaceholder": "Ex: V3R017C00S100",
    "cpeModels.formWifi": "WiFi Bands",
    "cpeModels.formWifiPlaceholder": "Ex: 2.4GHz, 5GHz",
    "cpeModels.formPorts": "Ports",
    "cpeModels.formPortsPlaceholder": "Ex: 4x GE, 2x POTS, 1x USB",
    "cpeModels.formDescPlaceholder": "Ex: GPON ONT for residential customers",
    "cpeModels.deleteTitle": "Delete CPE Model",
    "cpeModels.deleteMessage": "Are you sure you want to delete",
    "firmware.title": "Firmware",
    "firmware.description": "Manage firmware versions and deploy to engines",
    "firmware.newFirmware": "New Firmware",
    "firmware.loading": "Loading firmware...",
    "firmware.error": "Error loading firmware.",
    "firmware.empty": "No firmware found.",
    "firmware.createFirst": "Create your first Firmware",
    "firmware.tableName": "Name",
    "firmware.tableVersion": "Version",
    "firmware.tableModel": "Model",
    "firmware.tableSize": "Size",
    "firmware.editTitle": "Edit Firmware",
    "firmware.newTitle": "New Firmware",
    "firmware.editDesc": "Edit the firmware fields.",
    "firmware.newDesc": "Fill in the fields to register a new firmware.",
    "firmware.formVersion": "Version *",
    "firmware.formVersionPlaceholder": "Ex: 3.2.1",
    "firmware.versionRequired": "Version is required.",
    "firmware.formModel": "Model (ID)",
    "firmware.formModelPlaceholder": "Ex: 5",
    "firmware.formFile": "File",
    "firmware.uploadFuture": "File upload via API available in a future version.",
    "firmware.formDesc": "Description",
    "firmware.formDescPlaceholder": "Ex: Version release notes",
    "firmware.deleteTitle": "Delete Firmware",
    "firmware.deleteMessage": "Are you sure you want to delete",
    "firmware.deployTitle": "Firmware Deploy",
    "firmware.deployDesc": "Deploy to selected engines.",
    "firmware.deployEngines": "Engine UUIDs",
    "firmware.deployPlaceholder": "UUID1, UUID2, UUID3 (comma-separated)",
    "firmware.deploySeparator": "Separate multiple UUIDs with commas.",
    "firmware.deploying": "Deploying...",
    "firmware.deploy": "Deploy",
    "firmware.deployError": "Failed to deploy.",
    "firmware.deploySuccess": "Deploy started successfully!",
    "files.title": "Files",
    "files.description": "Manage firmware, configuration, and other resource files",
    "files.newFile": "New File",
    "files.loading": "Loading files...",
    "files.error": "Error loading files.",
    "files.empty": "No file found.",
    "files.createFirst": "Create your first File",
    "files.tableType": "Type",
    "files.tableSize": "Size",
    "files.fileTypeImage": "Image",
    "files.fileTypeText": "Text",
    "files.fileTypeBinary": "Binary",
    "files.editTitle": "Edit File",
    "files.newTitle": "New File",
    "files.editDesc": "Edit the file metadata.",
    "files.newDesc": "Fill in the fields to add a new file.",
    "files.formNamePlaceholder": "Ex: fw_hg8245h_v3.2.1.bin",
    "files.formMime": "Description / MIME Type",
    "files.formMimePlaceholder": "Ex: application/octet-stream",
    "files.deleteTitle": "Delete File",
    "files.deleteMessage": "Are you sure you want to delete",
    "users.title": "Users",
    "users.newUser": "New User",
    "users.searchPlaceholder": "Search by name or email...",
    "users.allRoles": "All roles",
    "users.roleDeveloper": "Developer",
    "users.roleAdmin": "Admin",
    "users.roleOperator": "Operator",
    "users.tableName": "Name",
    "users.tableEmail": "Email",
    "users.tableRole": "Role",
    "users.tableStatus": "Status",
    "users.tableActions": "Actions",
    "users.empty": "No user found",
    "users.deleteTitle": "Delete user",
    "users.deleteMessage": "Are you sure you want to delete this user? This action cannot be undone.",

    "operators.pageTitle": "Operators",
    "operators.newOperator": "New Operator",
    "operators.never": "Never",
    "operators.searchPlaceholder": "Search by name or email...",
    "operators.empty": "No operator found",
    "operators.tableUsername": "Username",
    "operators.tableFullName": "Name",
    "operators.tableEmail": "Email",
    "operators.tableRole": "Role",
    "operators.tableLastLogin": "Last login",
    "operators.tableStatus": "Status",
    "operators.tableActions": "Actions",
    "operators.statusActive": "Active",
    "operators.statusInactive": "Inactive",
    "operators.reactivate": "Reactivate",
    "operators.createTitle": "New Operator",
    "operators.password": "Password",
    "operators.showPassword": "Show password",
    "operators.hidePassword": "Hide password",
    "operators.deactivateButton": "Deactivate",
    "operators.deactivateTitle": "Deactivate operator?",
    "operators.deactivateMessage": "The operator \"{{name}}\" will be deactivated (soft delete).",
    "operators.deleteButton": "Delete",
    "operators.deleteTitle": "Delete operator?",
    "operators.deleteMessage": "Permanently delete {{name}}? This action cannot be undone and also removes the operator's passkeys.",
    "operators.setPasswordButton": "Set password",
    "operators.setPasswordTitle": "Set password",
    "operators.setPasswordFor": "Set password for {{name}}",
    "operators.currentPassword": "Current password",
    "operators.newPassword": "New password",
    "operators.confirmPassword": "Confirm new password",
    "operators.passwordMismatch": "Passwords do not match",

    "profile.changePassword.title": "Change your password",
    "profile.changePassword.description": "For security, you must set a new password before continuing.",
    "profile.changePassword.currentPassword": "Current password",
    "profile.changePassword.newPassword": "New password",
    "profile.changePassword.confirmPassword": "Confirm new password",
    "profile.changePassword.submit": "Save new password",
    "profile.changePassword.required": "Fill in all fields.",
    "profile.changePassword.mismatch": "Passwords do not match.",
    "profile.changePassword.weakPassword": "The new password must be at least 12 characters long, with an uppercase letter, a lowercase letter, a digit and a symbol.",
    "profile.changePassword.invalidCurrent": "Current password is incorrect.",
    "profile.changePassword.error": "The password could not be changed. Please try again.",

    "settings.title": "Settings",
    "backups.title": "Backups",
    "billing.title": "Billing",
    "profile.title": "Profile",
    "profile.name": "Name",
    "profile.email": "Email",
    "profile.role": "Role",
    "profile.roleDeveloper": "Developer",
    "profile.roleAdmin": "Administrator",
    "profile.roleOperator": "Operator",
    "profile.sessionExpires": "Session expires in",
    "profile.authorizedEngines": "Authorized engines",
    "profile.none": "None",
    "profile.notAuthenticated": "Not authenticated",
    "profile.authMethod": "Authentication method",
    "profile.authWebauthn": "Local WebAuthn",
    "profile.authSso": "SSO (Core)",
    "profile.authPassword": "Local password",
    "profile.session": "Session",
    "profile.renewSession": "Renew session",
    "profile.localSessionNote": "Local sessions cannot be renewed (no connection to the Core).",
    "profile.coreConnection": "Core connection",
    "profile.connected": "Connected",
    "profile.disconnected": "Offline",
    "profile.passkeys.title": "Passkeys",
    "profile.passkeys.empty": "No passkeys registered.",
    "profile.passkeys.created": "Created",
    "profile.passkeys.revoke": "Revoke passkey",
    "profile.passkeys.revokeTitle": "Revoke passkey?",
    "profile.passkeys.revokeMessage": "The passkey \"{{name}}\" will be revoked and can no longer be used to log in. This action cannot be undone.",
    "profile.passkeys.revokeConfirm": "Revoke",
    "profile.passkeys.revoked": "Passkey revoked successfully.",
    "profile.passkeys.error": "Failed to load passkeys. Please try again.",
    "profile.passkeys.notFound": "Passkey not found or unavailable.",
    "notFound.message": "Page not found",
    "createEngine.title": "Create Engine Node",
    "createEngine.description": "Register a new Engine Node in the registry.",
    "createEngine.searchMinChars": "Type at least 3 characters to search",
    "createEngine.formUrl": "Base URL",
    "createEngine.formUrlPlaceholder": "https://engine.cliente.com",
    "createEngine.formChannel": "Channel",
    "createEngine.channelStable": "Stable",
    "createEngine.channelBeta": "Beta",
    "createEngine.channelAlfa": "Alpha",
    "createEngine.formCloudHosted": "Cloud Hosted",
    "createEngine.formIpv4": "Server IPv4",
    "createEngine.formIpv6": "Server IPv6",
    "createEngine.formCaaEmail": "CAA Email (TLS)",
    "createEngine.creating": "Creating...",
    "createEngine.createButton": "Create Engine",
    "createEngine.createError": "Failed to create engine node.",
    "createEngine.tokenWarning": "Store this token securely — shown only once. The node's API Token can be regenerated later in the node's actions panel.",
    "createEngine.installTokenTitle": "Install Token",
    "createEngine.installTokenHint24h": "Expires in 24 hours — single use",
    "createEngine.installTokenPlaybookHint": "Use with the installer/install-engine.yml playbook",
    "createEngine.installTokenGenerating": "Generating install token...",
    "createEngine.installTokenGenerateError": "Could not generate the install token. The node was created — generate the token from the node's action panel.",
    "createEngine.installTokenNotStarted": "Install token generation could not be started. Use the node's actions panel to generate it.",
    "createEngine.installTokenPendingHint": "Install token not requested yet.",
    "createEngine.goToNodeActions": "Open node actions",
    "createEngine.apiTokenLabel": "Node API Token",
    "createEngine.apiTokenDesc": "M2M token the node uses to authenticate against the Core API.",
    "engine.create.clientSearchMode": "Search mode",
    "engine.create.clientSearch": "Search client",
    "engine.create.clientSearchPlaceholder": "Search by name or document/EIN...",
    "engine.create.clientSearchMinChars": "Enter at least 3 characters or 11 digits",
    "engine.create.clientSearchError": "Error searching clients",
    "engine.create.clientsFound": "Clients found",
    "engine.create.controlClientsFound": "Clients in RC Control (click to import)",
    "engine.create.noClientsFound": "No clients found",
    "engine.create.newClient": "Register new client",
    "engine.create.newClientTitle": "New Local Client",
    "engine.create.clientName": "Client Name",
    "engine.create.clientDocument": "Document / Tax ID",
    "engine.create.clientEmail": "Email (optional)",
    "engine.create.createClientBtn": "Register and Select",
    "engine.create.creatingClient": "Registering...",
    "engine.create.clientCreateError": "Failed to register client",
    "engine.create.selectedClient": "Selected Client",
    "engine.create.changeClient": "Change",
    "engine.create.selectContract": "Active Contract (required)",
    "engine.create.selectContractPlaceholder": "Select an active contract...",
    "engine.create.contractRequired": "Selecting an active contract is required",
    "engine.create.noActiveContracts": "No active contracts found for this client.",
    "engine.create.goToContracts": "Go to Contracts",
    "engine.create.contractNotActive": "The selected contract is no longer active. Please select another contract.",
    "engine.create.loadingContracts": "Loading active contracts...",
    "engine.create.contractsLoadError": "Failed to load active contracts",
    "engine.create.clientMatchAmbiguous": "Client already registered, but could not be linked automatically.",
    "engine.create.clientNameAndDocumentRequired": "Name and document are required",
    "engine.create.clientDocumentInvalid": "Invalid document (must contain 11 or 14 digits)",
    "engine.create.clientAlreadyExists": "A client with this document already exists",
    "engine.create.clientCreateBadRequest": "Invalid client registration data",
    "engine.create.noPlan": "No plan",
    "engine.create.noEndDate": "No end date",
    "engine.create.contractNotFound": "Contract not found",
    "engine.create.createBadRequest": "Invalid request for engine creation",
    "engine.contracts.linkedContract": "Linked Contract",
    "engine.contracts.contractLabel": "Contract",
    "engine.contracts.planLabel": "Plan",
    "engine.contracts.clientLabel": "Client",
    "engine.contracts.validity": "Validity",
    "engine.contracts.status.active": "Active",
    "engine.contracts.status.warning": "Warning",
    "engine.contracts.status.blocked": "Blocked",
    "engine.contracts.status.revoked": "Revoked",
    "engine.contracts.unavailable": "Contract information unavailable",
    "engine.contracts.noContract": "No contract linked",
    "engine.contracts.changeContract": "Change Contract",
    "engine.contracts.unlinkContract": "Unlink Contract",
    "engine.contracts.selectActiveContract": "Select Active Contract",
    "engine.contracts.pickerTitle": "Link Contract to Engine",
    "engine.contracts.pickerDescription": "Select an active contract of the client to link to this Engine.",
    "engine.contracts.pickerDescriptionAny": "Select an active contract to link to this Engine.",
    "engine.contracts.saving": "Saving...",
    "engine.contracts.saveSuccess": "Contract updated successfully",
    "engine.contracts.saveError": "Failed to update contract",
    "engine.contracts.pendingChange": "Pending change",
    "engineActions.activateDesc": "Enable this Engine Node.",
    "engineActions.cloudAutoActivate": "Cloud nodes auto-activate.",
    "engineActions.openEngine": "Open Engine",
    "engineActions.openEngineDesc": "Access the Engine Node interface.",
    "engineActions.restorePayment": "Restore Payment",
    "engineActions.restorePaymentDesc": "Reactivate Engine Node billing.",
    "engineActions.confirmRevoke": "Confirm Revocation",
    "engineActions.confirmRevokeDesc": "Confirm definitive revocation of the Engine Node.",
    "engineActions.suspendDesc": "Temporarily suspend CPE management.",
    "engineActions.rotateToken": "Rotate API Token",
    "engineActions.rotateTokenDesc": "Generate new HMAC secret. Old token invalidated immediately.",
    "engineActions.tokenWarning": "Keep it safe — you won't see it again.",
    "engineActions.deleteDesc": "Remove permanently. Cascade-deletes backups and logs.",
    "engineActions.suspendTitle": "Suspend Engine Node",
    "engineActions.suspendMessage": "Are you sure you want to suspend",
    "engineActions.deleteTitle": "Delete Engine Node",
    "engineActions.deleteMessage": "You are about to permanently delete",
    "engineActions.confirmDelete": "Confirm Deletion",
    "engineActions.confirmDeletePrompt": "Type the engine name to confirm:",
    "engineActions.deletePermanently": "Delete Permanently",
    "engineActions.copied": "Copied",
    "engineActions.copy": "Copy",
    "engineActions.rotateTokenButton": "Rotate Token",
    "engineActions.installToken": "Install Token",
    "engineActions.installTokenDesc": "Generate temporary token (24h) for the Ansible installer to download configuration.",
    "engineActions.installTokenButton": "Generate Install Token",
    "engineActions.installTokenWarning": "Keep this token safe — valid for 24h, single use only.",
    "engine.actions.openPanelFailed": "Failed to generate access token. Please try again.",
    "engineInfo.title": "Engine Information",
    "engineInfo.description": "Editable fields can be changed inline — click Save Changes to persist.",
    "engineInfo.configSection": "Configuration",
    "engineInfo.reportedBy": "Reported by Engine",
    "engineInfo.nodeId": "Node ID",
    "engineInfo.slug": "Slug",
    "engineInfo.billingStatus": "Billing Status",
    "engineInfo.contractVersion": "Contract Version",
    "engineInfo.containerVersions": "Container Versions",
    "engineInfo.lastHeartbeat": "Last Heartbeat",
    "engineInfo.lastHealthScore": "Last Health Index",
    "engineInfo.cpeCount": "Active CPEs",
    "engineInfo.cpeOverage": "CPE Overage",
    "engineInfo.tokenRotated": "API Token Rotated",
    "engineInfo.createdAt": "Created at",
    "engineInfo.updatedAt": "Updated at",
    "engineInfo.suspendedAt": "Suspended at",
    "engineInfo.revokedAt": "Revoked at",
    "engineInfo.haproxyIp": "HAProxy IP",
    "engineInfo.redisEndpoint": "Redis Endpoint",
    "engineInfo.clickhouseEndpoint": "ClickHouse Endpoint",
    "engineInfo.billingPeriod": "Billing Period",
    "engineInfo.periodStart": "Period Start",
    "engineInfo.periodEnd": "Period End",
    "engineInfo.uspVersion": "USP Version",
    "engineInfo.uspCpes": "USP CPEs",
    "engineInfo.formClientId": "Client ID",
    "engineInfo.formWsUrl": "WS URL",
    "engineInfo.formWsUrlPlaceholder": "wss://engine.example.com",
    "engineInfo.cloudHosted": "Cloud Hosted",
    "engineInfo.formMaxCpes": "Max CPEs",
    "engineInfo.formSoftLimit": "Soft Limit",
    "engineInfo.formHardLimit": "Hard Limit",
    "engineInfo.saveChanges": "Save Changes",
    "engineInfo.saveError": "Failed to save",
    "hostServices.editInterface": "Edit interface",
    "hostServices.deleteInterface": "Delete interface",
    "hostServices.deleteInterfaceConfirm": "Confirm interface deletion",
    "hostServices.deleteInterfaceWarning": "The interface will be removed from the kernel and configuration. For VLANs, the interface is deleted from the system. For physical interfaces, only removed from configuration.",
    "hostServices.interfaceName": "Name",
    "hostServices.ipv4Address": "IPv4 address",
    "hostServices.ipv6Address": "IPv6 address",
    "hostServices.subnet4": "IPv4 subnet",
    "hostServices.prefix6": "IPv6 prefix",
    "hostServices.poolStart": "Pool start",
    "hostServices.poolEnd": "Pool end",
    "hostServices.gateway": "Gateway",
    "hostServices.dnsServers": "DNS servers",
    "hostServices.leaseTime": "Lease time (s)",
    "hostServices.cidr.ipv4Cidr": "IPv4 CIDR",
    "hostServices.cidr.ipv6Cidr": "IPv6 CIDR",
    "hostServices.cidr.ipv4Placeholder": "e.g. 10.50.0.1/24",
    "hostServices.cidr.ipv6Placeholder": "e.g. 2001:db8::1/64",
    "hostServices.cidr.auto": "auto",
    "hostServices.cidr.manual": "manual",
    "hostServices.cidr.reset": "Reset to derived",
    "hostServices.cidr.reserveFirst": "Reserve first",
    "hostServices.cidr.networkId": "Network",
    "hostServices.cidr.netmask": "Netmask",
    "hostServices.cidr.prefixLength": "Prefix",
    "hostServices.cidr.broadcast": "Broadcast",
    "hostServices.cidr.usableRange": "Usable range",
    "hostServices.cidr.totalHosts": "Total hosts",
    "hostServices.cidr.locked": "locked",
    "hostServices.cidr.unlocked": "editable",
    "hostServices.cidr.lock": "Lock gateway",
    "hostServices.cidr.unlock": "Unlock gateway",
    "hostServices.wizard.interfaces": "CPE interfaces",
    "hostServices.wizard.subnets": "Subnet/Prefix",
    "hostServices.wizard.step1": "Network & CPE Pools",
    "hostServices.wizard.step2": "TR-069 & Services",
    "hostServices.wizard.step3": "Preview & Commit",
    "hostServices.wizard.services": "TR-069 & Services",
    "hostServices.wizard.review": "Review",
    "hostServices.wizard.preview.kea4": "Kea DHCPv4",
    "hostServices.wizard.preview.kea6": "Kea DHCPv6",
    "hostServices.wizard.preview.unbound": "Unbound",
    "hostServices.wizard.preview.radvd": "radvd",
    "hostServices.wizard.noInterfacesSelected": "Select at least one interface to continue.",
    "hostServices.wizard.overlapConflictV4": "IPv4 overlap between {a} and {b}",
    "hostServices.wizard.overlapConflictV6": "IPv6 overlap between {a} and {b}",
    "hostServices.wizard.autoDnsServers": "DNS for CPEs (auto)",
    "hostServices.wizard.noDnsServers": "none",
    "hostServices.wizard.autoDnsViews": "Auto-generated DNS views",
    "hostServices.wizard.acsUrlPrimary": "ACS URL primary",
    "hostServices.wizard.acsUrlFallback": "ACS URL fallback",
    "hostServices.wizard.dnsForwarders": "DNS upstream forwarders",
    "hostServices.wizard.dnsForwardersPlaceholder": "1.1.1.1, 8.8.8.8",
    "hostServices.wizard.dnsForwardersHelp": "Comma-separated forwarder IPs. Leave blank to disable upstream forwarding.",
    "hostServices.wizard.reviewInterfaces": "Interfaces",
    "hostServices.wizard.reviewSubnet4": "IPv4 subnet",
    "hostServices.wizard.reviewPrefix6": "IPv6 prefix",
    "hostServices.wizard.reviewForwarders": "Forwarders",
    "hostServices.wizard.noForwarders": "none",
    "hostServices.wizard.reviewAcsUrl": "Primary ACS",
    "hostServices.wizard.activating": "Activating...",
    "hostServices.wizard.confirmActivate": "Confirm and activate",
    "hostServices.vlanIdRange": "VLAN ID must be between 1 and 4094.",
    "hostServices.parentRequired": "Parent interface is required.",
    "hostServices.wizard.result.title": "Apply result",
    "hostServices.wizard.result.applied": "applied",
    "hostServices.wizard.result.partial": "Partial apply — some services failed.",
    "hostServices.wizard.result.success": "All services applied successfully.",
    "hostServices.wizard.result.allFailed": "No services could be applied.",
    "hostServices.wizard.result.retry": "Retry",
    "hostServices.wizard.result.retrying": "Retrying...",
    "hostServices.wizard.result.close": "Close",
    "hostServices.wizard.result.backToEdit": "Back to edit",
    "hostServices.wizard.result.skipped": "skipped (no qualifying interfaces)",
    "hostServices.wizard.result.noServices": "No services returned by the backend.",
    "hostServices.wizard.result.appliedCount": "service(s) applied.",
    "hostServices.wizard.open": "Open configuration wizard",
    "hostServices.wizard.slaacOverlapNote": "With simultaneous SLAAC+DHCPv6 (dual mode), EUI-64 addresses may overlap with the DHCPv6 pool; client DAD resolves duplicates. Reduce the pool or disable SLAAC if you want total isolation.",

    "engineBackups.loading": "Loading backups...",
    "engineBackups.empty": "No backups available",
    "engineBackups.tableFilename": "Filename",
    "engineBackups.tableChecksum": "Checksum",
    "engineBackups.tableCreatedAt": "Created at",
    "engineBackups.statusUnknown": "Unknown",
    "engineBackups.restore": "Restore",
    "engineBackups.restoreConfirmTitle": "Restore backup on node",
    "engineBackups.restoreConfirmBody": "This operation replaces the node's data with the contents of this backup. The node restarts during the restore.",
    "engineBackups.restoreConfirmNodePrompt": "Type the node name ({{node}}) to confirm:",
    "engineBackups.restoreConfirmInputLabel": "Node name for confirmation",
    "engineBackups.restoreConfirmPlaceholder": "Node name",
    "engineBackups.restoreConfirmAction": "Restore backup",
    "engineBackups.restorePending": "Restore in progress",
    "engineBackups.restoreApplied": "Restore applied",
    "engineBackups.restoreFailed": "Restore failed",
    "engineBackups.restoreSessionTitle": "Restores in this session",
    "engineBackups.restoreErrorLegacy": "Per-node backups are not supported — recreate the backup with the current installer.",
    "engineBackups.restoreErrorKeyMismatch": "The backup was encrypted for a different key and cannot be restored.",
    "engineBackups.restoreErrorNodeUnavailable": "Node offline or without backup restore support.",
    "engineBackups.restoreErrorAlreadyActive": "Another restore is already active on this node.",
    "engineBackups.restoreErrorDecryptFailed": "Failed to decrypt the backup — check the Core logs.",
    "engineBackups.restoreErrorGeneric": "Failed to start the restore.",
    "engineBackups.restoreErrorBusy": "The node is busy with another restore operation.",
    "engineBackups.restoreErrorKeyUnavailable": "Global backup key unavailable on the Core — try again.",
    "engineBackups.restoreErrorNotFound": "Restore record not found.",
    "engineBackups.restoreErrorNodeDisconnected": "The node disconnected during the restore.",
    "engineBackups.restoreErrorTimeout": "The restore exceeded its time limit.",
    "engineBackups.restoreErrorFileUnavailable": "Backup file unavailable in Core storage.",
    "engineBackups.restoreErrorSendFailed": "Failed to deliver the backup to the node.",
    "engineBackups.restoreErrorOverLimit": "The backup exceeds the restore size limit.",
    "engineBackups.restoreErrorEmptyPlaintext": "The decrypted backup content is empty.",
    "engineBackups.restoreErrorReconciled": "Stale restore reconciled by the Core — start a new operation.",
    "engineBackups.restoreErrorFailed": "The restore failed on the node.",
    "engineBackups.restoreErrorOther": "The restore failed with an unrecognized code — check the Core logs.",
    "engine.backupTitle": "Backups",
    "engine.download": "Download",
    "engine.noBackups": "No backups available",
    "engine.downloadFailed": "Failed to download backup",
    "engine.backupSize": "Size",
    "engine.backupDate": "Date",
    "engineHealth.loading": "Loading health data...",
    "engineHealth.empty": "No health data yet",
    "engineHealth.last24h": "Last 24 hours",
    "engineHealth.chartLabel": "Health Index",
    "engineHealth.details": "Health score breakdown",
    "engineHealth.currentScore": "Current score",
    "engineHealth.lastUpdate": "Last update",
    "engineHealth.component.db": "Database",
    "engineHealth.component.redis": "Redis",
    "engineHealth.component.genieacs": "GenieACS",
    "engineHealth.component.core_ws": "Core WebSocket",
    "engineHealth.statusOk": "OK",
    "engineHealth.statusDown": "Down",
    "engineHealth.statusUnconfigured": "Unconfigured",
    "engineHealth.pointsLost": "Points lost",
    "engineHealth.noPenalty": "No penalty",
    "engineHealth.cpu": "CPU",
    "engineHealth.producerHeartbeat": "Source: Engine heartbeat",
    "engineHealth.producerSentinel": "Source: Sentinel telemetry",
    "engineHealth.formulaHeartbeat":
      "Base 100; -20 per essential service down (DB, Redis, GenieACS); -10 if the Core WebSocket is disconnected.",
    "engineHealth.formulaSentinel":
      "Base 100; -15 per unhealthy service; -20 if CPU > 90%; -10 if CPU > 75%.",
    "engineHealth.breakdownPending":
      "Breakdown available from the next heartbeat.",
    "engineHealth.noHistory24h": "No history in the last 24 hours.",
    "engineLogs.loading": "Loading logs...",
    "engineLogs.filterLevel": "Filter by level",
    "engineLogs.allLevels": "All Levels",
    "engineLogs.levelInfo": "Info",
    "engineLogs.levelWarn": "Warning",
    "engineLogs.levelError": "Error",
    "engineLogs.autoRefresh": "Auto-refresh every 10s",
    "engineLogs.empty": "No log entries yet",
    "versionHistory.title": "Version History",
    "versionHistory.noVersions": "No previous versions found.",
    "versionHistory.reason": "Reason",
    "noc.totalCpes": "Total CPEs",
    "noc.onlineEngines": "Engines Online",
    "noc.activeOutages": "Active Outages",
    "noc.inactiveDevices": "Inactive Devices",
    "noc.cpeCountChart": "CPE Count (24h)",
    "noc.noHistoryData": "No historical data",
    "noc.engineHealth": "Engine Health",
    "noc.healthIndex": "Health Index",
    "noc.noEngines": "No engine registered",
    "noc.statusOnline": "Online",
    "noc.statusOffline": "Offline",
    "noc.statusDegraded": "Degraded",
    "noc.statusSuspended": "Suspended",
    "noc.recentOutages": "Recent Outages",
    "noc.noOutages": "No outages in the last 24h",
    "noc.avgCpu": "Avg CPU",
    "noc.totalRam": "Total RAM",
    "noc.totalDisk": "Total Disk",
    "engine.cpu": "CPU",
    "engine.ram": "RAM",
    "engine.disk": "Disk",
    "engine.services": "Services",
    "engine.containers": "Containers",
    "engine.infrastructure": "Infrastructure",
    "engine.serviceName": "Service",
    "engine.serviceStatus": "Status",
    "engine.containerImage": "Image",
    "noc.active": "Active",
    "noc.resolved": "Resolved",
    "noc.engineRegistry": "Engine Registry",
    "noc.liveEvents": "Live Events",
    "noc.filterAll": "All",
    "noc.filterFaults": "Faults",
    "noc.filterOutages": "Outages",
    "noc.filterSignals": "Signals",
    "noc.noEvents": "No events yet",
    "noc.device": "Device",
    "noc.olt": "OLT",
    "settings.tabGeneral": "General",
    "settings.tabSmtp": "SMTP",
    "settings.tabIntegrations": "Integrations",
    "settings.tabSecurity": "Security",
    "settings.timezone": "Timezone",
    "settings.timezoneHelp": "Interface times will be displayed in this timezone. Servers always run UTC.",
    "settings.smtpHost": "Host",
    "settings.smtpPort": "Port",
    "settings.smtpUser": "Username",
    "settings.smtpPassword": "Password",
    "settings.smtpFrom": "Sender (Email)",
    "settings.smtpEncryption": "Encryption",
    "settings.smtpEncryptionNone": "None",
    "settings.smtpEncryptionTls": "TLS (implicit)",
    "settings.smtpEncryptionStarttls": "STARTTLS",
    "settings.testEmail": "Test Email",
    "settings.sending": "Sending...",
    "settings.testEmailSent": "Email sent successfully!",
    "settings.loadingSettings": "Loading settings...",
    "settings.saveSettings": "Save Settings",
    "settings.savedSuccess": "Settings saved successfully!",
    "settings.saveError": "Error saving",
    "settings.rcControlToken": "RC Control API Token",
    "settings.rcControlTokenPlaceholder": "Bearer token generated in RC Control admin panel",
    "settings.rcControlTokenHelp": "Generated at: RC Control → Settings → API Tokens. Scopes: read:clients or *.",
    "settings.saveIntegration": "Save Integration",
    "settings.tokenSavedSuccess": "Token saved successfully!",
    "settings.testToken": "Test token",
    "settings.testing": "Testing...",
    "settings.tokenValid": "Token valid",
    "settings.tokenInvalid": "Token invalid",
    "settings.sessionTimeout": "Session Timeout (minutes)",
    "settings.sessionTimeoutHelp": "Changes require Core restart to take effect.",
    "settings.rateLimit": "Rate Limit (per minute)",
    "settings.tabUpdateChannels": "Update Channels",
    "settings.tabRegistry": "Registry",
    "settings.tabBackup": "Backups",
    "settings.backupRetentionTitle": "Backup Retention",
    "settings.backupRetentionDescription": "Global retention policy applied to every node on auto-prune (upload and worker).",
    "settings.backupRetentionMaxCount": "Max backups per node",
    "settings.backupRetentionMaxCountHelp": "0 disables the count criterion. The system keeps an absolute ceiling of 50 backups per node (safety net).",
    "settings.backupRetentionMaxCountError": "Enter a whole number between 0 and 1000.",
    "settings.backupRetentionMaxAgeDays": "Max backup age (days)",
    "settings.backupRetentionMaxAgeDaysHelp": "0 disables the age criterion. Backups older than this limit are removed by the prune.",
    "settings.backupRetentionMaxAgeDaysError": "Enter a whole number between 0 and 3650.",
    "settings.backupRetentionSave": "Save retention",
    "settings.backupRetentionSaved": "Retention saved successfully.",
    "settings.updateChannels.title": "Update Channels",
    "settings.updateChannels.channel": "Channel",
    "settings.updateChannels.latestTag": "Latest Tag",
    "settings.updateChannels.releasedAt": "Released At",
    "settings.updateChannels.sentinelVersion": "Sentinel Version",
    "settings.updateChannels.scheduleMode": "Schedule Mode",
    "settings.updateChannels.immediate": "Immediate",
    "settings.updateChannels.maintenanceWindow": "Maintenance Window",
    "settings.updateChannels.manual": "Manual",
    "settings.updateChannels.paused": "Paused",
    "settings.updateChannels.windowStart": "Start",
    "settings.updateChannels.windowEnd": "End",
    "settings.updateChannels.timezone": "Timezone",
    "settings.updateChannels.pushUpdateNow": "Push Update Now",
    "settings.updateChannels.save": "Save",
    "settings.updateChannels.cancel": "Cancel",
    "settings.registry.title": "Registry Settings",
    "settings.registry.url": "Registry URL",
    "settings.registry.repository": "Repository",
    "settings.registry.username": "Username",
    "settings.registry.password": "Password",
    "settings.registry.passwordSaved": "Password saved",
    "settings.registry.showPassword": "Show password",
    "settings.registry.hidePassword": "Hide password",
    "settings.registry.passwordKeepHint": "Leave blank to keep the current password.",
    "settings.smtp.passwordSaved": "Password saved",
    "settings.smtp.passwordKeepHint": "Leave blank to keep the current password.",
    "settings.registry.testConnection": "Test Connection",
    "settings.registry.save": "Save",
    "settings.registry.connectionSuccess": "Connection successful",
    "settings.registry.connectionFailed": "Connection failed",
    "backups.trigger": "Trigger Backup",
    "backups.triggering": "Triggering...",
    "backups.tableId": "ID",
    "backups.tableDate": "Date",
    "backups.tableSize": "Size",
    "backups.tableType": "Type",
    "backups.tableNode": "Engine",
    "backups.typeDatabase": "Database",
    "backups.typeConfig": "Configuration",
    "backups.manual": "Manual",
    "backups.automatic": "Automatic",
    "backups.noBackups": "No backup found",
    "backups.deleteTitle": "Delete backup",
    "backups.deleteMessage": "Are you sure you want to delete this backup?",
    "backups.effectiveRetention": "Effective retention (per node)",
    "backups.effectiveRetentionValue": "{{count}} / {{days}}d",
    "billing.monthlyClose": "Monthly Close",
    "billing.loadingSummary": "Loading summary...",
    "billing.totalEngines": "Total Engines",
    "billing.activeCpes": "Active CPEs",
    "billing.activeEngines": "Active Engines",
    "billing.warningEngines": "Warning",
    "billing.enginesBilling": "Engines - Billing",
    "billing.overageAlerts": "Overage Alerts",
    "billing.noOverage": "No engine exceeding soft limit.",
    "billing.antiFraudAlerts": "Anti-Fraud Alerts",
    "billing.antiFraudDetected": "alerts detected.",
    "billing.noAnomalies": "No anomalies detected.",
    "billing.revenueTrend": "Revenue Trend",
    "billing.revenueChartPlaceholder": "Revenue chart - available after F08-BILLING",
    "billing.closeConfirmTitle": "Monthly Close",
    "billing.closeConfirmMessage": "Confirm billing close for the current period? This action triggers the BillingCalculatorWorker and generates monthly invoices.",
    "billing.closeConfirmLabel": "Confirm Close",
    "billing.overviewSubtitle": "Overview and billing close for contracts and engines.",
    "billing.selectPeriod": "Reference Month",
    "billing.periodsHistory": "Closing History",
    "billing.periodsCount": "period(s) registered",
    "billing.scope": "Scope",
    "billing.scopeContract": "Contract",
    "billing.scopeEngine": "Engine",
    "billing.reference": "Reference",
    "billing.period": "Period",
    "billing.cpeDays": "CPE-Days / Average",
    "billing.amount": "Amount",
    "billing.status": "Status",
    "billing.dueDate": "Due Date",
    "billing.breakdown": "Breakdown",
    "billing.viewBreakdown": "View Breakdown",
    "billing.noPeriods": "No billing periods registered.",
    "billing.periodsClosed": "contract period(s) closed.",
    "billing.engineCloseLegacy": "Engine Close (Legacy)",
    "billing.engineCloseConfirmTitle": "Engine Close (Legacy)",
    "billing.engineCloseConfirmMessage": "Confirm legacy close per engine? This action triggers the BillingCalculatorWorker via ClickHouse.",
    "billing.engineCloseResult": "Engine Close Result",
    "billing.contractsOverview": "Billing by Contract",
    "billing.totalContracts": "Total Contracts",
    "billing.totalLabel": "Total",
    "billing.engineRef": "Engine",
    "billing.loadingPeriods": "Loading periods history...",
    "billing.closeError": "Error triggering monthly close.",
    "billing.engineCloseError": "Error triggering legacy engine close.",
    "billing.revenueEstimate": "Revenue Estimate (All Scopes)",
    "billing.contracts.title": "Contracts",
    "billing.contracts.id": "Contract",
    "billing.contracts.client": "Client",
    "billing.contracts.plan": "Plan",
    "billing.contracts.periodsClosed": "Periods Closed",
    "billing.contracts.avgCpes": "Average CPEs",
    "billing.contracts.faixa": "Tier",
    "billing.contracts.totalAmount": "Total Billed",
    "billing.contracts.noContracts": "No contracts with closed periods.",
    "billing.plans.title": "Billing Plans",
    "billing.plans.default": "Default",
    "billing.clients.title": "Clients",

    "versioning.versionControl": "Version Control",
    "versioning.commit": "Commit",
    "versioning.commitMessage": "Commit Message",
    "versioning.history": "History",
    "versioning.branch": "Branch",
    "versioning.branches": "Branches",
    "versioning.createBranch": "Create Branch",
    "versioning.branchName": "Branch Name",
    "versioning.baseRef": "Base Ref",
    "versioning.merge": "Merge",
    "versioning.mergeFrom": "Merge From",
    "versioning.mergeTo": "Merge To",
    "versioning.cherryPick": "Cherry Pick",
    "versioning.rollback": "Rollback",
    "versioning.rollbackTo": "Rollback To",
    "versioning.publish": "Publish",
    "versioning.publishTo": "Publish To",
    "versioning.publishAll": "Publish All",
    "versioning.staged": "Staged",
    "versioning.staging": "Staging",
    "versioning.stage": "Stage",
    "versioning.unstage": "Unstage",
    "versioning.clearStaging": "Clear Staging",
    "versioning.stagedChanges": "Staged Changes",
    "versioning.noCommits": "No commits yet",
    "versioning.noBranches": "No branches available",
    "versioning.currentBranch": "Current Branch",
    "versioning.version": "Version",
    "versioning.author": "Author",
    "versioning.date": "Date",
    "versioning.message": "Message",
    "versioning.compare": "Compare",
    "versioning.diff": "Diff",
    "versioning.from": "From",
    "versioning.to": "To",
    "versioning.field": "Field",
    "versioning.noChanges": "No changes",
    "versioning.showAll": "Show All",
    "versioning.showChangesOnly": "Show Changes Only",
    "staging.title": "Staged Changes",
    "staging.empty": "No staged changes",
    "staging.remove": "Remove",
    "staging.channel": "Channel",
    "staging.clearAll": "Clear All",
    "staging.publishAll": "Publish All",
    "staging.published": "{count} items published successfully",
    "staging.publishError": "Error publishing",
    "staging.badgeLabel": "Staged Changes",
    "staging.addToStaging": "Add to Staging",
    "cwmp.cwmpConfigs": "CWMP Configs",
    "cwmp.configKey": "Config Key",
    "cwmp.configValue": "Config Value",
    "cwmp.dataType": "Data Type",
    "cwmp.configType": "Config Type",
    "cwmp.tagValue": "Tag Value",
    "cwmp.objectName": "Object Name",
    "cwmp.ageValue": "Age (seconds)",
    "cwmp.provisionArgs": "Provision Arguments",
    "cwmp.createConfig": "Create Config",
    "cwmp.editConfig": "Edit Config",
    "cwmp.deleteConfig": "Delete Config",
    "cwmp.configName": "Config Name",
    "cwmp.configDescription": "Config Description",
    "cwmp.active": "Active",
    "branch.channel": "Channel",
    "branch.feature": "Feature",
    "branch.deleteBranch": "Delete Branch",
    "branch.cannotDeleteChannel": "Cannot delete a channel branch",
    "branch.cannotDelete": "Could not delete the branch",
    "diff.fieldByField": "Field by Field",
    "diff.noDifferences": "No differences found",
    "publish.publishWarning": "Engines will receive this update on next sync. This action cannot be undone.",
    "publish.publishConfirm": "Confirm Publish",
    "publish.publishSuccess": "Publish completed successfully",
    "publish.publishFailed": "Failed to publish",
    "common.searchPlaceholder": "Search...",
    "common.searchDevice": "Search device...",
    "common.loadError": "Failed to load. Please try again.",
    "common.notFound": "Not found",
    "common.notFoundTitle": "404",
    "common.notFoundMessage": "Page not found",
    "common.comingSoon": "Coming soon",
    "common.critical": "Critical",
    "common.ignoreReasonPrompt": "Reason for ignoring?",
    "common.assignOltPrompt": "Assign to OLT ID:",
    "common.examplePlaceholder": "e.g. 8.8.8.8",
    "common.cidrPlaceholder": "192.168.1.0/24",
    "common.oltId": "OLT ID",
    "faults.title": "Fault Management",
    "faults.searchCommands": "Search commands...",
    "faults.searchBySerial": "Search by serial...",
    "engine.vp.title": "Virtual Parameters (Local Cache)",
    "engine.provisions.title": "Provisions (Local Cache)",
    "engine.files.title": "Files (Local Cache)",
    "onboarding.title": "Onboarding",
    "health.scoreTitle": "Health Score",
    "health.factorsTitle": "Factors",
    "health.trendTitle": "Trend (30 days)",
    "status.title": "System Status",
    "status.workers": "Workers",
    "status.mariadb": "MariaDB",
    "settings.general": "General",
    "settings.erpTest": "ERP Test",
    "settings.systemLogs": "System Logs",
    "hostServices.title": "Host Services (On-Premise)",
    "hostServices.config": "Configuration",
    "hostServices.configApplied": "Configuration applied",
    "hostServices.apply": "Apply",
    "hostServices.restart": "Restart",
    "hostServices.restarting": "Restarting...",
    "hostServices.restartSuccess": "Service restarted",
    "hostServices.restartError": "Failed to restart service",
    "hostServices.advanced": "Advanced",
    "hostServices.vendorClasses": "Vendor Classes",
    "hostServices.testClassification": "Test classification",
    "hostServices.matchType": "Match type",
    "hostServices.matchValue": "Match value",
    "hostServices.urlMode": "URL mode",
    "hostServices.protocol": "Protocol",
    "hostServices.acsUrl": "ACS URL",
    "hostServices.simulate": "Simulate",
    "hostServices.matchedClass": "Matched class",
    "hostServices.wizardCompleted": "Wizard completed",
    "hostServices.configLoading": "Loading config...",
    "hostServices.wizardApplied": "Services applied",
    "hostServices.wizardApplyPartial": "Applied with partial failures",
    "hostServices.wizardApplyError": "Error applying configuration",
    "hostServices.parentInterface": "Parent interface",
    "hostServices.unbound.forwarders": "Forwarders (comma-separated)",
    "hostServices.unbound.forwardersPlaceholder": "8.8.8.8, 8.8.4.4",
    "hostServices.unbound.port": "Port",
    "hostServices.unbound.generate": "Generate config",
    "hostServices.unbound.noInterfacesSelected": "No interfaces selected",
    "hostServices.unbound.interfaceListHelp": "DNS views are auto-generated for selected interfaces:",
    "hostServices.unbound.derivedAcls": "ACLs derived from interfaces:",
    "hostServices.unbound.parsedAcls": "ACLs detected in current config (read-only):",
    "hostServices.unbound.openResolverWarning": "Open resolver detected: access-control 0.0.0.0/0 allow or ::/0 allow found. Remove it to prevent open DNS recursion.",
    "auth.passkeyTitle": "Passkeys (WebAuthn)",
    "auth.registerChallengeError": "Failed to get registration challenge",
    "auth.registerVerificationError": "Registration verification failed",
    "auth.registerPasskeyError": "Failed to register passkey",
    "auth.registerPasskeySuccess": "Passkey registered successfully",
    "auth.registeringPasskey": "Registering...",
    "auth.registerPasskeyButton": "Register Passkey",
    "auth.passkeyIspOnly": "Passkey registration is only available to authenticated ISP operators.",
    "engRemote.consoleInfo": "Interactive SSH terminal via xterm.js",
    "engRemote.tr069Info": "Direct TR-069 query to GenieACS",
    "nodeSelector.local": "Local",
    "nodeSelector.selectNode": "Select Node",
    "nodeSelector.remoteNodes": "Remote Nodes",

    // Task 14 — Clients / Contracts / Billing Plans pages
    "billing.clients.subtitle": "Provider client registry",
    "billing.clients.newClient": "New client",
    "billing.clients.searchPlaceholder": "Search by name or document",
    "billing.clients.clearSearch": "Clear search",
    "billing.clients.colName": "Name",
    "billing.clients.colDocument": "Document",
    "billing.clients.colEmail": "Email",
    "billing.clients.colPhone": "Phone",
    "billing.clients.colOrigin": "Origin",
    "billing.clients.colCreatedAt": "Created at",
    "billing.clients.colActions": "Actions",
    "billing.clients.originControl": "RC Control",
    "billing.clients.originLocal": "Local",
    "billing.clients.emptyList": "No clients registered.",
    "billing.clients.noResults": "No clients found for this search.",
    "billing.clients.loadMore": "Load more",
    "billing.clients.loading": "Loading clients...",
    "billing.clients.showingCount": "Showing {current} of {total} clients",
    "billing.clients.dialogCreateTitle": "New client",
    "billing.clients.dialogEditTitle": "Edit client",
    "billing.clients.dialogDescription": "Search the client in RC Control or fill in the data manually.",
    "billing.clients.controlSearchTitle": "Search RC Control",
    "billing.clients.controlSearchHelp": "The search queries RC Control by subscriber name. If the service is unavailable, manual registration stays available.",
    "billing.clients.controlSearchPlaceholder": "Subscriber name in RC Control",
    "billing.clients.controlSearchButton": "Search",
    "billing.clients.controlSearchSearching": "Searching...",
    "billing.clients.controlSearchNoResults": "No subscribers found in RC Control.",
    "billing.clients.controlSearchSelect": "Use this client",
    "billing.clients.fieldName": "Name",
    "billing.clients.fieldNamePlaceholder": "Full name or company name",
    "billing.clients.fieldDocument": "Document",
    "billing.clients.fieldDocumentPlaceholder": "Tax ID",
    "billing.clients.fieldEmail": "Email",
    "billing.clients.fieldEmailPlaceholder": "contact@provider.com",
    "billing.clients.fieldPhone": "Phone",
    "billing.clients.fieldPhonePlaceholder": "+00 000 000 000",
    "billing.clients.fieldControlId": "RC Control ID",
    "billing.clients.fieldControlIdHelp": "Filled automatically when the client comes from RC Control.",
    "billing.clients.save": "Save",
    "billing.clients.saving": "Saving...",
    "billing.clients.cancel": "Cancel",
    "billing.clients.editButton": "Edit",
    "billing.clients.deleteButton": "Delete",
    "billing.clients.deleteConfirmTitle": "Delete client",
    "billing.clients.deleteConfirmMessage": "Delete client {name}? This action cannot be undone.",
    "billing.clients.deleteConfirmLabel": "Delete",
    "billing.clients.cancelLabel": "Cancel",
    "billing.clients.duplicateDocumentError": "A client with this document is already registered.",
    "billing.clients.duplicateControlEntityError": "This RC Control client is already linked to another record.",
    "billing.clients.clientHasContractsError": "This client has contracts and cannot be deleted.",
    "billing.clients.validationNameRequired": "Enter the client name.",
    "billing.clients.validationDocumentRequired": "Enter the client document.",
    "billing.clients.fieldControlIdPlaceholder": "e.g. 1045",
    "billing.clients.controlSearchUnavailable": "RC Control search is unavailable right now. Register the client manually.",
    "billing.clients.genericError": "The operation could not be completed. Please try again.",
    "billing.contracts.subtitle": "Subscriber contracts, status and billing history",
    "billing.contracts.newContract": "New contract",
    "billing.contracts.filterClient": "Client",
    "billing.contracts.filterClientAll": "All clients",
    "billing.contracts.filterStatus": "Status",
    "billing.contracts.filterStatusAll": "All statuses",
    "billing.contracts.statusActive": "Active",
    "billing.contracts.statusWarning": "Warning",
    "billing.contracts.statusBlocked": "Blocked",
    "billing.contracts.statusRevoked": "Revoked",
    "billing.contracts.colId": "ID",
    "billing.contracts.colClient": "Client",
    "billing.contracts.colPlan": "Plan",
    "billing.contracts.colStatus": "Status",
    "billing.contracts.colStartDate": "Start",
    "billing.contracts.colEndDate": "End",
    "billing.contracts.colActions": "Actions",
    "billing.contracts.emptyList": "No contracts found.",
    "billing.contracts.loading": "Loading contracts...",
    "billing.contracts.actionDetails": "Details",
    "billing.contracts.actionEdit": "Edit",
    "billing.contracts.actionDelete": "Delete",
    "billing.contracts.transitionTitle": "Change contract status",
    "billing.contracts.transitionDescription": "Enter the transition reason and, if applicable, the related ticket.",
    "billing.contracts.transitionReason": "Reason",
    "billing.contracts.transitionReasonPlaceholder": "Describe the reason for the status change",
    "billing.contracts.transitionTicket": "Ticket",
    "billing.contracts.transitionTicketPlaceholder": "Ticket number (optional)",
    "billing.contracts.transitionConfirm": "Confirm transition",
    "billing.contracts.transitionCancel": "Cancel",
    "billing.contracts.dialogCreateTitle": "New contract",
    "billing.contracts.dialogEditTitle": "Edit contract",
    "billing.contracts.dialogDescription": "Enter the client, plan and contract term.",
    "billing.contracts.fieldClient": "Client",
    "billing.contracts.fieldClientSelect": "Select the client",
    "billing.contracts.fieldPlan": "Plan",
    "billing.contracts.fieldPlanSelect": "Select the plan",
    "billing.contracts.fieldStartDate": "Start date",
    "billing.contracts.fieldEndDate": "End date",
    "billing.contracts.fieldNotes": "Notes",
    "billing.contracts.fieldNotesPlaceholder": "Contract notes (optional)",
    "billing.contracts.save": "Save",
    "billing.contracts.saving": "Saving...",
    "billing.contracts.cancel": "Cancel",
    "billing.contracts.deleteConfirmTitle": "Delete contract",
    "billing.contracts.deleteConfirmMessage": "Delete contract {id}? This action cannot be undone.",
    "billing.contracts.deleteConfirmLabel": "Delete",
    "billing.contracts.cancelLabel": "Cancel",
    "billing.contracts.contractHasEnginesError": "This contract has linked engines and cannot be deleted.",
    "billing.contracts.contractDeleteBlockedError": "Deletion blocked: unlink the engines before deleting the contract.",
    "billing.contracts.invalidTransitionError": "Status transition not allowed for this contract.",
    "billing.contracts.endDateBeforeStartDateError": "The end date cannot be earlier than the start date.",
    "billing.contracts.validationRequiredFields": "Fill in all required fields.",
    "billing.contracts.detailsTitle": "Contract details",
    "billing.contracts.detailsSubtitle": "Linked engines and billing history",
    "billing.contracts.enginesSectionTitle": "Linked engines",
    "billing.contracts.noEnginesLinked": "No engines linked to this contract.",
    "billing.contracts.engineNodeId": "Engine",
    "billing.contracts.engineStatus": "Status",
    "billing.contracts.engineBillingStatus": "Billing",
    "billing.contracts.periodsSectionTitle": "Billing history",
    "billing.contracts.periodsTruncated": "Partial history: the period read is capped at 1000 records. Older periods may be missing.",
    "billing.contracts.noPeriodsRecorded": "No billing periods recorded.",
    "billing.contracts.periodRange": "Period",
    "billing.contracts.periodCpeDays": "CPE-days",
    "billing.contracts.periodDueDate": "Due date",
    "billing.contracts.periodAmount": "Amount",
    "billing.contracts.periodStatus": "Status",
    "billing.contracts.periodStatusOpen": "Open",
    "billing.contracts.periodStatusPaid": "Paid",
    "billing.contracts.registerPaymentAction": "Register payment",
    "billing.contracts.paymentRestoredSuccess": "Payment registered and contract restored to active.",
    "billing.contracts.paymentRecordedNotRestored": "Payment registered. Subscriber reactivation was not applied.",
    "billing.contracts.paymentPeriodNotUpdated": "Payment registered, but the billing period was not updated. Check the history before repeating the operation.",
    "billing.contracts.periodStatusUnknown": "Unknown",
    "billing.contracts.showingCount": "Showing {current} of {total} contracts",
    "billing.contracts.loadMore": "Load more",
    "billing.contracts.dropdownTruncated": "… and {hidden} more not shown. Refine the search to find the rest.",
    "billing.contracts.closeDetails": "Close details",
    "billing.contracts.genericError": "The operation could not be completed. Please try again.",
    "billing.plans.subtitle": "Billing plans and consumption price tiers",
    "billing.plans.newPlan": "New plan",
    "billing.plans.colName": "Name",
    "billing.plans.colMetric": "Metric",
    "billing.plans.colPeriodType": "Period",
    "billing.plans.colTiersSummary": "Price tiers",
    "billing.plans.colDefault": "Default",
    "billing.plans.colCreatedAt": "Created at",
    "billing.plans.colActions": "Actions",
    "billing.plans.defaultBadge": "Default",
    "billing.plans.emptyList": "No billing plans registered.",
    "billing.plans.loading": "Loading plans...",
    "billing.plans.actionEdit": "Edit",
    "billing.plans.actionDelete": "Delete",
    "billing.plans.dialogCreateTitle": "New plan",
    "billing.plans.dialogEditTitle": "Edit plan",
    "billing.plans.dialogDescription": "Define the plan metric and price tiers.",
    "billing.plans.fieldName": "Name",
    "billing.plans.fieldNamePlaceholder": "e.g. 300 Mbps plan",
    "billing.plans.fieldMetric": "Billing metric",
    "billing.plans.fieldDefault": "Set as default",
    "billing.plans.fieldDefaultHelp": "The default plan is used when no plan is specified.",
    "billing.plans.tiersSectionTitle": "Price tiers",
    "billing.plans.tiersSectionHelp": "Enter ascending tiers. The last tier can be left without a limit (unlimited).",
    "billing.plans.tierColIndex": "#",
    "billing.plans.tierColUpTo": "Up to",
    "billing.plans.tierColPrice": "Price",
    "billing.plans.tierColActions": "Actions",
    "billing.plans.tierUpToUnlimited": "Unlimited",
    "billing.plans.tierUpToPlaceholder": "Empty = unlimited",
    "billing.plans.tierPricePlaceholder": "0.00",
    "billing.plans.addTier": "Add tier",
    "billing.plans.removeTier": "Remove tier",
    "billing.plans.save": "Save",
    "billing.plans.saving": "Saving...",
    "billing.plans.cancel": "Cancel",
    "billing.plans.deleteConfirmTitle": "Delete plan",
    "billing.plans.deleteConfirmMessage": "Delete the selected plan? This action cannot be undone.",
    "billing.plans.deleteConfirmLabel": "Delete",
    "billing.plans.cancelLabel": "Cancel",
    "billing.plans.planHasContractsError": "This plan is linked to contracts and cannot be deleted.",
    "billing.plans.validationNameRequired": "Enter the plan name.",
    "billing.plans.validationMinTiers": "Enter at least one price tier.",
    "billing.plans.validationMaxTiers": "A price ladder supports at most 20 tiers.",
    "billing.plans.validationNullOnlyOnLastTier": "Only the last tier can be left without a limit.",
    "billing.plans.validationPositiveIntegerUpTo": "The tier limit must be an integer greater than or equal to zero.",
    "billing.plans.validationStrictlyAscending": "Tiers must be strictly ascending.",
    "billing.plans.validationPricePositive": "The tier price must be greater than or equal to zero.",
    "billing.plans.validationPriceFormat": "Enter a plain decimal (e.g. 12.50) — no exponent or hex notation.",
    "billing.plans.validationPriceCeiling": "The tier price must be at most 99999999.99.",
    "billing.plans.yes": "Yes",
    "billing.plans.fieldMetricPlaceholder": "cpe-days",
    "billing.plans.tierSummaryUpTo": "up to {up_to} — {price}",
    "billing.plans.tierSummaryUnlimited": "unlimited — {price}",
    "billing.plans.genericError": "The operation could not be completed. Please try again.",
    "billing.errValidation": "Invalid data:",
    "billing.errDuplicateDocument": "A record with this document or link already exists.",
    "billing.errClientHasContracts": "This client has contracts and cannot be deleted.",
    "billing.errPlanHasContracts": "This plan is linked to contracts and cannot be deleted.",
    "billing.errContractHasEngines": "This contract has linked engines and cannot be deleted or blocked.",
    "billing.errInvalidTransition": "Status transition not allowed for this contract.",
    "billing.errContractNotFound": "Contract not found.",
    "billing.errContractNotActive": "The contract is not active for this operation.",
    "billing.errContractDeleteBlocked": "This contract has linked billing (billing records) and cannot be deleted.",
    "billing.errorLoading": "Failed to load data.",
    "billing.retry": "Retry",
    "billing.errGeneric": "The operation could not be completed. Please try again.",
    "billing.commonValidationError": "invalid value",

  },

};
